import {
  type ApplicationTaskType,
  type BaseLogger,
  type BorrowerUploadRequest,
  type BorrowerWidgetSpec,
  type ChecklistType,
  type checklist,
  type DocumentType,
  type documents,
  entities,
  enums,
  formatters,
  type GoogleDriveFolder,
  getSuperAgent,
  NotImplementedError,
  parsers,
  plainToInstance,
  type S3Bucket,
  type Saved,
  type SyntheticApplicationStatus,
  selectors,
  toJSConvert,
  transformAndValidate,
  types,
  type UserAccountType,
  utilities,
  ValueError,
  type workflow,
} from "@fraction/shared";
import { validate } from "class-validator";
import _ from "lodash";
import type { Request, Response, SuperAgentStatic } from "superagent";
import { z } from "zod";
import { selectChecklistApplication } from "../selectors/application";
import type { PlatformAdapter } from "../types/PlatformAdapter";
import { attachChecklistDocumentUrls } from "./checklistDocuments";
import { recreateErrorObject } from "./errors";
import type { ChecklistApp, FinmoValidation, PinSearchResult } from "./types";

type FlaggedAlert = entities.FlaggedAlert;

export type ChecklistExceptionChecklists = Partial<
  Record<SyntheticApplicationStatus | enums.LoanStatus, types.ChecklistResult[]>
>;

export interface ChecklistExceptionMutationResult {
  exception: entities.ChecklistException;
  checklists: ChecklistExceptionChecklists;
}

export type ApplicationNotification = Pick<
  entities.Notification,
  | "id"
  | "type"
  | "subject"
  | "content"
  | "channels"
  | "recipients"
  | "recipientUserIds"
  | "deliveryStatus"
  | "attemptCount"
  | "earlyDeferralCount"
  | "lastError"
  | "createdAt"
  | "sendAt"
  | "queuedAt"
  | "lastAttemptAt"
  | "sentAt"
  | "terminalFailedAt"
  | "metadata"
  | "connectedEntityType"
  | "connectedEntityIds"
  | "notificationAutomationRunId"
> & {
  relatedFiles?: Array<{ id: string; deletedAt?: Date | null }>;
  deliveries: Array<
    Pick<
      entities.NotificationChannelDelivery,
      | "id"
      | "channel"
      | "recipientKey"
      | "recipientUserId"
      | "status"
      | "attemptCount"
      | "lastAttemptAt"
      | "sentAt"
      | "lastError"
      | "communicationId"
    > & { previewCommunicationId?: string | null }
  >;
};

export type DocumentSearchVersions = {
  extractionVersion: string;
  chunkingVersion: string;
  embeddingModelVersion: string;
};

export type DocumentSearchCoverage = {
  documents: Array<{
    documentId: string;
    fileHash: string;
    filename: string | null;
    totalPages: number;
    /**
     * Text stored for every page. This and the three below are decided by the server with the same
     * definitions the farm's backlog uses, so the two cannot drift apart.
     */
    textComplete: boolean;
    /** Features stored for every page, at the requested `featureVersion`. */
    featuresComplete: boolean;
    /** Search chunks exist, or the stored pages have no text to chunk. */
    chunksComplete: boolean;
    /** All three: nothing left to read. */
    readingComplete: boolean;
    /**
     * The stored statement reading: the most preferred of the asked-for generations whose pages are
     * all stored. Null, zero and `false` when none was asked for or none is stored.
     */
    bankStatementExtractionVersion?: string | null;
    bankStatementTotalPages: number;
    bankStatementPages: number;
    bankStatementHasFacts: boolean;
    /** The reader that produced the stored ledger, from inside the facts. */
    bankStatementReaderVersion: string | null;
    bankStatementRepairsApplied: boolean;
    /** The stored typing's generation and the bytes it was gathered from; null when none is stored. */
    typingVersion: string | null;
    typingFileHash: string | null;
    typingDecidedAt: string | null;
    /** When a person last dismissed this file's robot guess. */
    lastDismissedAt: string | null;
  }>;
};

/** A document's stored pages and features, as `getDocumentPageFeatures` returns them. */
export type StoredDocumentPageFeatures = {
  complete: boolean;
  featureVersion: number;
  bankStatementFacts?: documents.BankStatementFacts;
  bankStatementTransactionCandidates?: documents.BankStatementLedgerLine[];
  pages: Array<{
    pageNumber: number;
    content: string;
    extractionMethod: "pdf-text" | "apple-vision-ocr" | "mixed";
    features: Record<string, unknown>;
    featurePrint: number[];
    textBlocks: Array<{
      id: string;
      text: string;
      confidence?: number;
      boundingBox?: { x: number; y: number; width: number; height: number };
    }>;
    barcodes: Array<{ symbology: string; payload: string }>;
    /**
     * Recognized table structure, stored from v6 on. Empty for a page with no table, and for
     * anything extracted earlier — though the version filter means those are re-read instead.
     */
    tables: Array<{
      rowCount: number;
      columnCount: number;
      boundingBox?: { x: number; y: number; width: number; height: number };
      cells: Array<{
        rowStart: number;
        rowEnd: number;
        columnStart: number;
        columnEnd: number;
        text: string;
        boundingBox?: { x: number; y: number; width: number; height: number };
      }>;
    }>;
    ocrCandidates: Array<{
      engine: "pp-ocr-v6-tiny-coreml" | "pp-ocr-v6-tiny-det-small-rec-coreml" | "pdf-text";
      textBlocks: Array<{
        id: string;
        text: string;
        confidence?: number;
        boundingBox?: { x: number; y: number; width: number; height: number };
      }>;
    }>;
  }>;
};

/** What the local agent read off a document, for Magic Upload's last-resort match. */
export type MagicUploadExtraction = {
  applicants?: Array<{ firstName?: string; lastName?: string; email?: string }>;
  id?: string;
  parcelId?: string;
  propertyAddress?: string;
  property?: {
    country?: string;
    administrativeArea?: string;
    locality?: string;
    postalCode?: string;
    thoroughfare?: string;
    premise?: string | null;
  };
};

/** A deal Magic Upload matched a document to, and why. */
export type MagicUploadMatch = {
  id: string;
  eid?: string;
  propertyAddress?: string;
  applicantNames?: string;
  via: "qr" | "applicationId" | "sameFile" | "entities" | "extraction";
  evidence: string[];
  score?: number;
};

/** A document's stored typing, as `getDocumentTypingResult` returns it. */
export type StoredDocumentTyping = {
  fileHash: string;
  typingVersion: string;
  decidedAt: string;
  evidence: documents.DocumentTypingEvidence;
  segments: documents.DecidedDocumentSegment[];
};

export type DocumentSearchResult = {
  id: string;
  documentId: string;
  fileHash: string;
  filename: string | null;
  documentType: string | null;
  pageNumber: number;
  ordinal: number;
  content: string;
  tokenCount: number;
  score: number;
  lexicalRank?: number;
  vectorRank?: number;
};

// Frontend copies of backend response types (src/lambda/http/velocityDeals.ts)
export type VelocityDealSummary = {
  loanCode: string;
  velocityStatus?: number | null;
  dateCreated?: string | null;
  closingDate?: string | null;
  agent?: string | null;
  borrowers: { name: string; email?: string }[];
  address?: string;
  city?: string;
  /** Cents */
  requestedAmount?: number;
  alreadyImported: boolean;
  applicationId?: string;
  applicationEid?: string;
};

export type VelocityDealsPage = {
  deals: VelocityDealSummary[];
  page: number;
  totalPages: number;
  totalDeals: number;
};

export type VelocityImportTask = {
  id: string;
  status: enums.AsyncJobStatus;
  friendlyName?: string | null;
  friendlyStatus?: string | null;
  metadata?: { loanCode: string };
  output?: { applicationId?: string; applicationEid?: string; error?: string };
};

export type VelocityImportJob = {
  id: string;
  status: enums.AsyncJobStatus;
  friendlyName?: string | null;
  createdAt: string;
  output?: { total: number; imported: number; failed: number };
  tasks?: VelocityImportTask[];
};

export type BmoPaymentFileReplayState =
  | "not_available"
  | "available"
  | "automatic_retry_pending"
  | "automatic_retry_exhausted"
  | "manual_replay_processing"
  | "manual_replay_completed"
  | "manual_replay_failed"
  | "processed";

export type BmoPaymentFileReplayStatus = {
  state: BmoPaymentFileReplayState;
  eligible: boolean;
  attemptCount: number;
  maxAutomaticAttempts: number;
  lastFailedAt?: string;
  requestedAt?: string;
  requestedById?: string;
  completedAt?: string;
  errorMessage?: string;
  resultingFileStatus?: string;
  transactionsProcessed?: number;
};

export type BmoPaymentFileSummary = {
  id: string;
  direction: string;
  fileType: string;
  status: string;
  originalFilename: string;
  s3DocumentId?: string;
  s3Document?: entities.S3Document;
  storageBucket?: string;
  storageKey?: string;
  sha256: string;
  byteSize: number;
  contentType?: string;
  transferredAt?: string | Date;
  processedAt?: string | Date;
  fileCreationNumber?: number;
  systemFileSerialNumber?: string;
  rowCount: number;
  totalAmount: number;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
  createdById?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  replay: BmoPaymentFileReplayStatus;
};

export type BmoPaymentFileRow = {
  id: string;
  rowIndex: number;
  status: string;
  accountNumber?: string;
  normalizedAccountNumbers?: string[];
  paymentReferenceNumber?: string;
  customerName?: string;
  amount?: number;
  paymentDate?: string | Date;
  loanId?: string;
  loanEid?: string;
  applicationId?: string;
  transactionRecordId?: string;
  transaction?: {
    id: string;
    status: string;
    type: string;
    direction: string;
    amount?: number;
    transactionDate?: string | Date;
    errorMessage?: string;
  };
  returnCode?: string;
  returnReason?: string;
  outcome?: unknown;
  itemTraceNumber?: unknown;
  originalItemTraceNumber?: unknown;
  reportCode?: unknown;
  errors?: string[];
  parsedData?: Record<string, unknown>;
  rawData?: Record<string, unknown>;
  createdAt?: string | Date;
  updatedAt?: string | Date;
};

export type LinkedBmoPaymentFileRole =
  | "outbound"
  | "receipt"
  | "confirmation"
  | "bill_payment"
  | "control_report";

export type LinkedBmoPaymentFile = {
  id: string;
  role: LinkedBmoPaymentFileRole;
  direction?: string;
  fileType?: string;
  status?: string;
  originalFilename?: string;
  transferredAt?: string | Date;
};

export type TransactionBmoFile = {
  id: string;
  direction: string;
  fileType: string;
  status: string;
  originalFilename?: string;
  transferredAt?: string | Date;
};

export type BmoPaymentFileDetail = BmoPaymentFileSummary & {
  rows: BmoPaymentFileRow[];
  linkedFiles?: LinkedBmoPaymentFile[];
};

export type PendingBmoPaymentFilePreview = {
  status: "to_be_sent";
  direction: string;
  fileType: string;
  scheduledSendAt: string | Date;
  timeZone: string;
  /** The BMO EFT ID this file goes out under; the file creation number is scoped to it. */
  serviceAgreement: string;
  projectedFileCreationNumber: number;
  projectedRequestedFilename?: string;
  projectedFilename?: string;
  sha256?: string;
  byteSize?: number;
  rowCount: number;
  totalAmount: number;
  excludedTransactionCount: number;
  /** Valid orders held out of this file purely for BMO daily-limit headroom; they stay queued. */
  dailyLimitDeferredCount: number;
  /** One entry per value date this file touches, against BMO's per-agreement daily limit. */
  dailyLimitUsage: BmoEftDailyLimitUsage[];
  rows: BmoPaymentFileRow[];
};

export type BmoEftDailyLimitUsage = {
  valueDateKey: string;
  committedInCents: number;
  batchedInCents: number;
  limitInCents: number;
  remainingInCents: number;
};

export type BmoOutboundReconciliationResolution = "confirm_sent" | "confirm_not_sent";

export type BmoOutboundReconciliationResponse = {
  resolution: BmoOutboundReconciliationResolution;
  changed: boolean;
  transactionsUpdated: number;
  file: BmoPaymentFileDetail;
};

export type BmoPaymentFileReplayResponse = {
  transactionsProcessed: number;
  errorMessage?: string;
  file: BmoPaymentFileDetail;
};

export type BmoPaymentFileRematchResponse = {
  rematchedCount: number;
  remainingUnmatchedCount: number;
  file: BmoPaymentFileDetail;
};

export type BmoDeliveryReceiptCheckResponse = {
  processedCount: number;
  files: Array<{
    fileName: string;
    source: "live" | "recovery" | "cached";
    error?: string;
  }>;
};

export type BmoDeliveryTriggerResponse = {
  ok: boolean;
  sent: number;
  failed: number;
  unknown: number;
  sentTransactionIds: string[];
  failedTransactionIds: string[];
  unknownTransactionIds: string[];
  errorMessage?: string;
};

export type BmoManualPollMailbox = "file_mailbox" | "reports" | "receipts";

export type BmoManualPollResponse = {
  mailbox: BmoManualPollMailbox;
  transactionsProcessed: number;
  processedCount?: number;
  files?: BmoDeliveryReceiptCheckResponse["files"];
};

export type BmoPaymentFilesResponse = {
  data: BmoPaymentFileSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type BmoPaymentFileFilters = {
  direction?: string;
  fileType?: string;
  status?: string;
  from?: string;
  to?: string;
  loanId?: string;
  applicationId?: string;
  reference?: string;
  limit?: number;
  offset?: number;
};

/**
 * Whether a request already carries `name` as a query param. Superagent keeps
 * `.query()` values in two places depending on the runtime: Node merges object
 * values into `qs`, while the browser serializes each call into an `_query`
 * string that may contain multiple `&`-separated parameters. Neither field is
 * on the public type, hence the cast.
 *
 * @internal Exported for focused request-serialization tests.
 */
export const hasQueryParam = (request: Request, name: string): boolean => {
  const req = request as Request & { qs?: Record<string, unknown>; _query?: string[] };
  if (req.qs && req.qs[name] !== undefined) return true;
  return (req._query ?? []).some((query) => new URLSearchParams(query).has(name));
};

export class FractionClient {
  protected api: SuperAgentStatic & Request;
  protected _baseURL: string;
  protected logger: BaseLogger;
  protected adapter: PlatformAdapter;
  // you'd think we could access headers from superagent, but nope.
  private headers: Record<string, string> = {};

  public constructor(adapter: PlatformAdapter) {
    this.adapter = adapter;
    this.logger = adapter.logger;

    this.api = getSuperAgent({
      basePath: adapter.env.apiBaseUrl,
      logger: this.logger,
      timeout: { response: 27500, deadline: 27500 },
      retries: 0, // react-query handles retries
    });

    // recreate the error object when possible
    this.api.use((req) => {
      const end = req.end;
      req.end = function (callback) {
        return end.call(this, (error, response) => {
          callback?.(recreateErrorObject(error), response);
        });
      };
    });

    // Per-request Authorization override for active borrower-impersonation
    // sessions (used by the dashboard's /apply page). When a borrower session
    // is active we swap in `BorrowerSession <token>`; otherwise the default
    // Bearer JWT (installed via setBearerToken) passes through unchanged.
    //
    // Applied inside an `end` wrapper — NOT as a plain plugin-time req.set —
    // because superagent runs `.use()` plugins in registration order and
    // `setBearerToken` registers its Bearer plugin LATER (at login). With a
    // plain set, an employee logged into the same origin had their Bearer
    // JWT silently overwrite the borrower token on every /apply request:
    // the whole borrower flow ran as the employee, and borrower-only
    // endpoints 401'd. Wrapping `end` defers the override to send time,
    // after every header plugin has run.
    this.api.use((req) => {
      const end = req.end;
      req.end = (callback) => {
        const borrowerSession = this.adapter.auth.getActiveBorrowerSession();
        if (borrowerSession) {
          req.set("Authorization", `BorrowerSession ${borrowerSession}`);
        }
        return end.call(req, callback);
      };
      return req;
    });

    // Every request carries the account view the UI is currently rendering, so
    // role-scoped endpoints answer as that role — an employee previewing the
    // broker dashboard gets the broker payload, not the internal one. Applied
    // in an `end` wrapper so it reads the CURRENT value at send time rather
    // than whatever was set when the plugin registered, and skipped when the
    // caller already specified an accountType of its own (those are explicit
    // data scopes, e.g. the applicant deal counts, not the active view).
    this.api.use((req) => {
      const end = req.end;
      req.end = (callback) => {
        if (this.viewAccountType && !hasQueryParam(req, "accountType")) {
          req.query({ accountType: this.viewAccountType });
        }
        return end.call(req, callback);
      };
      return req;
    });

    this._baseURL = adapter.env.apiBaseUrl;
  }

  public baseURL = (): string => this._baseURL;

  /**
   * The dashboard view the user is in ("employee", "broker", "applicant"…),
   * appended to every request as `?accountType=`. The server only honours it
   * for accounts the user actually holds, and ignores values it doesn't know,
   * so it's safe to set from the raw view preference.
   */
  private viewAccountType?: UserAccountType;

  public setAccountType = (accountType?: UserAccountType) => {
    this.viewAccountType = accountType;
  };

  public getAccountType = (): UserAccountType | undefined => this.viewAccountType;
  public authenticated: boolean = false;
  public token?: string;

  public setDefaultHeader = (header: string, value: string) => {
    this.api.use((req) => {
      req.set({ [header]: value });
      this.headers[header] = value;
      return req;
    });
  };

  public setBearerToken = (token: string) => {
    try {
      const user = utilities.auth.decodeToken(token);
      z.string().uuid().parse(user.id);
      z.string().email().parse(user.email);
      this.authenticated = true;
      this.setDefaultHeader("Authorization", `Bearer ${token}`);
      this.token = token;
    } catch (err) {
      if (err instanceof Error) {
        this.logger.exception(err, `Attempting to set invalid user token ${token}`);
      }
    }
  };

  public clearBearerToken = () => {
    this.removeDefaultHeader("Authorization");
    this.authenticated = false;
    this.token = undefined;
  };

  public setHeaderSecret = (secret: string) => {
    this.setDefaultHeader("fraction-secret", secret);
  };

  public removeDefaultHeader = (header: string) => {
    this.api.use((req) => {
      req.unset(header);
      delete this.headers[header];
      return req;
    });
  };

  private setBearerTokenFromStorage = () => {
    const token = this.adapter.auth.getStoredAuthToken();
    if (token) {
      this.setBearerToken(token);
    }
  };

  private prepBearerToken = () => {
    const existingToken = this.headers.Authorization;
    if (!existingToken) {
      this.setBearerTokenFromStorage();
    }
  };

  private getCookieFilledRequest(request: Request) {
    this.prepBearerToken();

    const cookieHeaders = this.adapter.auth.getCookieRequestHeaders();
    this.logger.log(`Stored cookies to be loaded into HTTP request: ${JSON.stringify(cookieHeaders)}`);

    for (const [name, value] of Object.entries(cookieHeaders)) {
      request.set(name, value);
    }

    if (!this.headers.Authorization) {
      const stored = this.adapter.auth.getStoredAuthToken();
      this.logger.log(
        `Auth header is missing in request; stored token is ${stored?.substring(0, 10)}, len ${stored?.length}`
      );
    }

    return request;
  }

  private getIdempotentRequest(request: Request, idempotencyKey: string) {
    if (!idempotencyKey) {
      this.logger.warn("Idempotency key is missing");
    }
    return request.set("idempotency-key", idempotencyKey);
  }

  public addToWaitlist = async (waitlist: parsers.inquiry.Inquiry): Promise<Response> =>
    this.getCookieFilledRequest(this.api.post("/application/waitlist")).send(waitlist);

  public async deleteApplications(loanApplications: string[]) {
    await this.api.delete("/application").send(loanApplications);
  }

  /**
   * Log in with email + password. Returns the bearer token on success. When the
   * account exists and the password is correct but it isn't `REGISTERED` yet,
   * the server returns `{ needsVerification: true, userId }` instead of a token
   * so the caller can route the user into the OTP claim/verify flow.
   */
  public async login(
    user: entities.User
  ): Promise<{ token?: string; needsVerification?: boolean; userId?: string }> {
    await validate(user);
    const { email, password } = user;
    const response = await this.api.get("/user/login").query({ email, password });
    return response.body;
  }

  public async create(user: entities.User): Promise<string> {
    await validate(user);
    const response = await this.api
      .post("/user")
      .send(toJSConvert(entities.User, user, { groups: ["admin"] }));
    return response.body.token;
  }

  /**
   * Self-service signup. POSTs the user's credentials and optional brokerage
   * selection to `/user`, then installs the returned bearer so subsequent requests
   * authenticate automatically.
   */
  public async createUserWithPassword({
    email,
    password,
    firstName,
    lastName,
    doNotSendVerificationRequest,
    createBrokerAccount,
    brokerageId,
  }: {
    email: string;
    /** Optional — omit when the caller doesn't yet know the user's password
     *  (e.g. the ClaimAccount flow, which sets the password after the merge
     *  via /user/register-password). */
    password?: string;
    firstName?: string;
    lastName?: string;
    /** When true, suppress the auto-fired email verification — used by the
     * ClaimAccount flow, which proves channel ownership via its own OTP. */
    doNotSendVerificationRequest?: boolean;
    /** When true, create a broker account for the new user so they resolve to
     * the broker dashboard. Used by the self-serve /sign-up flow. */
    createBrokerAccount?: boolean;
    /** Optionally associate the new broker account with an existing brokerage. */
    brokerageId?: string;
  }): Promise<string> {
    const response = await this.api.post("/user").send({
      email,
      password,
      firstName,
      lastName,
      doNotSendVerificationRequest,
      createBrokerAccount,
      brokerageId,
    });
    this.setBearerToken(response.body.token);
    return response.body.token;
  }

  /**
   * Search for an existing broker profile that matches the supplied
   * identifying info. Used by the "claim your account" flow on both web
   * and mobile to surface a likely-existing broker so the new user can
   * merge into it rather than creating a duplicate.
   */
  public async searchForBroker(
    args: parsers.broker.BrokerSearchArgs
  ): Promise<
    | types.RequiredFields<entities.BrokerT, "user" | "brokerGroupMemberships.brokerGroup.officeLocation">
    | undefined
  > {
    const response = await this.api.post("/broker/search").send(args).retry(1);
    return response.body;
  }

  /**
   * Fallback to the provincial registry when /broker/search comes up empty.
   * The server scrapes Ontario/BC registries for the selected regions,
   * upserts any matches as placeholder Broker rows, and then re-runs the
   * regular DB search. Returns the same shape as `searchForBroker`.
   */
  public async searchForBrokerInRegistry(
    args: parsers.broker.BrokerSearchArgs
  ): Promise<
    | types.RequiredFields<entities.BrokerT, "user" | "brokerGroupMemberships.brokerGroup.officeLocation">
    | undefined
  > {
    const response = await this.api.post("/broker/search/registry").send(args);
    return response.body;
  }

  /**
   * Kick off an async re-scrape for an existing broker. Returns the id of the
   * `AsyncBrokerRegistryRefreshJob` row; poll via `getBrokerRefreshJob` until
   * status flips to DONE or FAILED.
   */
  public async refreshBrokerFromRegistry(brokerId: string): Promise<{ jobId: string }> {
    const response = await this.api.post(`/broker/${brokerId}/refresh`).send({});
    return response.body;
  }

  /**
   * Poll the status of an in-flight broker-registry refresh. Returns only the
   * job's status + output shape — never broker-detail payloads — so it's safe
   * to expose unauthenticated.
   */
  public async getBrokerRefreshJob(jobId: string): Promise<{
    id: string;
    status: enums.AsyncJobStatus;
    output?: { scrapedCount?: number; error?: string };
    metadata?: { brokerId?: string };
  }> {
    const response = await this.api.get(`/broker/refresh-job/${jobId}`);
    return response.body;
  }

  /**
   * Replace the on-file email/phone of an UNVERIFIED user with no
   * applications — used during the claim flow when the on-file channel is
   * unreachable. Returns the new Channel; the caller still has to OTP it
   * before /user/merge-accounts will succeed.
   */
  public async replaceUserChannel(
    userId: string,
    args: { type: "email" | "phone"; value: string }
  ): Promise<entities.Channel> {
    const response = await this.api.post(`/user/${userId}/replace-channel`).send(args);
    return response.body;
  }

  /**
   * Probe whether the current authed user can merge into the user with the
   * supplied id. Returns `ok: true` when the merge can proceed without any
   * additional channel verification; otherwise returns the list of phones /
   * emails on the target account that the current user must verify
   * ownership of first (one is sufficient).
   */
  public async checkMergeWithUser(
    userId: string
  ): Promise<{ phones: entities.Channel[]; emails: entities.Channel[]; ok: boolean }> {
    const response = await this.api.get("/user/merge-accounts").query({ id: userId });
    return response.body;
  }

  /**
   * Merge the current authed user into the user with the supplied id. The
   * server returns a fresh bearer token scoped to the merged account.
   */
  public async mergeWithUser(userId: string): Promise<{ token: string }> {
    const response = await this.api.post("/user/merge-accounts").send({ id: userId });
    return response.body;
  }

  public async getUser(token?: string): Promise<entities.User> {
    const query: any = {};
    if (token) {
      query.token = token;
    }
    const response = await this.api.get("/user").query(query);
    return response.body;
  }

  public async updateUser(user: Partial<entities.User>): Promise<entities.User> {
    const response = await this.api.put("/user").send(user);
    return response.body;
  }

  public async getSubmissionCredentialsStatus(): Promise<{ finmo: boolean; velocity: boolean }> {
    const response = await this.api.get("/user/submission-credentials-status");
    return response.body;
  }

  /**
   * One page of the broker's Velocity book. Paged because the backend does one Newton
   * round-trip per page — page 1 lands in ~1-2s, the rest stream in behind it.
   *
   * `statuses` narrows the Newton query itself (fewer statuses = fewer 100-deal pages =
   * faster); omitted means the live pipeline (New + Lead). `loanCode` does an exact
   * single-deal lookup, the only search Newton supports server-side.
   */
  public async listVelocityDeals({
    page = 1,
    statuses,
    loanCode,
  }: {
    page?: number;
    statuses?: number[];
    loanCode?: string;
  } = {}): Promise<VelocityDealsPage> {
    const response = await this.api.get("/user/velocity-deals").query({
      page,
      ...(statuses?.length ? { statuses: statuses.join(",") } : {}),
      ...(loanCode ? { loanCode } : {}),
    });
    return response.body;
  }

  public async getVelocityWebhookUrl(): Promise<{ url: string }> {
    const response = await this.api.get("/user/velocity-deals/webhook-url");
    return response.body;
  }

  public async getVelocityWebhookStatus(): Promise<{ lastWebhookReceivedAt: string | null }> {
    const response = await this.api.get("/user/velocity-deals/webhook-status");
    return response.body;
  }

  public async importVelocityDeals(
    args: { loanCodes: string[] } | { all: true }
  ): Promise<VelocityImportJob> {
    const response = await this.api.post("/user/velocity-deals/import").send(args);
    return response.body;
  }

  public async listVelocityImportJobs(): Promise<{ jobs: VelocityImportJob[] }> {
    const response = await this.api.get("/user/velocity-deals/import");
    return response.body;
  }

  public async getVelocityImportJob(jobId: string): Promise<VelocityImportJob> {
    const response = await this.api.get(`/user/velocity-deals/import/${jobId}`);
    return response.body;
  }

  public createApiToken = async (args: {
    name?: string;
    permissions: enums.ApiTokenPermission[];
  }): Promise<{
    id: string;
    token: string;
    tokenPrefix: string;
    name: string | null;
    permissions: enums.ApiTokenPermission[];
    createdAt: string;
  }> => {
    const response = await this.api.post("/user/api-tokens").send(args);
    return response.body;
  };

  public listApiTokens = async (): Promise<
    Array<{
      id: string;
      tokenPrefix: string;
      name: string | null;
      permissions: enums.ApiTokenPermission[];
      createdAt: string;
      lastUsedAt: string | null;
      expiresAt: string | null;
      revokedAt: string | null;
    }>
  > => {
    const response = await this.api.get("/user/api-tokens");
    return response.body;
  };

  public revokeApiToken = async (id: string): Promise<{ success: boolean }> => {
    const response = await this.api.delete(`/user/api-tokens/${id}`);
    return response.body;
  };

  public getUserBrokerage = async (): Promise<entities.BrokerGroupMember> => {
    const response = await this.api.get("/user/brokerage");
    return response.body;
  };

  public updateAssignedBrokerage = async (brokerageId: string): Promise<entities.UserT> => {
    const response = await this.api.put("/user/brokerage").send({ brokerageId });
    return response.body;
  };

  public async getUserFromID(id: string): Promise<entities.User> {
    const response = await this.api.get(`/user/${id}`);
    return transformAndValidate(entities.User, response.body as object);
  }

  public async resetUserPasswordRequest(email: string): Promise<void> {
    const response = await this.api.post("/user/passwordResetRequest").send({ email });
    return response.body;
  }

  public async resetUserPassword(password: string): Promise<entities.User> {
    const response = await this.api.post("/user/passwordReset").send({ password });
    return transformAndValidate(entities.User, response.body as object);
  }

  public async registerUserPassword(password: string): Promise<entities.User> {
    const response = await this.api.post("/user/register-password").send({ password });
    return transformAndValidate(entities.User, response.body as object);
  }

  public async requestVerificationEmail({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<void> {
    await this.api.post("/user/verify").send({ email, password });
  }

  public async verifyEmail(token: string): Promise<void> {
    await this.api.get("/user/verify").query({ token });
  }

  public getApplication = async (
    id: string,
    options?: { accountType?: UserAccountType }
  ): Promise<ChecklistApp> => {
    const response = await this.api.get(`/application/${id}`).query({ ...options, normalize: false });

    // const body = denormalizeResponse(response.body);
    const body = response.body;
    const app: any = plainToInstance(entities.Application, body);
    const keys = Object.keys(app.checklists || {});
    for (const key of keys) {
      app.checklists[key] = sortChecklistItems(app.checklists[key], {
        apiBaseUrl: this._baseURL,
        token: this.token,
      });
    }

    app.syntheticStatus = selectors.application.selectSyntheticApplicationStatus(body);
    return app;
  };

  public getTitleReports = async (applicationId: string): Promise<entities.TitleReport[]> => {
    const response = await this.api.get(`/application/${applicationId}/title-reports`);
    return plainToInstance(entities.TitleReport, response.body as object[]);
  };

  public deleteTitleReport = async (
    applicationId: string,
    titleReportId: string
  ): Promise<{ ok: boolean }> => {
    const response = await this.api.delete(`/application/${applicationId}/title-reports/${titleReportId}`);
    return response.body as { ok: boolean };
  };

  public getCreditReports = async (applicationId: string): Promise<entities.CreditReport[]> => {
    const response = await this.api.get(`/application/${applicationId}/credit-reports`);
    return plainToInstance(entities.CreditReport, response.body as object[]);
  };

  public getCreditPullAttempts = async (applicationId: string): Promise<entities.CreditPullAttempt[]> => {
    const response = await this.api.get(`/application/${applicationId}/credit-pull-attempts`);
    return plainToInstance(entities.CreditPullAttempt, response.body as object[]);
  };

  public getCreditPullAttempt = async (
    applicationId: string,
    attemptId: string
  ): Promise<entities.CreditPullAttempt> => {
    const response = await this.api.get(`/application/${applicationId}/credit-pull-attempts/${attemptId}`);
    return plainToInstance(entities.CreditPullAttempt, response.body as object);
  };

  public pullTitleReport = async (
    applicationId: string,
    options?: {
      pullIfMissing?: boolean;
      pullIfExisting?: boolean;
      propertyId?: string;
      lro?: string;
    }
  ): Promise<{ existing: boolean; report?: entities.TitleReport; analysisJobId?: string }> => {
    const response = await this.api
      .post(`/application/${applicationId}/pull-title`)
      .timeout(30000)
      .send(options || {});
    return response.body as { existing: boolean; report?: entities.TitleReport; analysisJobId?: string };
  };

  /**
   * Poll the status of the async title-report LLM analysis job (debt
   * reconciliation + title-ownership notes) kicked off by pull-title. The report
   * + PDF are already available before this job runs; callers poll until it flips
   * to DONE/FAILED, then refetch the title reports / application.
   */
  public getTitleReportAnalysisJob = async (
    applicationId: string,
    jobId: string
  ): Promise<{
    id: string;
    status: enums.AsyncJobStatus;
    output?: { titleNotesUpdated?: boolean; debtCount?: number; mergedCount?: number; error?: string };
  }> => {
    const response = await this.api.get(`/application/${applicationId}/title-analysis-job/${jobId}`);
    return response.body as {
      id: string;
      status: enums.AsyncJobStatus;
      output?: { titleNotesUpdated?: boolean; debtCount?: number; mergedCount?: number; error?: string };
    };
  };

  public pullCreditReport = async (
    applicantId: string,
    options: {
      pullType: string;
      pullIfMissing?: boolean;
      pullIfExisting?: boolean;
      bureau?: string;
      pullInDebts?: boolean;
      lookupOverwrite?: types.PartialIdentityLookupInformation;
    }
  ): Promise<any> => {
    const response = await this.api.post(`/application/applicant/admin/${applicantId}/credit`).send(options);
    return response.body;
  };

  public createManualCreditReport = async (
    applicantId: string,
    body: {
      score: number;
      scoreType: enums.CreditScoreType;
      fetchedOn: string;
      bureau?: enums.CreditBureau;
      pullType?: enums.CreditPullType;
    }
  ): Promise<entities.CreditReport> => {
    const response = await this.api
      .post(`/application/applicant/admin/${applicantId}/credit/manual`)
      .send(body);
    return plainToInstance(entities.CreditReport, response.body as object);
  };

  public fetchParcelIds = async ({
    propertyId,
    lro,
  }: {
    propertyId: string;
    lro?: string;
  }): Promise<PinSearchResult[]> => {
    const response = await this.api.get("/ops/property/parcel").query({ propertyId, lro });
    return response.body;
  };

  public fetchParcelByPointer = async (pointer: string): Promise<string[]> => {
    const response = await this.api.get(`/ops/property/parcel/${pointer}`);
    return response.body;
  };

  public getPropertyValuation = async (
    location: types.ExtractedPropertyAddress | string
  ): Promise<{ value: number }> => {
    const response = await this.api
      .get("/ops/property/avm")
      .query(typeof location === "string" ? { q: location } : location);
    return response.body;
  };

  public getPropertyReport = async (location: types.ExtractedPropertyAddress): Promise<entities.Property> => {
    const response = await this.api.get("/ops/property/report").query(location);
    return response.body;
  };

  public async searchApplications(q: string): Promise<Saved<entities.ApplicationT>[]> {
    const response = await this.api.get("/application/search").query({ q });
    return (plainToInstance(entities.Application, response.body as object[]) ||
      []) as Saved<entities.ApplicationT>[];
  }

  public getApplications = async (
    {
      accountType,
      status,
      offset,
      limit,
      since,
      lite,
      countOnly,
      brand,
    }: {
      accountType?: "employee" | "broker" | "applicant" | "conveyancer" | "lender" | "desk-agent";
      /** `archived` is the only status that returns soft-deleted applications. */
      status?: "active" | "all" | "closed" | "realization" | "funded" | "archived";
      offset?: number;
      limit?: number;
      since?: number;
      lite?: boolean;
      countOnly?: boolean;
      /**
       * Brand pool for the broker pipeline (Fraction vs the white-label brands), so each
       * dashboard only shows its own deals. Omit to see everything.
       */
      brand?: string;
    } = {},
    options?: types.RequestOptions
  ): Promise<
    types.ArrayResponse<ChecklistApp> & {
      // when countOnly is true and accountType = applicant
      all?: number;
      active?: number;
      closed?: number;
      funded?: number;
    }
  > => {
    const response = await this.api
      .get("/application")
      .timeout(30000)
      .query({
        options: formatters.querystring.stringify(options),
        accountType,
        status,
        offset,
        limit,
        since,
        lite,
        brand,
        countOnly,
      });
    const data = response.body.data;
    return {
      count: response.body.count,
      // when countOnly is true and accountType = applicant
      all: response.body.all,
      // when countOnly is true and accountType = applicant
      active: response.body.active,
      // when countOnly is true and accountType = applicant
      closed: response.body.closed,
      // when countOnly is true and accountType = applicant
      funded: response.body.funded,
      pagination: response.body.pagination,
      data: data.map(selectChecklistApplication),
    };
  };

  /**
   * One server-side page of funded applications (LOS Funded pipeline). Filters, search, sort
   * and paging all run in SQL; `count` is the total matching the filters.
   */
  public getFundedApplications = async (
    query: types.FundedApplicationsQuery
  ): Promise<{ count: number; data: ChecklistApp[]; provinces: string[] }> => {
    const response = await this.api
      .get("/application/funded")
      .timeout(30000)
      .query(_.omitBy(query, (value) => value == null || value === ""));
    return {
      count: response.body.count,
      provinces: response.body.provinces ?? [],
      data: (response.body.data as object[]).map(selectChecklistApplication),
    };
  };

  public async updateUploadedFile(file: entities.UploadedFile): Promise<entities.UploadedFile> {
    await validate(file);
    const response = await this.api.put("/document/file").send(toJSConvert(entities.UploadedFile, file));
    return transformAndValidate(entities.UploadedFile, response.body as object);
  }

  /**
   * One document with a fresh presigned URL — the `["initial-file", id]` query behind the
   * workspace, the task queue and the checklist document pane.
   *
   * Rebuilt into the entity rather than returned as raw JSON, which is what it used to be: every
   * field arrived as whatever JSON could carry, so dates were strings and the rate was a plain
   * number under types declaring otherwise. `fileURL` is not a column and survives because
   * class-transformer keeps properties the class does not declare.
   */
  public async getDocumentDetailsWithUrl(args: {
    fileId: string;
  }): Promise<Partial<entities.UploadedFile> & { fileURL: string }> {
    const response = await this.api.get("/document/file/fetch-details").query(args);
    return plainToInstance(
      entities.UploadedFile,
      response.body as object
    ) as Partial<entities.UploadedFile> & { fileURL: string };
  }

  public async getSourceDocuments(labeledDocumentId: string): Promise<entities.UploadedFile[]> {
    const response = await this.api.get(`/document/file/${labeledDocumentId}/source-documents`);
    return response.body?.data || [];
  }

  public async classifyDocumentWithLLM(fileId: string): Promise<{ classifyJobId: string }> {
    const response = await this.api.post(`/document/file/${fileId}/classify`);
    return response.body;
  }

  public async pollClassifyJob(
    fileId: string,
    { classifyJobId }: { classifyJobId: string }
  ): Promise<{
    classify?: { status: string; output?: any };
    isPolling: boolean;
  }> {
    const response = await this.api.get(`/document/file/${fileId}/classify`).query({ classifyJobId });
    return response.body;
  }

  public async setDocumentStatusAndNotes({
    id,
    ...args
  }: {
    status: enums.ApplicationTaskApprovalStatus;
    notes?: string;
    id: string;
  }): Promise<Partial<entities.UploadedFile> & { fileURL: string }> {
    const response = await this.api.patch(`/document/file/approval/${id}`).send(args);
    return response.body;
  }

  public async getParsedBankStatementDetails(
    documentId: string
  ): Promise<entities.ParsedBankStatementDetails | { isPolling: true; jobId: string } | null> {
    try {
      const response = await this.api.get(`/document/${documentId}/parsed`);
      const body = response.body;

      // Check if response indicates polling is in progress
      if (body && typeof body === "object" && "isPolling" in body && body.isPolling === true) {
        return body as { isPolling: true; jobId: string };
      }

      return body;
    } catch (err) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  public async getParsedCondoDocDetails(
    documentId: string
  ): Promise<entities.ParsedCondoDocDetails | { isPolling: true; jobId: string } | null> {
    try {
      const response = await this.api.get(`/document/${documentId}/parsed`);
      const body = response.body;

      // Check if response indicates polling is in progress
      if (body && typeof body === "object" && "isPolling" in body && body.isPolling === true) {
        return body as { isPolling: true; jobId: string };
      }

      return body;
    } catch (err) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  /**
   * Record an appraisal read locally off the document, and fill what it says about the property.
   *
   * Values only — the reading has already been reviewed by whoever pressed the button, so the
   * server has no use for the confidence that produced it.
   */
  public async applyAppraisalFacts(
    documentId: string,
    body: {
      appraisedValue: number;
      effectiveDate?: string;
      landValue?: number;
      grossLivingArea?: number;
      lotSize?: number;
      yearBuilt?: number;
      appraiserName?: string;
      basementSize?: number;
      /** Already resolved to the deal's enums by the reader; the server validates them again. */
      propertyType?: string;
      heatingType?: string;
      garageType?: string;
      /** Open proposed updates this save answers; the server marks them applied. */
      settledProposedUpdateIds?: string[];
    }
  ): Promise<{
    appraisalId?: string;
    propertyId?: string;
    /** The proposals the save actually settled. */
    settledProposedUpdateIds?: string[];
    /** Property columns the appraisal wrote. */
    updatedPropertyFields: string[];
    /** The subset of those that already held a different value. */
    replacedPropertyFields: string[];
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/${documentId}/appraisal`).send(body)
    );
    return response.body;
  }

  /**
   * Record what a reviewer decided about a bank statement's reading, so a later re-read replays it.
   *
   * Appends; a later decision about the same row or exception supersedes the earlier one. Returns
   * the whole list as the document now carries it, for patching the cached document in place.
   */
  public async recordBankStatementDecisions(
    documentId: string,
    decisions: documents.BankStatementDecisionInput[]
  ): Promise<{ decisions: documents.BankStatementDecision[] }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/${documentId}/bank-statement-decisions`).send({ decisions })
    );
    return response.body as { decisions: documents.BankStatementDecision[] };
  }

  /**
   * Save what the desktop read off a bank statement.
   *
   * Values only, never confidence: what reached here is what a person looked at and pressed a button
   * on. `transactions` is absent rather than empty when the reviewer left the ledger out, because
   * the server only overwrites what the parse produced — an absent ledger leaves whatever the deal
   * already holds alone, and an empty one would wipe it.
   */
  public async applyBankStatementFacts(
    documentId: string,
    body: {
      /** Optional: without it the statement still saves, it just is not linked to a bank account. */
      accountNumber?: string;
      institution?: string;
      accountType?: string;
      holderName?: string;
      /** ISO `yyyy-mm-dd`. */
      periodStart?: string;
      periodEnd?: string;
      /** Dollars, as the reader produces them. The server converts. */
      openingBalance?: number;
      closingBalance?: number;
      totalDeposits?: number;
      totalWithdrawals?: number;
      /** Signed dollars: positive is money in. Omit to leave the deal's ledger untouched. */
      transactions?: {
        date: string;
        amount: number;
        description?: string;
        runningBalance?: number;
        /** Where the line was printed. Carried so a re-read can tell this file has been read. */
        provenance?: {
          pageNumber: number;
          box?: { x: number; y: number; width: number; height: number };
          extraction: "text" | "ocr";
          repaired?: boolean;
          readerVersion: string;
        };
      }[];
      /** Changes this statement evidences on OTHER entities — see `readPaymentProofPoints`. */
      proofPoints?: {
        entityKind: "debt" | "income";
        entityId: string;
        field: string;
        value: number | string;
        /** 0-100, as the proposal ledger stores it. */
        confidence: number;
        reasoning: string;
        /** 0-based, as the analyzer emits. */
        sourcePageNumber?: number;
      }[];
    }
  ): Promise<{
    parsedBankStatementDetailsId?: string;
    bankAccountId?: string;
    transactionsSaved: number;
    proofPointsApplied: number;
    /** Proof-points the server declined, with why — a stale entity, or a field it will not write. */
    proofPointsSkipped: { entityId: string; field: string; reason: string }[];
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/${documentId}/bank-statement`).send(body)
    );
    return response.body;
  }

  /**
   * Save what the desktop read off a debt statement, onto the debt a reviewer confirmed it is about.
   *
   * The figures ride as proof-points rather than as a typed body because that is what they are: the
   * server writes them through the proposal ledger, which is already the debt write path, so they
   * arrive with provenance and can be reverted. Money is in DOLLARS and the rate is a printed
   * percentage; the server converts both.
   */
  public async applyDebtStatementFacts(
    documentId: string,
    body: {
      debtId: string;
      proofPoints: {
        entityKind: "debt";
        entityId: string;
        field: string;
        value: number | string;
        /** 0-100. */
        confidence: number;
        reasoning: string;
        /** 0-based, as the analyzer emits. */
        sourcePageNumber?: number;
      }[];
      /** Open proposed updates this save answers; the server applies and settles them. */
      settledProposedUpdateIds?: string[];
    }
  ): Promise<{
    debtId: string;
    proofPointsApplied: number;
    proofPointsSkipped: { entityId: string; field: string; reason: string }[];
    /** The proposals the save applied outright, rather than through a proof-point. */
    settledProposedUpdateIds?: string[];
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/${documentId}/debt-statement`).send(body)
    );
    return response.body;
  }

  public async getAppraisalDetails(
    documentId: string
  ): Promise<entities.Appraisal | { isPolling: true; jobId: string } | null> {
    try {
      const response = await this.api.get(`/document/${documentId}/parsed`);
      const body = response.body;

      if (body && typeof body === "object" && "isPolling" in body && body.isPolling === true) {
        return body as { isPolling: true; jobId: string };
      }

      return body;
    } catch (err) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  /**
   * Save an insurance document's reading as the deal's policy. `review` is a person pressing Save;
   * `farm` is the desktop's background reader, which the server re-checks and may decline.
   */
  public async applyInsurancePolicyFacts(
    documentId: string,
    body: {
      policyNumber?: string;
      insuranceProvider?: string;
      /** ISO `yyyy-mm-dd`. */
      startDate?: string;
      /** ISO `yyyy-mm-dd`. */
      expiryDate?: string;
      /** Dollars. */
      coverageAmount?: number;
      /** Dollars. */
      deductible?: number;
      lossPayee?: enums.InsuranceLossPayee;
      /** Fields to write over the policy's own value, which a save otherwise only fills when blank. */
      overwriteFields?: (
        | "policyNumber"
        | "insuranceProvider"
        | "startDate"
        | "expiryDate"
        | "coverageAmount"
        | "deductible"
        | "lossPayee"
      )[];
      trigger: "review" | "farm";
    }
  ): Promise<{
    insurancePolicy?: entities.InsurancePolicy;
    /** Why nothing was written, when nothing was. */
    skipped?: "already_linked" | "not_condo" | "not_confident" | "type_not_confident";
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/${documentId}/insurance-policy`).send(body)
    );
    const { insurancePolicy, skipped } = response.body ?? {};
    return {
      insurancePolicy: insurancePolicy
        ? (plainToInstance(entities.InsurancePolicy, insurancePolicy as object) as entities.InsurancePolicy)
        : undefined,
      skipped,
    };
  }

  public async getInsurancePolicyDetails(
    documentId: string
  ): Promise<entities.InsurancePolicy | { isPolling: true; jobId: string } | null> {
    try {
      const response = await this.api.get(`/document/${documentId}/parsed`);
      const body = response.body;

      if (body && typeof body === "object" && "isPolling" in body && body.isPolling === true) {
        return body as { isPolling: true; jobId: string };
      }

      return body;
    } catch (err) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  public async parseDocumentWithLLM(documentId: string): Promise<entities.ParsedBankStatementDetails> {
    const response = await this.api.post(`/document/${documentId}/parse`);
    return response.body;
  }

  /**
   * Employee-only: wipe ALL of an application's bank-statement analysis and re-run the
   * parse→autotag→cluster→enrich pipeline from the original PDFs. Returns the count of
   * bank-statement files re-enqueued and their async parse job ids.
   */
  public async reanalyzeApplicationBankStatements(
    applicationId: string
  ): Promise<{ fileCount: number; jobIds: string[] }> {
    const response = await this.api.post(`/document/application/${applicationId}/reanalyze-bank-statements`);
    return response.body;
  }

  /** Clear saved statement transactions and their derived review without starting a new parse. */
  public async clearApplicationBankStatements(applicationId: string): Promise<void> {
    await this.api.post(`/document/application/${applicationId}/clear-bank-statements`);
  }

  /**
   * Get all proposed entity updates for a file
   */
  public async getProposedUpdates(fileId: string): Promise<entities.ProposedEntityUpdate[]> {
    const response = await this.getCookieFilledRequest(this.api.get(`/document/${fileId}/proposed-updates`));
    return response.body;
  }

  public async getSupersededByFile(
    fileId: string
  ): Promise<{ updates: entities.ProposedEntityUpdate[]; questions: entities.ProposedCommunication[] }> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/${fileId}/superseded-updates`)
    );
    return response.body;
  }

  /**
   * Update the status of a proposed entity update (approve/reject)
   */
  public async updateProposedUpdateStatus(
    updateId: string,
    status:
      | enums.ProposedUpdateStatus.APPROVED
      | enums.ProposedUpdateStatus.REJECTED
      | enums.ProposedUpdateStatus.PENDING,
    reasoning?: string
  ): Promise<entities.ProposedEntityUpdate> {
    const response = await this.getCookieFilledRequest(
      this.api.patch(`/document/proposed-update/${updateId}`).send({ status, reasoning })
    );
    return response.body;
  }

  public async editProposedUpdateValue(
    updateId: string,
    proposedValue: unknown,
    reasoning: string
  ): Promise<entities.ProposedEntityUpdate> {
    const response = await this.getCookieFilledRequest(
      this.api.patch(`/document/proposed-update/${updateId}/value`).send({ proposedValue, reasoning })
    );
    return response.body;
  }

  /**
   * Apply a single proposed entity update
   */
  public async applyProposedUpdate(
    updateId: string
  ): Promise<{ update: entities.ProposedEntityUpdate; previousValue: unknown; newValue: unknown }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/proposed-update/${updateId}/apply`)
    );
    return response.body;
  }

  /**
   * Revert a single applied/auto-applied proposed entity update
   */
  public async revertProposedUpdate(updateId: string): Promise<{ update: entities.ProposedEntityUpdate }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/proposed-update/${updateId}/revert`)
    );
    return response.body;
  }

  /**
   * Apply all pending/approved proposed entity updates for a file
   */
  public async applyAllProposedUpdates(
    fileId: string
  ): Promise<{ applied: number; errors: { updateId: string; error: string }[] }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/${fileId}/proposed-updates/apply-all`)
    );
    return response.body;
  }

  /**
   * Get all proposed updates for an application (all files + notes-based)
   */
  public async getApplicationProposedUpdates(
    applicationId: string
  ): Promise<entities.ProposedEntityUpdate[]> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/application/${applicationId}/proposed-updates`)
    );
    return response.body;
  }

  /**
   * Send a proposed communication as an email
   */
  public async sendProposedCommunication(
    id: string
  ): Promise<{ question: entities.ProposedCommunication; sentTo: string }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/proposed-communication/${id}/send`)
    );
    return response.body;
  }

  /**
   * Dismiss a proposed communication
   */
  public async dismissProposedCommunication(id: string): Promise<entities.ProposedCommunication> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/proposed-communication/${id}/dismiss`)
    );
    return response.body;
  }

  /**
   * Mark a proposed communication as resolved (used after sending a fresh email
   * via the compose modal — the original proposal is fulfilled, not dismissed).
   */
  public async resolveProposedCommunication(id: string): Promise<entities.ProposedCommunication> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/proposed-communication/${id}/resolve`)
    );
    return response.body;
  }

  /**
   * Get a presigned URL for a proposed communication attachment
   */
  public async getProposedCommunicationAttachmentUrl(
    id: string,
    index = 0
  ): Promise<{ url: string; filename: string }> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/proposed-communication/${id}/attachment-url`).query({ index })
    );
    return response.body;
  }

  /**
   * Get proposed communications for an application
   */
  public async getProposedCommunications(applicationId: string): Promise<entities.ProposedCommunication[]> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/application/${applicationId}/proposed-communications`)
    );
    return response.body;
  }

  /**
   * Get the source communication (email) for a file
   */
  public async getSourceCommunication(fileId: string): Promise<{
    subject?: string;
    rawSender?: string;
    date?: string;
    content?: string;
    html?: string;
    gmailMessageId?: string;
    attachments: { id: string; typeId?: string; robotGuessedTypeId?: string }[];
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/file/${fileId}/source-communication`)
    );
    return response.body;
  }

  /**
   * Get all flagged alerts for a file
   */
  public async getFlaggedAlerts(fileId: string): Promise<entities.FlaggedAlert[]> {
    const response = await this.getCookieFilledRequest(this.api.get(`/document/${fileId}/flagged-alerts`));
    return _.sortBy(response.body, (alert: FlaggedAlert) =>
      alert.severity === "info"
        ? 2
        : alert.severity === "warning"
          ? 1
          : alert.severity === "critical"
            ? 0
            : -1
    );
  }

  /**
   * Create review alerts linked to an uploaded file. Exact duplicates are ignored by the API.
   */
  public async createFlaggedAlerts(
    fileId: string,
    alerts: Array<{
      severity: enums.FlaggedAlertSeverity;
      title: string;
      description: string;
      sourcePageNumber?: number;
    }>
  ): Promise<{ created: entities.FlaggedAlert[]; duplicateCount: number }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/${fileId}/flagged-alerts`).send({ alerts })
    );
    return response.body;
  }

  /**
   * Update the status of a flagged alert (dismiss/restore)
   */
  public async updateFlaggedAlertStatus(
    alertId: string,
    status: enums.FlaggedAlertStatus
  ): Promise<entities.FlaggedAlert> {
    const response = await this.getCookieFilledRequest(
      this.api.patch(`/document/flagged-alert/${alertId}`).send({ status })
    );
    return response.body;
  }

  public async getProposedUpdateById(id: string): Promise<entities.ProposedEntityUpdate> {
    const response = await this.getCookieFilledRequest(this.api.get(`/document/proposed-update/${id}`));
    return response.body;
  }

  public async getFlaggedAlertById(id: string): Promise<entities.FlaggedAlert> {
    const response = await this.getCookieFilledRequest(this.api.get(`/document/flagged-alert/${id}`));
    return response.body;
  }

  public async getProposedCommunicationById(id: string): Promise<entities.ProposedCommunication> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/proposed-communication/${id}`)
    );
    return response.body;
  }

  // ---- Application questions (generic Q&A on ProposedCommunication) --------

  /**
   * Role-scoped questions for an application. Employees get full entities plus
   * open bank-statement questions; brokers/applicants get the stripped
   * {@link ApplicationQuestion} shape.
   */
  public async getApplicationQuestions(applicationId: string): Promise<ApplicationQuestionsResult> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/application/${applicationId}/questions`)
    );
    return response.body;
  }

  /** Employee writes a question by hand (created PENDING, sent via batch-send). */
  public async createApplicationQuestion(
    applicationId: string,
    body: { questionText: string; context?: string; recipientRole: "broker" | "applicant" }
  ): Promise<{ question: entities.ProposedCommunication }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/questions`).send(body)
    );
    return response.body;
  }

  /** Employee approves an LLM-drafted suggestion into the question batch (optionally edited). */
  public async approveQuestionSuggestion(
    questionId: string,
    body: { questionText?: string; context?: string; recipientRole?: "broker" | "applicant" } = {}
  ): Promise<{ question: entities.ProposedCommunication }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/question/${questionId}/approve`).send(body)
    );
    return response.body;
  }

  /** Send pending questions as ONE email (employee; brokers for their own forwards). */
  public async batchSendQuestions(body: {
    questionIds: string[];
    /** Bank-statement questions to fold into the same email. */
    clusterQuestionIds?: string[];
    /** Required when the batch is only bank-statement questions. */
    recipientRole?: "broker" | "applicant";
    commentary?: string;
    subject?: string;
  }): Promise<{ applied: boolean; batchId?: string; sentTo?: string; sentQuestionIds: string[] }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/questions/batch-send`).send(body)
    );
    return response.body;
  }

  /** Answer a question in-dashboard (broker/applicant/employee). */
  public async answerApplicationQuestion(
    questionId: string,
    body: { responseText: string }
  ): Promise<{ question: ApplicationQuestion }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/question/${questionId}/answer`).send(body)
    );
    return response.body;
  }

  /**
   * Broker forwards a broker-addressed question on to their client (clone,
   * idempotent). Pass `remove: true` to withdraw an unsent forward (un-queue).
   */
  public async forwardQuestionToApplicant(
    questionId: string,
    body: { note?: string; remove?: boolean } = {}
  ): Promise<{ question?: ApplicationQuestion; removed?: boolean }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/question/${questionId}/forward`).send(body)
    );
    return response.body;
  }

  /** Employee clears the NEW-answer badge for an application. */
  public async markQuestionsSeen(applicationId: string): Promise<{ marked: number }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/questions/mark-seen`)
    );
    return response.body;
  }

  public async getPresignedS3Url(s3Key: string, s3Bucket?: string): Promise<string> {
    const response = await this.api.get("/document/file/fetch").query({ s3Key, s3Bucket });
    return response.body.fileURL;
  }

  public async getPresignedDownloadParams(
    args: { filename: string; applicationID: string } | { fileId: string }
  ): Promise<{ fileURL: string }> {
    const response = await this.api.get("/document/file/fetch").query(args);
    return response.body;
  }

  public async getPresignedFileDownloadUrl(uploadedFileId: string): Promise<string> {
    const response = await this.api.get("/document/file/fetch").query({ uploadedFileId });
    return response.body.fileURL;
  }

  /**
   * Repoint a document's stored `fileHash` at the PDF actually in S3.
   *
   * Called by the desktop document cache when a download does not hash to the
   * digest the row carries — the residue of uploads that were converted or
   * combined before 2026-08-25, which recorded the source bytes' hash instead.
   * The server re-reads the object and decides; nothing here is trusted.
   */
  public async repairUploadedFileHash(
    uploadedFileId: string
  ): Promise<{ fileHash: string; repaired: boolean }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/file/${uploadedFileId}/repair-hash`).send({})
    );
    return response.body;
  }

  public async getDocumentSearchCoverage(
    applicationId: string,
    body: DocumentSearchVersions & {
      documentIds?: string[];
      featureVersion: number;
      /** Superseded by `bankStatementExtractionVersions`; still read by servers that predate it. */
      bankStatementExtractionVersion?: string;
      bankStatementExtractionVersions?: readonly string[];
    }
  ): Promise<DocumentSearchCoverage> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/search-index/coverage`).send(body)
    );
    return response.body;
  }

  /**
   * The next documents anywhere that no machine has read yet, for the desktop processing farm.
   *
   * Distinct from `getDocumentSearchCoverage`, which answers the same question for one deal. The
   * server claims each row it returns for a few minutes, so two employees' machines are handed
   * disjoint work rather than both paying for the same OCR.
   */
  /** Records that a reader failed on a document, so the farm backlog stops handing it out. */
  public async recordDocumentReadFailure(body: {
    documentId: string;
    reason: "missing_source" | "unreadable";
    extractionVersion: string;
    message?: string;
  }): Promise<void> {
    await this.getCookieFilledRequest(this.api.post("/document/search-index/read-failure").send(body));
  }

  /** The statement reader's website switch and its usage over the last `days`, for the LOS. */
  public async getStatementReaderUsage(days: number): Promise<documents.StatementReaderUsage> {
    const response = await this.getCookieFilledRequest(
      this.api.get("/ops/statement-reads/usage").query({ days })
    );
    return response.body as documents.StatementReaderUsage;
  }

  /** Show or hide the statement reader on fraction.com. */
  public async setStatementReaderEnabled(enabled: boolean): Promise<{ enabled: boolean }> {
    const response = await this.getCookieFilledRequest(
      this.api.put("/ops/statement-reads/settings").send({ enabled })
    );
    return response.body as { enabled: boolean };
  }

  /**
   * fraction.com's statement reader, desktop side: the oldest broker reading waiting for a reader,
   * now claimed by this desktop — or null — and how long to wait before asking again. `desktopId`
   * tells apart the machines one employee runs, on every call that touches the claim; `desktopName`
   * is what employees see it called.
   */
  public async claimStatementRead(
    desktopId: string,
    desktopName?: string
  ): Promise<{
    claim: documents.StatementReadClaim | null;
    pollAfterMs: number;
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.post("/document/statement-reads/claim").send({ desktopId, desktopName })
    );
    return response.body as { claim: documents.StatementReadClaim | null; pollAfterMs: number };
  }

  /** A file of a claimed reading is done, or a long one is still going: keeps the claim alive. */
  public async recordStatementReadProgress(id: string, desktopId: string, filesRead: number): Promise<void> {
    await this.getCookieFilledRequest(
      this.api
        .post(`/document/statement-reads/${encodeURIComponent(id)}/progress`)
        .send({ desktopId, filesRead })
    );
  }

  /** Hand the finished report over; the backend deletes the broker's PDFs on receipt. */
  public async completeStatementRead(
    id: string,
    desktopId: string,
    report: documents.StatementReadReport
  ): Promise<void> {
    await this.getCookieFilledRequest(
      this.api.post(`/document/statement-reads/${encodeURIComponent(id)}/result`).send({ desktopId, report })
    );
  }

  /** Give a claimed reading back: `cancelled` puts it back in line, `failed` counts against it. */
  public async releaseStatementRead(
    id: string,
    desktopId: string,
    reason: "cancelled" | "failed"
  ): Promise<void> {
    await this.getCookieFilledRequest(
      this.api.post(`/document/statement-reads/${encodeURIComponent(id)}/release`).send({ desktopId, reason })
    );
  }

  public async getDocumentSearchBacklog(
    body: DocumentSearchVersions & {
      featureVersion: number;
      limit?: number;
      excludeApplicationId?: string;
      /** Also hand back documents that are read but not typed at this generation. */
      typingVersion?: string;
    }
  ): Promise<Saved<entities.UploadedFile>[]> {
    const response = await this.getCookieFilledRequest(
      this.api.post("/document/search-index/backlog").send(body)
    );
    return plainToInstance(
      entities.UploadedFile,
      (response.body.files ?? []) as object[]
    ) as Saved<entities.UploadedFile>[];
  }

  public async publishDocumentSearchPages(
    applicationId: string,
    body: DocumentSearchVersions & {
      documentId: string;
      fileHash: string;
      totalPages: number;
      /** Completeness-proven OCR ledger; still requires reviewer acceptance before application use. */
      bankStatementTransactionCandidates?: documents.BankStatementLedgerLine[];
      /** Complete statement facts, persisted so a reopened workspace can render without reparsing. */
      bankStatementFacts?: documents.BankStatementFacts;
      pages: Array<{
        pageNumber: number;
        content: string;
        extractionMethod: "pdf-text" | "apple-vision-ocr" | "mixed";
        chunks: Array<{ ordinal: number; content: string; embedding: number[] }>;
        /**
         * The v5 feature set, where the caller computed it. Optional so the republish path — which
         * only ever has stored text — still publishes; the server COALESCEs rather than
         * overwriting, so cheap text cannot wipe expensive features.
         */
        pageFeatures?: {
          featureVersion: number;
          /** Absent where the page's raster failed; the page still counts as extracted. */
          featurePrint?: number[];
          features?: Record<string, unknown>;
          blocks?: {
            textBlocks: Array<{
              id: string;
              text: string;
              confidence?: number;
              boundingBox?: { x: number; y: number; width: number; height: number };
            }>;
            barcodes: Array<{ symbology: string; payload: string }>;
          };
        };
      }>;
    }
  ): Promise<{ publishedPages: number }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/search-index/pages`).send(body)
    );
    return response.body;
  }

  /**
   * Which model artifacts the desktop app should have, and the SHA-256 to verify each against.
   *
   * `published: false` rather than an empty list when nothing has been uploaded — a client seeing
   * `[]` cannot tell "nothing published yet" from "everything already cached", and would silently
   * run with no model at all.
   */
  public async getModelArtifacts(): Promise<{
    version: string;
    published: boolean;
    artifacts: Array<{ name: string; url: string; sha256: string; byteLength: number }>;
  }> {
    const response = await this.getCookieFilledRequest(this.api.get(`/document/model-artifacts`));
    return response.body;
  }

  /**
   * Nearest already-classified documents to this query vector, excluding this application's own.
   *
   * The exclusion is the reason the index lives in Postgres rather than shipping to the client: a
   * static snapshot contains the document being typed and its same-deal near-duplicates, which is
   * exactly what every accuracy measurement excluded.
   */
  public async searchDocumentTypingNeighbours(
    applicationId: string,
    body: {
      features: documents.DocumentTypingFeatures;
      limit?: number;
      fusion?: Pick<documents.DocumentTypingFusionConfig, "weights" | "candidatePool">;
    }
  ): Promise<{
    neighbours: Array<{
      documentTypeName: string;
      uploadedFileId: string;
      similarity: number;
      channelSimilarities?: documents.DocumentTypingChannelSimilarities;
    }>;
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/typing/search`).send(body)
    );
    return response.body;
  }

  /**
   * Adds one filed, production-detected single-segment document to the typing index. The client
   * performs the bundle check; the server verifies ownership and refuses untyped/generic files.
   */
  public async publishDocumentTypingVector(
    applicationId: string,
    body: {
      documentId: string;
      features: documents.DocumentTypingFeatures;
      featureVersion: number;
      pipelineVersion: string;
    }
  ): Promise<{ indexed: boolean }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/typing/vectors`).send(body)
    );
    return response.body;
  }

  /**
   * Records the local typing pipeline's answer as a document's robot guess.
   *
   * Replaces whatever guess is already on the file, including the LLM classifier's, and is recorded
   * even when it disagrees with the type the document is filed under — that disagreement is what the
   * mismatch callout exists to show. The client's file snapshot can be minutes stale, so the
   * decision is the server's; `recorded` says whether the write actually happened.
   */
  /** The stored typing for one of the application's documents, or null when none is stored yet. */
  public async getDocumentTypingResult(
    applicationId: string,
    documentId: string
  ): Promise<StoredDocumentTyping | null> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/application/${applicationId}/typing/result`).query({ documentId })
    );
    return (response.body as { result: StoredDocumentTyping | null }).result;
  }

  /**
   * Store a document's typing. The server projects it onto the file's robot guess in the same
   * transaction and hands back what the guess is now, for the caches.
   */
  public async putDocumentTypingResult(
    applicationId: string,
    body: {
      documentId: string;
      fileHash: string;
      typingVersion: string;
      pageCount: number;
      evidence: documents.DocumentTypingEvidence;
      segments: documents.DecidedDocumentSegment[];
    }
  ): Promise<{
    robotGuessedTypeId: string | null;
    robotGuessedType?: { id: string; name: string; fullName: string | null };
    projected: boolean;
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.put(`/document/application/${applicationId}/typing/result`).send(body)
    );
    return response.body;
  }

  /**
   * Stored v5 features for a document, so extraction can be skipped entirely.
   *
   * `complete` is false unless every page has features at this version: the models consume the page
   * sequence, so a partial set would pair real features with absent ones and look like a document
   * whose middle pages say nothing.
   */
  public async getDocumentPageFeatures(
    applicationId: string,
    body: Pick<DocumentSearchVersions, "extractionVersion"> & {
      documentId: string;
      featureVersion: number;
    }
  ): Promise<StoredDocumentPageFeatures> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/search-index/features`).send(body)
    );
    return response.body;
  }
  /** The stored reading of some bytes, by their SHA-256 — for a document not uploaded yet. */
  public async getDocumentPageFeaturesByHash(
    body: Pick<DocumentSearchVersions, "extractionVersion"> & { fileHash: string; featureVersion: number }
  ): Promise<StoredDocumentPageFeatures> {
    const response = await this.getCookieFilledRequest(
      this.api.post("/document/search-index/features-by-hash").send(body)
    );
    return response.body;
  }

  public async getDocumentSearchText(
    applicationId: string,
    body: Pick<DocumentSearchVersions, "extractionVersion"> & { documentId: string }
  ): Promise<{
    pages: Array<{
      fileHash: string;
      totalPages: number;
      pageNumber: number;
      content: string;
      extractionMethod: "pdf-text" | "apple-vision-ocr" | "mixed";
    }>;
  }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/search-index/text`).send(body)
    );
    return response.body;
  }

  public async searchApplicationDocumentArtifacts(
    applicationId: string,
    body: DocumentSearchVersions & {
      query: string;
      queryEmbedding: number[];
      documentIds?: string[];
      limit?: number;
    }
  ): Promise<{ query: string; results: DocumentSearchResult[] }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/search`).send(body)
    );
    return response.body;
  }

  public async applyDocumentPageEdits(
    fileId: string,
    body: parsers.documentPageEdits.ApplyDocumentPageEditsArgs
  ): Promise<parsers.documentPageEdits.ApplyDocumentPageEditsResult> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/file/${fileId}/apply-page-edits`).send(body)
    );
    return response.body;
  }

  public async getDocumentPageEditApplyStatus(
    fileId: string,
    jobId: string
  ): Promise<parsers.documentPageEdits.DocumentPageEditApplyStatus> {
    const response = await this.getCookieFilledRequest(
      this.api.get(`/document/file/${fileId}/page-edit-jobs/${jobId}`)
    );
    return response.body;
  }

  public async deleteFile(id: string, opts: { force?: boolean } = {}): Promise<void> {
    await this.api.delete(`/document/file/${id}`).query(opts);
  }

  public async searchForExistingBroker(q: string): Promise<entities.BrokerT[]> {
    const response = await this.api.get("/broker/search").query({ q });
    return response.body;
  }

  public async listBrokerages(
    query: { q?: string; start?: number; limit?: number } = {}
  ): Promise<parsers.brokerageAdmin.BrokerageAdminList> {
    const response = await this.api.get("/broker/group/admin").query(query);
    return response.body;
  }

  public async getBrokerageAdmin(id: string): Promise<parsers.brokerageAdmin.BrokerageAdminDetail> {
    const response = await this.api.get(`/broker/group/admin/${id}`);
    return response.body;
  }

  public async saveBrokerageAdmin(
    id: string | undefined,
    patch: parsers.brokerageAdmin.BrokerageAdminPatch
  ): Promise<parsers.brokerageAdmin.BrokerageAdminDetail> {
    const response = id
      ? await this.api.patch(`/broker/group/admin/${id}`).send(patch)
      : await this.api.post("/broker/group/admin").send(patch);
    return response.body;
  }

  public async getBrokerageAdminMembers(
    id: string,
    query: {
      q?: string;
      start?: number;
      limit?: number;
      descendants?: boolean;
      status?: parsers.brokerageAdmin.BrokerageMembershipFilter;
    } = {}
  ): Promise<parsers.brokerageAdmin.BrokerageAdminMembers> {
    const response = await this.api.get(`/broker/group/admin/${id}/members`).query(query);
    return response.body;
  }

  public async assignBrokerageMember(
    id: string,
    brokerId: string
  ): Promise<parsers.brokerageAdmin.BrokerageAdminDetail> {
    const response = await this.api.post(`/broker/group/admin/${id}/members`).send({ brokerId });
    return response.body;
  }

  public async updateBrokerageMember(
    id: string,
    memberId: string,
    patch: parsers.brokerageAdmin.MembershipPatch
  ): Promise<parsers.brokerageAdmin.BrokerageAdminDetail> {
    const response = await this.api.patch(`/broker/group/admin/${id}/members/${memberId}`).send(patch);
    return response.body;
  }

  public async saveBrokerageLicense(
    id: string,
    licenseId: string | undefined,
    license: parsers.brokerageAdmin.BrokerageLicenseInput
  ): Promise<parsers.brokerageAdmin.BrokerageAdminDetail> {
    const response = licenseId
      ? await this.api.patch(`/broker/group/admin/${id}/licenses/${licenseId}`).send(license)
      : await this.api.post(`/broker/group/admin/${id}/licenses`).send(license);
    return response.body;
  }

  public async removeBrokerageLicense(
    id: string,
    licenseId: string
  ): Promise<parsers.brokerageAdmin.BrokerageAdminDetail> {
    const response = await this.api.delete(`/broker/group/admin/${id}/licenses/${licenseId}`);
    return response.body;
  }

  public async updateBrokerContact(
    id: string,
    patch: parsers.brokerageAdmin.BrokerContactInput
  ): Promise<entities.BrokerT> {
    const response = await this.api.patch(`/broker/${id}/contact`).send(patch);
    return response.body;
  }

  public async uploadBrokerageLogo(id: string, file: Blob): Promise<{ logoId: string; previewUrl: string }> {
    const upload = await this.api.post(`/broker/group/admin/${id}/branding/logo/upload-url`).send({});
    const result = await fetch(upload.body.url, {
      method: "PUT",
      body: file,
      headers: { "x-amz-acl": "bucket-owner-full-control", "Content-Type": file.type },
    });
    if (!result.ok) throw new Error("Logo upload failed");
    const response = await this.api
      .post(`/broker/group/admin/${id}/branding/logo`)
      .send({ key: upload.body.key });
    return response.body;
  }

  public async previewBrokerageBranding(
    id: string,
    draft: parsers.brokerageAdmin.BrokerageAdminPatch
  ): Promise<Blob> {
    const response = await this.api
      .post(`/broker/group/admin/${id}/branding/preview`)
      .send(draft)
      .responseType("blob");
    return response.body;
  }

  public async getBrokerGroup(id: string, options?: types.RequestOptions): Promise<entities.BrokerGroup> {
    const response = await this.api
      .get(`/broker/group/${id}`)
      .query({ options: formatters.querystring.stringify(options) });
    return transformAndValidate(entities.BrokerGroup, response.body as object);
  }

  public async getBrokerGroupRequesters(
    id: string,
    options?: types.RequestOptions
  ): Promise<types.ArrayResponse<entities.BrokerGroupMember>> {
    const response = await this.api
      .get(`/broker/group/${id}/requesters`)
      .query({ options: formatters.querystring.stringify(options) });
    return selectors.query.parsePaginatedResponse(entities.BrokerGroupMember, response.body);
  }

  public async getBrokerGroupMembers(
    id: string,
    options?: types.RequestOptions
  ): Promise<types.ArrayResponse<entities.BrokerGroupMember>> {
    const response = await this.api
      .get(`/broker/group/${id}/members`)
      .query({ options: formatters.querystring.stringify(options) });
    return selectors.query.parsePaginatedResponse(entities.BrokerGroupMember, response.body);
  }

  public async adminRejectBrokerGroupMembershipRequest(groupID: string, memberID: string): Promise<void> {
    const response = await this.api.post(`/broker/group/${groupID}/member/${memberID}`).send({
      status: enums.GroupMemberStatus.REJECTED_BY_ADMIN,
    });
    return response.body;
  }

  public async adminAcceptBrokerGroupMembershipRequest(groupID: string, memberID: string): Promise<void> {
    const response = await this.api.post(`/broker/group/${groupID}/member/${memberID}`).send({
      status: enums.GroupMemberStatus.ACTIVE,
    });
    return response.body;
  }

  public async inviteToBrokerGroup(
    groupID: string,
    invitee: { lastName: string; firstName: string; email: string }
  ): Promise<void> {
    const response = await this.api.post("/broker/group/invite").send({ groupID, invitee });
    return response.body;
  }

  public async updateBrokerGroup(
    id: string,
    updates: Partial<{
      name: string;
      tradeName: string;
      email: string;
      phone: string;
      billingEmail: string;
      parentId: string | null;
      paymentsDelegateId: string | null;
      onboardedDate: Date | null;
    }>
  ): Promise<entities.BrokerGroup> {
    const response = await this.api.patch(`/broker/group/${id}`).send(updates);
    return response.body;
  }

  /**
   * Employee-only: fetch a broker group's payment info INCLUDING the normally-omitted sensitive
   * numbers (full account number, transit/routing, institution). Also returns the payments delegate
   * (the effective payout destination when this group delegates payments).
   */
  public async getBrokerGroupPaymentInfo(
    brokerGroupId: string
  ): Promise<parsers.bankAccount.BrokerGroupPaymentInfoResponse> {
    const response = await this.api.get(`/broker/group/${brokerGroupId}/bank/account/payment-info`);
    return response.body;
  }

  /** Employee-only: in-place update of a broker group's OWN payment info. */
  public async updateBrokerGroupPaymentInfo(
    brokerGroupId: string,
    updates: parsers.bankAccount.BrokerGroupPaymentInfoUpdate
  ): Promise<parsers.bankAccount.BrokerGroupPaymentInfo> {
    const response = await this.api
      .put(`/broker/group/${brokerGroupId}/bank/account/payment-info`)
      .send(updates);
    return response.body;
  }

  public async sendInviteToApplicant({
    inviteeId,
    channels,
    note,
  }: Partial<entities.Invite> & {
    channels: enums.ChannelType[];
    note?: string;
  }): Promise<entities.Invite> {
    const response = await this.api
      .post("/user/invite")
      .send({ inviteeId, type: enums.InviteType.BROKER_TO_APPLICANT, channels, note });
    return plainToInstance(entities.Invite, response.body as object);
  }

  public async getInvites(): Promise<entities.Invite[]> {
    const response = await this.api.get("/user/invite");
    return plainToInstance(entities.Invite, response.body as object[]);
  }

  /** Deal-team invites the user has received (the teams they're a member of). */
  public async getReceivedInvites(): Promise<entities.Invite[]> {
    const response = await this.api.get("/user/received-invites");
    return plainToInstance(entities.Invite, response.body as object[]);
  }

  public async getInvite(id: string): Promise<entities.Invite> {
    const response = await this.api.get(`/user/invite/${id}`);
    return plainToInstance(entities.Invite, response.body as object);
  }

  public async acceptInvite(inviteId: string): Promise<void> {
    await this.api.post(`/user/invite/${inviteId}/accept`);
  }

  public async refreshApplicationLinks(applicationId: string): Promise<entities.LinkT[]> {
    const response = await this.api.post(`/application/${applicationId}/refresh-links`);
    return plainToInstance(entities.Link, response?.body);
  }

  public async duplicateApplication(
    applicationId: string,
    options: { copyLenderDetails?: boolean } = {}
  ): Promise<{ id: string }> {
    const response = await this.api.post(`/application/${applicationId}/duplicate`).send(options);
    return response?.body;
  }

  public async mergeApplications(
    applicationId: string,
    sourceIds: string[]
  ): Promise<{ id: string; archivedIds: string[] }> {
    const response = await this.api.post(`/application/${applicationId}/merge`).send({ sourceIds });
    return response.body;
  }

  public async proceedAfterDuplicateReview(applicationId: string): Promise<{ id: string }> {
    const response = await this.api.post(`/application/${applicationId}/duplicate-review/proceed`);
    return response.body;
  }

  public async rejectInvite(inviteId: string): Promise<void> {
    await this.api.post(`/user/invite/${inviteId}/reject`);
  }

  public async userAcceptBrokerGroupInvite(groupID: string): Promise<void> {
    const response = await this.api
      .post(`/broker/group/${groupID}/member`)
      .send({ status: enums.GroupMemberStatus.ACTIVE });
    return response.body;
  }

  public async userRejectBrokerGroupInvite(groupID: string): Promise<void> {
    const response = await this.api
      .post(`/broker/group/${groupID}/member`)
      .send({ status: enums.GroupMemberStatus.REJECTED_BY_SELF });
    return response.body;
  }

  public async requestToJoinBrokerGroup(groupID: string): Promise<void> {
    const response = await this.api
      .post(`/broker/group/${groupID}/member`)
      .send({ status: enums.GroupMemberStatus.REQUESTED });
    return response.body;
  }

  public getZHVI = async (zip: string): Promise<entities.HomeValuationIndex | undefined> => {
    try {
      const response = await this.api.get("/admin/data/zhvi").query({ zip });
      if (response.status >= 500) {
        this.logger.warn("Internal error. Unable to get ZHVI", {
          body: response.body,
          status: response.status,
        });
      }
      return response.body;
    } catch (err) {
      if (err?.response?.status && err.response.status >= 500) {
        this.logger.exception(err, "Internal error. Unable to get ZHVI", {
          body: err.response.body,
          status: err.response.status,
        });
      }
    }
  };

  public async getMonthlyStatement(id: string): Promise<types.FileURL> {
    const response = await this.api.get(`/document/file/monthlyStatement/${id}`).timeout(30000);

    return response.body;
  }

  public async validateEmail(email: string) {
    return this.api.get("/user/validate/email").query({ email }).timeout(20000).retry(1);
  }

  public async getTransactionRecords(
    loanId: string,
    {
      transactionTypes,
      monthlyAggregatedTypes,
    }: {
      transactionTypes?: enums.TransactionType[];
      monthlyAggregatedTypes?: enums.TransactionType[];
    }
  ): Promise<parsers.transactionRecord.FetchTransactionRecordsResponse> {
    const response = await this.api.get(`/bank/transaction/loan/${loanId}`).query({
      types: JSON.stringify(transactionTypes),
      monthlyAggregatedTypes: JSON.stringify(monthlyAggregatedTypes),
    });

    return parsers.transactionRecord.parseFetchTransactionRecordsResponse(response.body);
  }

  public async getEmployeeTransactions(params: {
    status?: string[];
    type?: string[];
    method?: string;
    dateFrom?: string;
    dateTo?: string;
    offset?: number;
    limit?: number;
    loanId?: string;
    loanEid?: string;
    applicationId?: string;
    connectionId?: string;
    /** Only rows not yet attached to a loan — the bank-feed inbox. */
    unassigned?: boolean;
    /** Accruals are excluded by default; one funded loan carries a row per day. */
    includeInterestAccruals?: boolean;
  }): Promise<{
    count: number;
    data: (entities.TransactionRecord & {
      loanEid?: string;
      appEid?: string;
      applicationId?: string;
      counterpartyInstitutionName?: string;
      counterpartyAccountName?: string;
      connectionInstitutionName?: string;
      connectionAccountName?: string;
      connectionExternalAccountType?: string;
    })[];
    statusCounts: Record<string, number>;
  }> {
    const query: Record<string, string> = {};
    if (params.status?.length) query.status = params.status.join(",");
    if (params.type?.length) query.type = params.type.join(",");
    if (params.method) query.method = params.method;
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo) query.dateTo = params.dateTo;
    if (params.offset != null) query.offset = String(params.offset);
    if (params.limit != null) query.limit = String(params.limit);
    if (params.loanId) query.loanId = params.loanId;
    if (params.loanEid) query.loanEid = params.loanEid;
    if (params.applicationId) query.applicationId = params.applicationId;
    if (params.connectionId) query.connectionId = params.connectionId;
    if (params.unassigned) query.unassigned = "true";
    if (params.includeInterestAccruals) query.includeInterestAccruals = "true";
    const response = await this.api.get("/bank/transaction").query(query);
    return {
      ...response.body,
      data: plainToInstance(entities.TransactionRecord, response.body.data),
    };
  }

  public async getTransactionConnections(): Promise<
    {
      id: string;
      name?: string;
      institutionName?: string;
      externalAccountType?: string;
      accountType?: string;
    }[]
  > {
    const response = await this.api.get("/bank/transaction/connections");
    return response.body;
  }

  public async getBmoPaymentFiles(filters: BmoPaymentFileFilters = {}): Promise<BmoPaymentFilesResponse> {
    const response = await this.api.get("/admin/payments/bmo-files").query(filters);
    return response.body;
  }

  public async getBmoPaymentFile(id: string): Promise<BmoPaymentFileDetail> {
    const response = await this.api.get(`/admin/payments/bmo-files/${id}`);
    return response.body;
  }

  /** One preview per service agreement — the next send produces a separate file for each. */
  public async getPendingBmoPaymentFiles(): Promise<PendingBmoPaymentFilePreview[]> {
    const response = await this.api.get("/admin/payments/bmo-files/pending");
    return response.body;
  }

  public async downloadPendingBmoPaymentFile(serviceAgreement?: string): Promise<{
    blob: Blob;
    filename?: string;
  }> {
    const response = await this.api
      .get("/admin/payments/bmo-files/pending/download")
      .query(serviceAgreement ? { serviceAgreement } : {})
      .responseType("blob");
    const contentDisposition = response.headers["content-disposition"];
    const encodedFilename = contentDisposition?.match(/filename="([^"]+)"/)?.[1];
    return {
      blob: response.body,
      filename: encodedFilename ? decodeURIComponent(encodedFilename) : undefined,
    };
  }

  public async getBmoPaymentFileDownloadUrl(id: string): Promise<{ url: string; expiresInSeconds: number }> {
    const response = await this.api.get(`/admin/payments/bmo-files/${id}/download-url`);
    return response.body;
  }

  public async reconcileBmoPaymentFile(
    id: string,
    body: { resolution: BmoOutboundReconciliationResolution; note: string }
  ): Promise<BmoOutboundReconciliationResponse> {
    const response = await this.api.post(`/admin/payments/bmo-files/${id}/reconcile`).send(body);
    return response.body;
  }

  public async replayBmoPaymentFile(id: string): Promise<BmoPaymentFileReplayResponse> {
    const response = await this.api.post(`/admin/payments/bmo-files/${id}/replay`).send({});
    return response.body;
  }

  public async rematchBmoPaymentFile(id: string): Promise<BmoPaymentFileRematchResponse> {
    const response = await this.api.post(`/admin/payments/bmo-files/${id}/rematch`).send({});
    return response.body;
  }

  public async triggerBmoDelivery(): Promise<BmoDeliveryTriggerResponse> {
    const response = await this.api.post("/admin/payments/bmo-files/send").send({});
    return response.body;
  }

  public async checkBmoSendReceipts(body: {
    includeCached?: boolean;
  }): Promise<BmoDeliveryReceiptCheckResponse> {
    const response = await this.api.post("/admin/payments/bmo-files/check-receipts").send(body);
    return response.body;
  }

  public async previewNotificationAutomationAudience(
    body: types.NotificationAutomationAudiencePreviewRequest
  ): Promise<types.NotificationAutomationAudiencePreview> {
    const response = await this.api.post("/admin/notification-automations/audience/preview").send(body);
    return response.body;
  }

  public async startNotificationAutomationDocumentCheck(
    body: types.NotificationAutomationDocumentCheckRequest
  ): Promise<types.NotificationAutomationDocumentCheckJob> {
    const response = await this.api
      .post("/admin/notification-automations/audience/document-check")
      .send(body);
    return response.body;
  }

  public async getNotificationAutomationDocumentCheck(
    jobId: string
  ): Promise<types.NotificationAutomationDocumentCheckJob> {
    const response = await this.api.get(`/admin/notification-automations/audience/document-check/${jobId}`);
    return response.body;
  }

  public async previewNotificationAutomationSchedule(
    body: types.NotificationAutomationScheduleInput
  ): Promise<types.NotificationAutomationSchedulePreview> {
    const response = await this.api.post("/admin/notification-automations/schedule/preview").send(body);
    return response.body;
  }

  // --- employee-authored letter templates ---------------------------------
  // Rendering is deliberately absent here: a letter is previewed, saved and
  // emailed through the existing /document/generate/custom_template routes, so
  // there is one render path regardless of who is asking.

  public async listDocumentTemplates(options?: {
    publishedOnly?: boolean;
  }): Promise<types.DocumentTemplateListResult> {
    const response = await this.api
      .get("/admin/document-templates")
      .query(options?.publishedOnly ? { publishedOnly: "true" } : {});
    return response.body;
  }

  public async getDocumentTemplate(id: string): Promise<types.DocumentTemplateRecord> {
    const response = await this.api.get(`/admin/document-templates/${id}`);
    return response.body;
  }

  public async createDocumentTemplate(
    body: types.DocumentTemplateDraftInput
  ): Promise<types.DocumentTemplateRecord> {
    const response = await this.api.post("/admin/document-templates").send(body);
    return response.body;
  }

  public async updateDocumentTemplate(
    id: string,
    body: types.DocumentTemplateDraftInput
  ): Promise<types.DocumentTemplateRecord> {
    const response = await this.api.patch(`/admin/document-templates/${id}`).send(body);
    return response.body;
  }

  public async deleteDocumentTemplate(id: string): Promise<void> {
    await this.api.delete(`/admin/document-templates/${id}`);
  }

  public async publishDocumentTemplate(id: string): Promise<types.DocumentTemplateRecord> {
    const response = await this.api.post(`/admin/document-templates/${id}/publish`).send({});
    return response.body;
  }

  public async analyzeDocumentTemplate(body: unknown): Promise<types.DocumentTemplateDiagnostics> {
    const response = await this.api.post("/admin/document-templates/analyze").send({ body });
    return response.body;
  }

  public async listNotificationAutomations(): Promise<types.NotificationAutomationListResult> {
    const response = await this.api.get("/admin/notification-automations");
    return response.body;
  }

  public async getNotificationAutomation(id: string): Promise<types.NotificationAutomationDraftRecord> {
    const response = await this.api.get(`/admin/notification-automations/${id}`);
    return response.body;
  }

  public async createNotificationAutomation(
    body: types.NotificationAutomationDraftInput
  ): Promise<types.NotificationAutomationDraftRecord> {
    const response = await this.api.post("/admin/notification-automations").send(body);
    return response.body;
  }

  public async updateNotificationAutomation(
    id: string,
    body: types.NotificationAutomationDraftInput
  ): Promise<types.NotificationAutomationDraftRecord> {
    const response = await this.api.patch(`/admin/notification-automations/${id}`).send(body);
    return response.body;
  }

  public async sendNotificationAutomationTest(
    id: string,
    body: types.NotificationAutomationTestSendRequest
  ): Promise<types.NotificationAutomationTestSendResult> {
    const response = await this.api.post(`/admin/notification-automations/${id}/test-send`).send(body);
    return response.body;
  }

  public async publishNotificationAutomation(id: string): Promise<types.NotificationAutomationPublishResult> {
    const response = await this.api.post(`/admin/notification-automations/${id}/publish`).send({});
    return response.body;
  }

  public async pauseNotificationAutomation(id: string): Promise<types.NotificationAutomationDraftRecord> {
    const response = await this.api.post(`/admin/notification-automations/${id}/pause`).send({});
    return response.body;
  }

  public async resumeNotificationAutomation(id: string): Promise<types.NotificationAutomationDraftRecord> {
    const response = await this.api.post(`/admin/notification-automations/${id}/resume`).send({});
    return response.body;
  }

  public async listNotificationAutomationRuns(
    id: string
  ): Promise<types.NotificationAutomationRunListResult> {
    const response = await this.api.get(`/admin/notification-automations/${id}/runs`);
    return response.body;
  }

  public async getNotificationAutomationRun(
    automationId: string,
    runId: string
  ): Promise<types.NotificationAutomationRunDetail> {
    const response = await this.api.get(`/admin/notification-automations/${automationId}/runs/${runId}`);
    return response.body;
  }

  public async runNotificationAutomationNow(
    id: string,
    body: types.NotificationAutomationRunNowRequest
  ): Promise<types.NotificationAutomationRunRecord> {
    const response = await this.api.post(`/admin/notification-automations/${id}/runs`).send(body);
    return response.body;
  }

  public async retryNotificationAutomationRunFailures(
    automationId: string,
    runId: string
  ): Promise<types.NotificationAutomationRetryResult> {
    const response = await this.api
      .post(`/admin/notification-automations/${automationId}/runs/${runId}/retry`)
      .send({});
    return response.body;
  }

  public async getNotificationAutomationTemplates(
    refresh = false
  ): Promise<types.NotificationAutomationTemplateList> {
    const response = await this.api
      .get("/admin/notification-automations/templates")
      .query(refresh ? { refresh: "true" } : {});
    return response.body;
  }

  public async previewNotificationAutomationTemplate(
    templateId: string,
    body: types.NotificationAutomationTemplatePreviewRequest
  ): Promise<types.NotificationAutomationTemplatePreview> {
    const response = await this.api
      .post(`/admin/notification-automations/templates/${templateId}/preview`)
      .send(body);
    return response.body;
  }

  public async pollBmoMailbox(body: {
    mailbox: BmoManualPollMailbox;
    includeCached?: boolean;
  }): Promise<BmoManualPollResponse> {
    const response = await this.api.post("/admin/payments/bmo-files/poll").send(body);
    return response.body;
  }

  public async updateTransactionStatus(
    id: string,
    body: { status: string; notes?: string }
  ): Promise<entities.TransactionRecord> {
    const response = await this.api.patch(`/bank/transaction/${id}`).send(body);
    return response.body;
  }

  public async retryEftDebit(
    id: string
  ): Promise<{ transaction: entities.TransactionRecord; created: boolean }> {
    const response = await this.api.post(`/bank/transaction/${id}/retry-eft-debit`).send({});
    return response.body;
  }

  // ===== Swap rates =====
  // `swap_rate` is effective-dated: a day can hold several pulls and each row records the moment
  // it came into force, so "what was in force" is always a resolution, never a plain row read.

  /** Raw observation history. Superseded pulls are excluded unless asked for. */
  public getSwapRates = async (filters?: {
    type?: string;
    country?: string;
    months?: number;
    from?: string;
    to?: string;
    includeSuperseded?: boolean;
    limit?: number;
  }): Promise<entities.SwapRate[]> => {
    const response = await this.api.get("/admin/swap-rate").query(filters ?? {});
    return plainToInstance(entities.SwapRate, response.body as object[]);
  };

  /** The set in force at `asOf` (default now). Pass a past instant to reproduce a historical price. */
  public getSwapRatesInForce = async (options?: {
    asOf?: string;
    type?: string;
    country?: string;
  }): Promise<entities.SwapRate[]> => {
    const response = await this.api.get("/admin/swap-rate/in-force").query(options ?? {});
    return plainToInstance(entities.SwapRate, response.body as object[]);
  };

  /**
   * The tenors Fraction's own products price off, valued at `asOf`.
   *
   * Not the same set as `getSwapRatesInForce`: those are OBSERVED rows, these are the points the
   * products actually derive from, most of which are interpolated and so exist in no row at all.
   */
  public getPricedSwapTenors = async (options?: {
    asOf?: string;
    type?: string;
  }): Promise<
    Array<{
      months: number;
      type: string;
      rate: string | null;
      isObserved: boolean;
      usedBy: Array<{ productName: string; termLengthMonths: number | null; rateCount: number }>;
    }>
  > => {
    const response = await this.api.get("/admin/swap-rate/priced-tenors").query(options ?? {});
    return response.body;
  };

  /** Which (type, country) series exist, so filters track reality rather than a hardcoded list. */
  public getSwapRateSeries = async (): Promise<
    Array<{ type: string; country: string; months: number[]; latestDate: string | null }>
  > => {
    const response = await this.api.get("/admin/swap-rate/series");
    return response.body;
  };

  /** The pulls that wrote a given observation date, oldest first. */
  public getSwapRatePulls = async (params: {
    date: string;
    type?: string;
    country?: string;
  }): Promise<
    Array<{
      pullId: string;
      pulledAt: string;
      rowCount: number;
      activeForDateTime: string | null;
      types: string[];
    }>
  > => {
    const response = await this.api.get("/admin/swap-rate/pull").query(params);
    return response.body;
  };

  /**
   * Brings a pull into force from `activeForDateTime`, which may be backdated. Returns the set that
   * ended up in force, because backdating to before the current stamp is legal but changes nothing
   * about the present.
   *
   * Swap-linked product rates are repriced off the result, so `repricedCount` is how many products
   * actually moved — a backdated promotion that does not displace a later stamp correctly reprices
   * nothing.
   */
  public activateSwapRatePull = async (
    pullId: string,
    activeForDateTime: Date
  ): Promise<{
    pullId: string;
    updated: number;
    active: entities.SwapRate[];
    repricedCount: number;
  }> => {
    const response = await this.api
      .post(`/admin/swap-rate/pull/${pullId}/activate`)
      .send({ activeForDateTime: activeForDateTime.toISOString() });
    const body = response.body as {
      pullId: string;
      updated: number;
      active: object[];
      appliedSwapRates?: { versioned?: unknown[] };
    };
    return {
      ...body,
      // Written straight into the same react-query cache that getSwapRatesInForce fills, so it has
      // to be hydrated the same way — otherwise consumers get ISO strings where they expect Dates.
      active: plainToInstance(entities.SwapRate, body.active ?? []),
      repricedCount: body.appliedSwapRates?.versioned?.length ?? 0,
    };
  };

  /**
   * Takes a pull out of force without removing its rows.
   *
   * Unlike activation this rewrites the past: an `asOf` inside the window the pull stood will no
   * longer return it. Use it to undo a mistaken activation, not to record that a rate changed.
   */
  public deactivateSwapRatePull = async (
    pullId: string
  ): Promise<{ pullId: string; updated: number; active: entities.SwapRate[] }> => {
    const response = await this.api.post(`/admin/swap-rate/pull/${pullId}/deactivate`).send({});
    const body = response.body as { pullId: string; updated: number; active: object[] };
    return { ...body, active: plainToInstance(entities.SwapRate, body.active ?? []) };
  };

  /** Soft-deletes every rate a pull wrote — for data that should never have been recorded. */
  public deleteSwapRatePull = async (
    pullId: string
  ): Promise<{ pullId: string; updated: number; active: entities.SwapRate[] }> => {
    const response = await this.api.delete(`/admin/swap-rate/pull/${pullId}`);
    const body = response.body as { pullId: string; updated: number; active: object[] };
    return { ...body, active: plainToInstance(entities.SwapRate, body.active ?? []) };
  };

  /**
   * Re-runs the swap-rate pull now.
   *
   * Safe to run mid-day: the current day's rows are written per pull, so this records a new set
   * rather than overwriting the morning fix. Each series goes into force on its first sighting of
   * the day and lands dormant on any later one, so a source that was missing this morning takes
   * effect immediately while the ones already recorded wait for a promotion. Note it also posts
   * the usual rate table to the Slack rates channel.
   */
  public runSwapRatePull = async (): Promise<{
    message: string;
    pullId?: string;
    intradayWritten?: number;
    intradayActivated?: number;
    activatedSeries?: string[];
    dormantSeries?: string[];
    settledWritten?: number;
  }> => {
    const response = await this.api.post("/admin/fetchRates").send({});
    return response.body;
  };

  /**
   * Rebuilds the broker rate sheet PDF and returns a link to what was built.
   *
   * Rarely needed — the sheet re-renders itself whenever a published rate moves. This is the escape
   * hatch for a cached sheet built from data that has since been corrected. `rendered` is always
   * true; it is echoed so the caller does not have to assume.
   */
  public regenerateRateSheet = async (): Promise<{
    s3Key: string;
    rendered: boolean;
    effectiveFrom: string | null;
    url: string;
  }> => {
    const response = await this.api.post("/document/generate/rate-sheet").send({});
    return response.body;
  };

  /** Hand-enters a set of rates as its own pull — the fix when a scrape breaks. */
  public createSwapRates = async (body: {
    date: string;
    type: string;
    country: string;
    rates: Array<{ months: number; rate: number }>;
    activeForDateTime?: string;
  }): Promise<{ pullId: string; rates: entities.SwapRate[] }> => {
    const response = await this.api.post("/admin/swap-rate").send(body);
    return response.body;
  };

  public async getRates(options?: parsers.rate.FetchRateArgs): Promise<parsers.rate.Rates> {
    const optionsSerialized: Record<string, any> = {
      ...options,
    };
    if (options?.regions) {
      optionsSerialized.regions = JSON.stringify(options.regions);
    }
    const response = await this.api.get("/admin/rate").query(optionsSerialized).retry(1);
    return parsers.rate.parseRates(response.body);
  }

  public async getBankAccounts(filters?: { type?: enums.PaymentMethod }): Promise<entities.BankAccount[]> {
    const response = await this.api.get("/bank/account").query({ ...filters });
    return plainToInstance(entities.BankAccount, response.body.data);
  }

  public async createBankAccount(body: parsers.bankAccount.BankAccountSubmission): Promise<Response> {
    return this.api.post("/bank/account").send(body);
  }

  public async getPadPaymentProfile(loanId: string): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.get(`/bank/loan/${loanId}/payment-profile`);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async getPadServicingPaymentProfile(
    loanId: string
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.get(`/bank/loan/${loanId}/servicing-payment-profile`);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async getApplicationPaymentCollectionProfile(
    applicationId: string
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.get(`/bank/application/${applicationId}/payment-collection`);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async servicingCreateApplicationManualPadBankAccount(
    applicationId: string,
    body: parsers.padBankAccount.ServicingCreateManualPadBankAccount
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api
      .post(`/bank/application/${applicationId}/payment-collection/bank-accounts/manual`)
      .send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async setApplicationFirstPadDebitDate(
    applicationId: string,
    body: parsers.padBankAccount.SetApplicationFirstPadDebitDate
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api
      .put(`/bank/application/${applicationId}/payment-collection/first-debit-date`)
      .send({ firstDebitDate: formatters.date.iso8601(body.firstDebitDate) });
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async servicingDeleteApplicationManualPadBankAccount(
    applicationId: string,
    bankAccountId: string
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.delete(
      `/bank/application/${applicationId}/payment-collection/bank-accounts/${bankAccountId}`
    );
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async prepareManualPadEvidenceUpload(
    loanId: string,
    body: parsers.padBankAccount.PrepareManualPadEvidenceUpload
  ): Promise<parsers.padBankAccount.ManualPadEvidenceUploadTarget> {
    const response = await this.api.post(`/bank/loan/${loanId}/bank-account-evidence/upload-url`).send(body);
    return parsers.padBankAccount.parseManualPadEvidenceUploadTarget(response.body);
  }

  public async registerManualPadEvidence(
    loanId: string,
    body: parsers.padBankAccount.RegisterManualPadEvidence
  ): Promise<parsers.padBankAccount.ManualPadEvidence> {
    const response = await this.api.post(`/bank/loan/${loanId}/bank-account-evidence`).send(body);
    return parsers.padBankAccount.parseManualPadEvidence(response.body);
  }

  public async createManualPadBankAccount(
    loanId: string,
    body: parsers.padBankAccount.ManualPadBankAccount
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.post(`/bank/loan/${loanId}/bank-accounts/manual`).send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async servicingCreateManualPadBankAccount(
    loanId: string,
    body: parsers.padBankAccount.ServicingCreateManualPadBankAccount
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.post(`/bank/loan/${loanId}/servicing/bank-accounts/manual`).send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async servicingDeleteManualPadBankAccount(
    loanId: string,
    bankAccountId: string
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.delete(`/bank/loan/${loanId}/servicing/bank-accounts/${bankAccountId}`);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async setDefaultPadBankAccount(
    loanId: string,
    body: parsers.padBankAccount.SetDefaultPadBankAccount
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.put(`/bank/loan/${loanId}/default-bank-account`).send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async updatePadAutoPay(
    loanId: string,
    body: parsers.padBankAccount.SetPadAutoPay
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.patch(`/bank/loan/${loanId}/auto-pay`).send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async patchPadBankAccount(
    loanId: string,
    bankAccountId: string,
    body: parsers.padBankAccount.PatchPadBankAccount
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.patch(`/bank/loan/${loanId}/bank-accounts/${bankAccountId}`).send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async deletePadBankAccount(
    loanId: string,
    bankAccountId: string
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.delete(`/bank/loan/${loanId}/bank-accounts/${bankAccountId}`);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async reviewManualPadBankAccount(
    loanId: string,
    bankAccountId: string,
    body: parsers.padBankAccount.ReviewManualPadBankAccount
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api
      .post(`/bank/loan/${loanId}/bank-accounts/${bankAccountId}/verification`)
      .send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async resendPadAgreementConfirmation(
    loanId: string,
    agreementId: string,
    body: parsers.padBankAccount.ResendPadAgreementConfirmation
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api
      .post(`/bank/loan/${loanId}/pad-agreements/${agreementId}/confirmation/resend`)
      .send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async servicingChangePadDebitDate(
    loanId: string,
    body: parsers.padBankAccount.ServicingChangePadDebitDate
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.patch(`/bank/loan/${loanId}/servicing/debit-date`).send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async servicingDisablePadAutoPay(
    loanId: string,
    body: parsers.padBankAccount.ServicingDisablePadAutoPay
  ): Promise<parsers.padBankAccount.PadPaymentProfile> {
    const response = await this.api.post(`/bank/loan/${loanId}/servicing/auto-pay/disable`).send(body);
    return parsers.padBankAccount.parsePadPaymentProfile(response.body);
  }

  public async verifyBankAccount(bankAccountId: string, amounts: unknown): Promise<Response> {
    throw new NotImplementedError("not implemented");
  }

  public async deleteBankAccount(bankAccountId: string): Promise<Response> {
    return this.api.delete(`/bank/account/${bankAccountId}`);
  }

  public async deleteAllConnectedPlaidAccounts(): Promise<{
    bankAccounts?: string[];
    paymentAccounts?: string[];
    incomes?: string[];
    debts?: string[];
    assets?: string[];
    files?: string[];
    applications?: string[];
  }> {
    const response = await this.api.delete("/bank/account/all");
    return response.body || {};
  }

  public async editBankAccount(
    bankAccountId: string,
    body: parsers.bankAccount.PatchBankAccountSubmission
  ): Promise<Response> {
    return this.api.patch(`/bank/account/${bankAccountId}`).send(body);
  }

  public getPostalScore = async (
    postalCode: string
  ): Promise<{
    postalScoreCheckStatus: enums.EngineRuleStatus;
    bdm?: {
      email: string;
      firstName: string;
      lastName: string;
      title?: string;
      photoLink?: string;
      phone: string;
      calendarLink?: string;
    };
  }> => {
    const response = await this.api.get("/application/postal-score").query({ postalCode });
    return response.body;
  };

  public async searchForBrokerage(brokerageString: string): Promise<
    {
      id: string;
      name: string;
      tradeName?: string;
      officeLocation?: entities.OfficeLocation;
      hasParent: boolean;
      childCount: number;
    }[]
  > {
    const response = await this.api.get("/broker/group/search").query({ q: brokerageString }).retry(1);
    return response.body;
  }

  public async getBdmForBrokerage(id: string): Promise<
    | {
        email: string;
        firstName: string;
        lastName: string;
        title?: string;
        photoLink?: string;
        phone: string;
        calendarLink?: string;
      }
    | undefined
  > {
    const response = await this.api.get(`/broker/group/${id}/bdm`).retry(1);
    return response.body;
  }

  public async createPaymentOrder(
    body: parsers.payment.PaymentOrder,
    idempotencyKey: string
  ): Promise<Response> {
    return this.getIdempotentRequest(this.api.post("/bank/payments").send(body).retry(0), idempotencyKey);
  }

  public async cancelPendingPayment(transactionId: string, idempotencyKey: string): Promise<Response> {
    return this.getIdempotentRequest(
      this.api.delete(`/bank/payments/${transactionId}/cancel`).retry(0),
      idempotencyKey
    );
  }

  public async getPaymentSubscriptions(
    loanId: string
  ): Promise<parsers.paymentSubscription.UsablePaymentSubscription[]> {
    const response = await this.api.get(`/bank/paymentSubscription/loan/${loanId}`);
    return parsers.paymentSubscription.parseUsablePaymentSubscriptions(response.body.data);
  }

  public async createPaymentSubscription(
    body: parsers.paymentSubscription.PaymentSubscriptionBody
  ): Promise<Response> {
    return this.api.post("/bank/paymentSubscription").send(body);
  }

  public async deletePaymentSubscription(id: string): Promise<Response> {
    return this.api.delete(`/bank/paymentSubscription/${id}`);
  }

  public async getApplicationDocuments(
    applicationId: string,
    { status, q }: { status?: enums.ApplicationTaskApprovalStatus; q?: string }
  ): Promise<Saved<entities.UploadedFile>[]> {
    const response = await this.api.get(`/document/file/application/${applicationId}`).query({ status, q });
    return plainToInstance(
      entities.UploadedFile,
      response.body.data as object[]
    ) as Saved<entities.UploadedFile>[];
  }

  public async getApplicationLoanPackage(
    applicationId: string,
    { folders }: { folders?: GoogleDriveFolder[] } = {}
  ): Promise<types.FileURL> {
    const request = this.api.get(`/document/file/application/${applicationId}/zip`);
    if (folders?.length) {
      request.query({ folders: folders.join(",") });
    }
    const response = await request;
    return response.body;
  }

  public async getApplicationDocumentUrl(id: string): Promise<types.FileURL> {
    const response = await this.api.get(`/document/file/${id}/url`);
    return response.body;
  }

  /*
   * Admin authenticated requests (with fraction-secret header)
   */

  public async getDocumentTypes(): Promise<Saved<entities.DocumentType>[]> {
    const response = await this.api.get("/document/documentType");
    return plainToInstance(entities.DocumentType, response.body) as Saved<entities.DocumentType>[];
  }

  public async getDocumentClassificationCatalog(): Promise<types.DocumentClassificationCatalog> {
    const response = await this.api.get("/document/documentType/classification-catalog");
    return response.body;
  }

  public async deprecateDocumentType(id: string, deprecated: boolean): Promise<void> {
    await this.api.patch(`/document/documentType/${id}`).send({ deprecated });
  }

  public async createDocumentType(
    body: parsers.documentType.DocumentTypeNoId
  ): Promise<parsers.documentType.DocumentType> {
    const response = await this.api.post("/document/documentType").send(body);
    return parsers.documentType.parseDocumentType(response.body);
  }

  public async updateDocumentType(
    body: parsers.documentType.DocumentType
  ): Promise<parsers.documentType.DocumentType> {
    const response = await this.api.patch(`/document/documentType/${body.id}`).send(body);
    return parsers.documentType.parseDocumentType(response.body);
  }

  public async createLabeledDocumentAndStartSplitJob(
    labeledDocument: parsers.documentSplit.CreateLabeledDocumentArgs,
    opts?: {
      async?: boolean;
      usePoppler?: boolean;
    }
  ): Promise<
    | parsers.asyncJob.AsyncJobCreationLabeledDocumentHandlerResponse
    | { uploadedFile: entities.UploadedFile; presignedUrl: string }
  > {
    const response = await this.api
      .post("/document/label")
      .send({ ...labeledDocument, async: opts?.async ?? false, usePoppler: opts?.usePoppler })
      .timeout(35000);
    return response.body;
  }

  /**
   * Combine existing documents of an application, in order, into one document of a type. Returns
   * the split job making it; the documents are only replaced once that job succeeds. No `jobId`
   * when the same combine was already requested moments ago (`deduplicated`).
   */
  public async combineDocuments(
    applicationId: string,
    body: parsers.documentSplit.CombineUploadedFilesArgs
  ): Promise<{ jobId?: string; labeledDocumentId?: string; deduplicated?: boolean }> {
    const response = await this.api
      .post(`/document/application/${applicationId}/combine`)
      .send(body)
      .timeout(35000);
    return response.body;
  }

  public async getDocumentSplitJobStatus(
    applicationId: string,
    jobId: string
  ): Promise<{ status: enums.AsyncJobStatus; uploadedFileId?: string; error?: string }> {
    const response = await this.api.get(`/document/application/${applicationId}/split-job/${jobId}`);
    return response.body;
  }

  /**
   * Which deal a document belongs to, from its native reading — QR code, printed application id,
   * same bytes already filed, then the borrowers and property in its text. No model runs; pass the
   * local agent's `extraction` only when the ladder could not decide on its own.
   */
  public async matchMagicUploadDocument(body: {
    pages: Array<{ pageNumber: number; text: string; barcodes?: Array<{ payload: string }> }>;
    fileHash?: string;
    extraction?: MagicUploadExtraction;
  }): Promise<{ match?: MagicUploadMatch; candidates: MagicUploadMatch[] }> {
    const response = await this.api.post("/admin/magicUpload/match-document").send(body);
    return response.body;
  }

  public async uploadMagicUploadFiles(
    assignments: Array<{
      s3Key: string;
      s3Bucket: string;
      filename: string;
      applicationId: string;
      /** The type a person confirmed; the file is filed as it. */
      documentType?: DocumentType;
    }>
  ): Promise<{
    results: Array<{ filename: string; uploadedFileId?: string; error?: string }>;
  }> {
    const response = await this.api.post("/admin/magicUpload/upload").send({ assignments });
    return response.body;
  }

  public async getPresignedAdminFileUploadUrl(bucket: S3Bucket, key: string): Promise<string> {
    const response = await this.api.get("/document/file/admin/create").query({ key, bucket });
    return response.body.fileURL;
  }

  public async uploadFile(file: File, url: string, contentType?: string): Promise<void> {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: contentType ? { "Content-Type": contentType } : {},
    });
    if (!response.ok) {
      throw new ValueError(`Unable to upload the file (${response.status})`);
    }
    // let req = this.api.put(url).send(file);
    // if (contentType) {
    //   req = req.set("Content-Type", contentType);
    // }
    // await (req);
  }

  public async createAdminUploadedFile({
    s3Bucket,
    s3Key,
    applicationId,
    fileType,
    status,
    notes,
  }: {
    s3Bucket: S3Bucket;
    s3Key: string;
    applicationId: string;
    fileType?: string;
    status?: enums.ApplicationTaskApprovalStatus;
    notes?: string;
  }): Promise<Saved<entities.UploadedFile> & { pages: Saved<entities.UploadedFilePage>[] }> {
    const response = await this.api.post("/document/file/admin").send({
      applicationId,
      s3Bucket,
      s3Key,
      fileType,
      status,
      notes,
    });
    return transformAndValidate(entities.UploadedFile, response.body as object) as Promise<
      Saved<entities.UploadedFile> & {
        pages: Saved<entities.UploadedFilePage>[];
      }
    >;
  }

  public async createDocumentSplitJob(
    classificationId: string
  ): Promise<parsers.asyncJob.AsyncJobCreationHandlerResponse> {
    const response = await this.api
      .post("/document/documentSplit/admin/splitFromClassification")
      .send({ classificationId });

    return parsers.asyncJob.parseAsyncJobCreationHandlerResponse(response.body);
  }

  public async getDocumentSplitAsyncJob(jobId?: string): Promise<entities.AsyncDocumentSplitJob> {
    const response = await this.api.get(`/admin/asyncJob/${jobId}`);
    return transformAndValidate(entities.AsyncDocumentSplitJob, response.body as object);
  }

  public async initiateIDVerification({
    applicantId,
  }: InitiateIDVerificationArgs): Promise<types.IdVerificationReturnType> {
    const response = await this.api.post(`/application/applicant/${applicantId}/idVerification`).send({});
    if (!types.isIdVerificationReturn(response.body)) {
      throw new ValueError(
        `/application/applicant/${applicantId}/idVerification returned an incorrect id verification response`
      );
    }
    return response.body;
  }

  public async continueIDVerification({
    applicantId,
    answers,
    attemptId,
  }: AnswerIDVerificationArgs): Promise<types.IdVerificationReturnType> {
    const response = await this.api.post(`/application/applicant/${applicantId}/idVerification`).send({
      kiqAnswers: answers,
      attemptId,
    });
    return response.body as types.IdVerificationReturnType;
  }

  public async getTestApplication(type: string): Promise<entities.Application | undefined> {
    const response = await this.api.get(`/application/test/${type}`);
    if (response.statusCode !== 200) {
      return;
    }
    return transformAndValidate(entities.Application, response.body as object);
  }

  public async getChecklist({
    crmId,
    type,
  }: {
    crmId?: string;
    type?: ChecklistType;
  }): Promise<parsers.checklist.PerformedChecklist> {
    const response = await this.api.post("/ops/checklist").send({
      id: crmId,
      type,
    });
    return parsers.checklist.parsePerformedChecklist(response.body);
  }

  public async setApplicationStatus({
    id,
    status,
    force,
    brokerNotes,
  }: {
    id: string;
    status: SyntheticApplicationStatus | enums.LoanStatus;
    force?: boolean;
    brokerNotes?: string;
  }): Promise<
    Record<
      "succeeded" | "failed",
      {
        failures?: types.ChecklistResult[];
        applicationId: string;
        notes?: string;
        status: enums.ApplicationStatus;
        syntheticStatus?: SyntheticApplicationStatus;
      }[]
    >
  > {
    const response = await this.api.patch("/application/status").send([
      {
        applicationId: id,
        status,
        force,
        brokerNotes,
      },
    ]);
    return response.body;
  }

  public async getCrxData({ crmId, type }: { crmId?: string; type?: string }) {
    const response = await this.api.get(`/ops/crx/${crmId}/${type}`).send();
    return response.body;
  }

  public async getProtectedFileSignedUrl({
    password,
    token,
  }: {
    password: string;
    token: string;
  }): Promise<{ fileUrl: string; key: string }> {
    const response = await this.api
      .get("/document/file/protected-file")
      .set("Authorization", password)
      .query({ token });
    return response.body;
  }

  public async getCrxApplicants({ crmId }: { crmId?: string }): Promise<entities.Applicant[]> {
    const response = (await this.api.get(`/ops/crx/${crmId}/applicants`).send()) as {
      body: object[];
    };
    return Promise.all(
      response.body.map((body: object) => {
        return transformAndValidate(entities.Applicant, body as object);
      })
    );
  }

  public async getCrxLoan({ crmId }: { crmId?: string }): Promise<entities.Loan> {
    const response = (await this.api.get(`/ops/crx/${crmId}/loan`).send()) as {
      body: object[];
    };
    return transformAndValidate(entities.Loan, response.body as object);
  }

  public async getCrxApplication({ crmId }: { crmId?: string }): Promise<entities.Application> {
    const response = (await this.api.get(`/ops/crx/${crmId}/application`).send()) as {
      body: object[];
    };
    return transformAndValidate(entities.Application, response.body as object);
  }

  public async updateApplication(
    { id }: { id?: string },
    updates: Partial<entities.ApplicationT>
  ): Promise<entities.ApplicationT> {
    const response = await this.api.patch(`/application/${id}`).send(updates);
    return plainToInstance(entities.Application, response.body as object);
  }

  public async createProperty(
    applicationId: string,
    property: Partial<entities.Property>
  ): Promise<entities.Property> {
    const { owners, ...rest } = property as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.post(`/application/${applicationId}/property`).send(body);
    return plainToInstance(entities.Property, response.body as object);
  }

  public async updateProperty(
    applicationId: string,
    propertyId: string,
    updates: Partial<entities.Property>
  ): Promise<entities.Property> {
    const { owners, ...rest } = updates as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.patch(`/application/${applicationId}/property/${propertyId}`).send(body);
    return plainToInstance(entities.Property, response.body as object);
  }

  public async setSubjectProperty(applicationId: string, propertyId: string): Promise<{ message: string }> {
    const response = await this.api
      .patch(`/application/${applicationId}/subject-property`)
      .send({ propertyId });
    return response.body as { message: string };
  }

  public async upsertApplicationSolicitor(
    applicationId: string,
    updates: Partial<entities.Solicitor>
  ): Promise<entities.Solicitor> {
    const response = await this.api.put(`/application/${applicationId}/solicitor`).send(updates);
    return plainToInstance(entities.Solicitor, response.body as object);
  }

  public async updateLoan(applicationId: string, updates: Partial<entities.Loan>): Promise<entities.Loan> {
    const response = await this.api.patch(`/application/${applicationId}/loan`).send(updates);
    return plainToInstance(entities.Loan, response.body as object);
  }

  public async getPortfolioProperties(filters: PortfolioFilters = {}) {
    const response = await this.api
      .get("/admin/portfolio/analytics/properties")
      .query(constructPortfolioFilter(filters))
      .timeout(30000);
    return response.body;
  }

  public async getPortfolioDocuments(): Promise<Saved<entities.S3Document>[]> {
    const response = await this.api.get("/admin/portfolio/document").send();
    return response.body;
  }

  public async getPortfolioDocumentSignedUrl(s3DocumentId: string): Promise<types.FileURL> {
    const response = await this.api.get(`/admin/portfolio/document/${s3DocumentId}/url`).send();
    return response.body;
  }

  public async getPortfolioAnalytics() {
    const response = await this.api.get("/admin/portfolio/analytics").send();
    return response.body;
  }

  public async startPhoneVerification(phone: string): Promise<{ status: "pending" | "approved" }> {
    const response = await this.api.post("/user/verify/sms/start").send({ to: phone });
    return response.body;
  }

  public async checkPhoneVerification(
    phone: string,
    code: string
  ): Promise<{ status: "pending" | "approved" }> {
    const response = await this.api.post("/user/verify/sms/check").send({ to: phone, code });
    return response.body;
  }

  public async makeChannelPrimary(channelId: string): Promise<entities.Channel> {
    const response = await this.api.post(`/user/channel/${channelId}/make-primary`);
    return response.body;
  }

  public async deleteChannel(channelId: string): Promise<{ ok: boolean }> {
    const response = await this.api.delete(`/user/channel/${channelId}`);
    return response.body;
  }

  // Admin methods for managing user channels
  public async createUserChannel(
    userId: string,
    channel: {
      type: enums.ChannelType;
      value: string;
      emailType?: enums.EmailType;
      phoneType?: enums.PhoneType;
      primary?: boolean;
      verified?: boolean;
    }
  ): Promise<entities.Channel> {
    const response = await this.api.post(`/user/${userId}/channel`).send(channel);
    return plainToInstance(entities.Channel, response.body as object);
  }

  public async updateUserChannel(
    userId: string,
    channelId: string,
    updates: {
      value?: string;
      emailType?: enums.EmailType | null;
      phoneType?: enums.PhoneType | null;
      primary?: boolean;
      verified?: boolean;
    }
  ): Promise<entities.Channel> {
    const response = await this.api.put(`/user/${userId}/channel/${channelId}`).send(updates);
    return plainToInstance(entities.Channel, response.body as object);
  }

  public async adminUpdateUser(
    userId: string,
    updates: {
      email?: string;
      accountStatus?: enums.UserAccountStatus;
    }
  ): Promise<entities.User> {
    const response = await this.api.put(`/user/${userId}/admin`).send(updates);
    return plainToInstance(entities.User, response.body as object);
  }

  public async deleteUserChannel(userId: string, channelId: string): Promise<{ ok: boolean }> {
    const response = await this.api.delete(`/user/${userId}/channel/${channelId}`);
    return response.body;
  }

  public async startEmailVerification(email: string): Promise<{ status: "pending" | "approved" }> {
    const response = await this.api.post("/user/verify/email/start").send({ to: email });
    return response.body;
  }

  public async checkEmailVerification(
    email: string,
    code: string
  ): Promise<{ status: "pending" | "approved" }> {
    const response = await this.api.post("/user/verify/email/check").send({ to: email, code });
    return response.body;
  }

  /**
   * Account-claim OTP flow. Lists the masked email/phone channels on a
   * not-yet-registered user that the visitor can verify ownership of to claim
   * the account. Raw values stay server-side; the OTP is sent by channel id.
   */
  public async getClaimableChannels(
    userId: string
  ): Promise<{ id: string; type: enums.ChannelType; masked: string }[]> {
    const response = await this.api.get(`/user/${userId}/claimable-channels`);
    return response.body;
  }

  /** Send an OTP to the channel (looked up by id) on the user being claimed. */
  public async startClaimVerification(args: {
    userId: string;
    channelId: string;
  }): Promise<{ status: "pending" | "approved" }> {
    const response = await this.api.post("/user/claim/start").send(args);
    return response.body;
  }

  /**
   * Check the claim OTP. On approval the server marks the channel verified and
   * returns a bearer token for the claimed user. `hasVerifiedEmail` is false
   * after a phone-only verification — the caller must then add/verify an email
   * before the account can register.
   */
  public async checkClaimVerification(args: { userId: string; channelId: string; code: string }): Promise<{
    status: "approved" | "pending" | "cancelled";
    channelType: "sms" | "email";
    hasVerifiedEmail: boolean;
    token?: string;
  }> {
    const response = await this.api.post("/user/claim/check").send(args);
    return response.body;
  }

  public async getPortfolioTypes(filters: PortfolioFilters = {}): Promise<{
    timedOut: boolean;
    propertyTypes: (Record<string | "All", number> & {
      type: enums.PropertyType;
    })[];
    propertyMSAs: types.PropertyMsaRecord[];
    incomeSources: {
      type: enums.IncomeType;
      count: number;
      percentage: number;
    }[];
    applicantAges: parsers.portfolio.DataAndStats;
    loanSizes: parsers.portfolio.DataAndStats;
    creditScores: parsers.portfolio.ToggleableDataAndStats;
    loanToValue: {
      stats: { current: parsers.stats.Stats; atOrigination: parsers.stats.Stats };
      data: parsers.portfolio.LoanToValueData[];
    };
    portfolioSize: {
      fundings: parsers.portfolio.CountAndSumByQuarter[];
      total: parsers.portfolio.CountAndSumByQuarter[];
      payoffs: parsers.portfolio.CountAndSumByQuarter[];
    };
    loanToValueOverTime: GroupingLtvOverTimeData;
    propertyValueOverTime: GroupingPropertyValueOverTimeData;
    fundings: parsers.portfolio.CountAndSumByDay[];
    payouts: parsers.portfolio.Payout[];
    payoutGroupings: parsers.portfolio.PayoutGrouping[];
    useOfFunds: parsers.portfolio.UseOfFunds[];
    brokerVsDirect: { count: number; type: "broker" | "direct" }[];
    collateralGroupings: parsers.portfolio.PayoutGrouping[];
    weightedAverageCoupon: {
      withAppreciation: boolean;
      group: string;
      data: (parsers.stats.Stats & { year?: number; wac: number })[];
    }[];
    weightedAverageMaturity: {
      group: string;
      data: { year?: number; wam: number; wam_count: number; wa_termlength: number }[];
    }[];
    tdsAndGds: {
      tds: {
        pre: parsers.stats.Stats;
        post: parsers.stats.Stats;
      };
      gds: {
        pre: parsers.stats.Stats;
        post: parsers.stats.Stats;
      };
    };
    portfolioOverview: parsers.portfolio.Overview;
    projectedMaturities: { month: string; count: number; volume: number }[];
    creditScoreDelta: {
      data: parsers.stats.Stats & {
        data: { count: number; scoreDelta: number; percentage: number; label: string }[];
      };
    };
  }> {
    const response = await this.api
      .get("/admin/portfolio/analytics/types")
      .query(constructPortfolioFilter(filters))
      .timeout(30000);
    return response.body;
  }

  public async getPortfolioLoan(applicationId: string): Promise<types.PortfolioApp | undefined> {
    const response = await this.api.get(`/admin/portfolio/analytics/application/${applicationId}`).send();
    return response.body;
  }

  public async getPortfolioData(filters: PortfolioFilters = {}): Promise<entities.LoanT[]> {
    const response = await this.api
      .get("/admin/portfolio/analytics/application")
      .query(constructPortfolioFilter(filters))
      .send();

    return plainToInstance(entities.Loan, response.body);
  }

  public async createPrepayment(
    loanId: string,
    body: {
      type: types.PrepaymentType;
    }
  ): Promise<Response> {
    return this.api.post(`/bank/prepayment/${loanId}`).send(body);
  }

  public async createLoanDraw(loanId: string, body: parsers.loanDraw.LoanDraw): Promise<Response> {
    return this.api.post(`/bank/loanDraw/${loanId}`).send(body);
  }

  /**
   * Starting the bank account linking
   */
  public getPlaidLinkToken = async (
    purpose: "underwriting" | "payments",
    products: ("assets" | "liabilities" | "income_verification")[]
  ): Promise<string> => {
    const response = await this.api.post("/bank/link").send({ purpose, products });
    return response.body?.linkToken;
  };

  public registerPlaidBank = async (
    publicToken: string,
    applicationId: string,
    purpose: "underwriting" | "payments"
  ): Promise<{
    linkToken: string;
    plaidToken: string;
    /** Set on underwriting connects: the async debt-reconciliation job to poll. */
    reconciliationJobId?: string;
  }> => {
    const response = await this.api.post("/bank/link/register").send({
      publicToken,
      applicationId,
      purpose,
    });
    return response.body;
  };

  public getPlaidIncome = async (): Promise<any> => {
    const response = await this.api.get("/bank/income").send();
    return response.body?.income;
  };

  public getPlaidAssets = async (): Promise<any> => {
    const response = await this.api.get("/bank/assets").send();
    return response.body?.income;
  };

  public async getAppChecklist(
    appId: string,
    status: SyntheticApplicationStatus | enums.LoanStatus,
    accountType?: UserAccountType,
    // refresh=true forces a synchronous recompute server-side (the endpoint
    // otherwise serves the checklist cache and refreshes it in the background).
    { refresh }: { refresh?: boolean } = {}
  ): Promise<types.ChecklistResult[]> {
    const response = await this.api
      .get(`/application/${appId}/checklist/${status}`)
      .query({ accountType, refresh: refresh ? "true" : undefined });
    return sortChecklistItems(response.body, {
      apiBaseUrl: this._baseURL,
      token: this.token,
    });
  }

  public async getAllAppChecklists(
    appId: string,
    accountType?: UserAccountType
  ): Promise<Partial<Record<SyntheticApplicationStatus | enums.LoanStatus, types.ChecklistResult[]>>> {
    const response = await this.api.get(`/application/${appId}/checklist`).query({ accountType });
    return Object.fromEntries(
      Object.entries(response.body).map(([status, checklist]) => [
        status,
        sortChecklistItems(checklist as types.ChecklistResult[], {
          apiBaseUrl: this._baseURL,
          token: this.token,
        }),
      ])
    );
  }

  public async initiateTask({
    applicantId,
    taskType,
    channels,
  }: {
    applicantId: string;
    taskType: ApplicationTaskType;
    channels: ("email" | "sms")[];
  }) {
    const response = await this.api
      .post(`/application/applicant/${applicantId}/${taskType}/initiate`)
      .send({ channels });
    return response.body;
  }

  public getComms = async ({
    offset,
    limit,
    app,
  }: {
    offset?: number;
    limit?: number;
    app?: string;
  } = {}): Promise<types.ArrayResponse<entities.Communication>> => {
    const response = await this.api.get("/comms").timeout(30000).query({
      offset,
      limit,
      app,
    });
    const data = response.body.data;
    return {
      count: response.body.count,
      data: plainToInstance(entities.Communication, data),
    };
  };

  public getApplicationNotifications = async (
    applicationId: string,
    offset = 0,
    status?: "failed",
    q?: string
  ): Promise<{ count: number; data: ApplicationNotification[] }> => {
    const response = await this.api
      .get(`/application/${applicationId}/notifications`)
      .query({ offset, status, q });
    return response.body;
  };

  public getComm = async (id: string): Promise<entities.Communication> => {
    const response = await this.api.get(`/comms/${id}`).timeout(30000);

    return plainToInstance(entities.Communication, response.body) as entities.Communication;
  };

  public getPhoneConversation = async (id: string): Promise<types.PhoneConversation> => {
    const response = await this.api.get(`/comms/phone/${id}`);
    return response.body;
  };

  public attachPhoneConversationApplication = async (
    id: string,
    applicationId: string
  ): Promise<types.PhoneConversation> => {
    const response = await this.api.post(`/comms/phone/${id}/applications`).send({ applicationId });
    return response.body;
  };

  public detachPhoneConversationApplication = async (
    id: string,
    applicationId: string
  ): Promise<types.PhoneConversation> => {
    const response = await this.api.delete(`/comms/phone/${id}/applications/${applicationId}`);
    return response.body;
  };

  public linkPhoneConversationPerson = async (
    id: string,
    userId: string
  ): Promise<types.PhoneConversation> => {
    const response = await this.api.post(`/comms/phone/${id}/people`).send({ userId });
    return response.body;
  };

  public unlinkPhoneConversationPerson = async (
    id: string,
    userId: string
  ): Promise<types.PhoneConversation> => {
    const response = await this.api.delete(`/comms/phone/${id}/people/${userId}`);
    return response.body;
  };

  public recordPhoneConversationMatchAttempt = async (
    id: string,
    attempt: { matcher: string; outcome: enums.CommunicationMatchOutcome }
  ): Promise<types.PhoneConversation> => {
    const response = await this.api.post(`/comms/phone/${id}/match-attempts`).send(attempt);
    return response.body;
  };

  public getUnmatchedPhoneConversations = async ({
    matcher,
    limit,
  }: {
    matcher: string;
    limit?: number;
  }): Promise<types.PhoneConversationListItem[]> => {
    const response = await this.api.get("/comms/unmatched-phone").query({ matcher, limit });
    return response.body;
  };

  public getBrokeragePhoneConversations = async (
    brokerGroupId: string
  ): Promise<types.PhoneConversationListItem[]> => {
    const response = await this.api.get(`/comms/brokerage/${brokerGroupId}/phone`);
    return response.body;
  };

  public getApplicationPhoneConversations = async (
    applicationId: string
  ): Promise<types.PhoneConversationListItem[]> => {
    const response = await this.api.get(`/comms/application/${applicationId}/phone`);
    return response.body;
  };

  /**
   * This is a three-step process.
   * 1. The client gets N number of pre-signed URLs from the backend to upload the file(s) to S3 from GET /document/file/create/combinable
   * 2. The client uploads the file(s) to S3 directly
   * 3. The client sends the details of the file(s) to POST /document/file/register/combinable to combine the files and create an UploadedFile entity
   */
  public async getPresignedFileUploadUrls({
    applicationId,
    numberOfFiles,
  }: {
    applicationId: string;
    numberOfFiles: number;
  }): Promise<types.FileURL[]> {
    const response = await this.api
      .get("/document/file/create/combinable")
      .query({ applicationId, numberOfFiles });
    return response.body;
  }

  public async registerUploadedFilesToApplication(
    args: parsers.file.RegisterDocumentArgs
  ): Promise<Saved<entities.UploadedFile> & { classifyJobId?: string }> {
    const response = await this.api.post("/document/file/register/combinable").send(args);
    const file = plainToInstance(
      entities.UploadedFile,
      response.body as object
    ) as Saved<entities.UploadedFile> & { classifyJobId?: string };
    // plainToInstance drops the non-entity classifyJobId field — carry it back so callers
    // can poll the LLM classification job for upload progress.
    file.classifyJobId = (response.body as { classifyJobId?: string })?.classifyJobId;
    return file;
  }

  public async addFileConnections(
    fileId: string,
    args: {
      documentTypeName: string;
      /** The checklist item the file is being uploaded onto; reconnects it if it was disconnected. */
      checklistItem?: { checker: string; applicantId?: string; debtId?: string; incomeId?: string };
      applicantId?: string;
      debtId?: string;
      assetId?: string;
      incomeId?: string;
      propertyId?: string;
      debtIds?: string[];
      incomeIds?: string[];
      applicantIds?: string[];
      assetIds?: string[];
      propertyIds?: string[];
    }
  ): Promise<Saved<entities.UploadedFile>> {
    const response = await this.api.post(`/document/file/${fileId}/add-connections`).send(args);
    return plainToInstance(entities.UploadedFile, response.body as object) as Saved<entities.UploadedFile>;
  }

  public async setFileConnections(
    fileId: string,
    args: {
      applicantIds: string[];
      debtIds: string[];
      incomeIds: string[];
      assetIds: string[];
      propertyIds: string[];
      valuationId: string | null;
    }
  ): Promise<Saved<entities.UploadedFile>> {
    const response = await this.api.put(`/document/file/${fileId}/connections`).send(args);
    return plainToInstance(entities.UploadedFile, response.body as object) as Saved<entities.UploadedFile>;
  }

  /**
   * Disconnect a file from one checklist item. The file keeps its entity links; the checklist just
   * stops matching it to that item. See `ChecklistItemDisconnection`.
   */
  public async disconnectFileFromChecklistItem(
    fileId: string,
    item: { checker: string; applicantId?: string; debtId?: string; incomeId?: string }
  ): Promise<Saved<entities.ChecklistItemDisconnection>> {
    const response = await this.api.post(`/document/file/${fileId}/checklist-disconnection`).send(item);
    return plainToInstance(
      entities.ChecklistItemDisconnection,
      response.body as object
    ) as Saved<entities.ChecklistItemDisconnection>;
  }

  /** Reconnect a file to a checklist item it was disconnected from. */
  public async reconnectFileToChecklistItem(
    fileId: string,
    item: { checker: string; applicantId?: string; debtId?: string; incomeId?: string }
  ): Promise<void> {
    await this.api.delete(`/document/file/${fileId}/checklist-disconnection`).query(_.omitBy(item, _.isNil));
  }

  public previewDocumentDownloadUrl(documentType: parsers.docGen.DocGenDocumentType): string {
    return `${this._baseURL}/document/generate/${documentType}/preview-test`;
  }

  /**
   * Fraction's published broker rate sheet, as a PDF.
   *
   * Public and unauthenticated, and it 302s to a presigned S3 URL — so this must be used as a plain
   * `<a href>` or `window.open`, never fetched. A cross-origin XHR would have to follow the
   * redirect and would then need CORS on the documents bucket.
   */
  public rateSheetUrl(): string {
    return `${this._baseURL}/document/generate/rate-sheet`;
  }

  public async getFractionContacts(): Promise<entities.InternalEmployee[]> {
    const response = await this.api.get("/broker/contacts");
    return plainToInstance(entities.InternalEmployee, response.body);
  }

  public async getEmployees(types: enums.InternalEmployeeType[]): Promise<entities.InternalEmployee[]> {
    const response = await this.api.get("/ops/contact/employees").query({ types: types.join(",") });
    return plainToInstance(entities.InternalEmployee, response.body?.data);
  }

  public async getTaskQueue(opts?: {
    limit?: number;
    filterByRole?: boolean;
    /** Task types to skip computing server-side (e.g. the noisy heuristic/soft tasks). */
    excludeTypes?: types.TaskType[];
  }): Promise<types.TaskQueueResponse> {
    const response = await this.api.get("/ops/task-queue").query({
      limit: opts?.limit?.toString(),
      filterByRole: opts?.filterByRole === false ? "false" : undefined,
      excludeTypes: opts?.excludeTypes?.length ? opts.excludeTypes.join(",") : undefined,
    });
    return response.body;
  }

  public async getApplicationTasks(
    applicationId: string
  ): Promise<{ tasks: types.ComputedTask[]; computedAt: Date }> {
    const response = await this.api.get(`/ops/task-queue/${applicationId}`);
    return response.body;
  }

  /**
   * Commit an underwriter's decision from the LOS Underwriting tab: notes, the
   * concluded property value, and the conditions (as tasks feeding the checklist).
   *
   * Does NOT move the application's status — chain `approveDeal` / `rejectDeal`
   * after this resolves, so the existing checklist gate, HubSpot sync and Slack
   * notification keep running exactly once each.
   */
  public async submitUnderwritingDecision(
    applicationId: string,
    payload: types.UnderwritingDecisionSubmit
  ): Promise<types.UnderwritingDecisionResult> {
    const response = await this.api.post(`/application/${applicationId}/underwriting-decision`).send(payload);
    return response.body;
  }

  /**
   * Canada Job Bank wage bands, via our passthrough. Goes through the backend
   * because open.canada.ca does not serve our origin over CORS.
   */
  public async searchJobBankWages(opts: {
    query?: string;
    prov?: string;
    nocCode?: string;
    limit?: number;
  }): Promise<types.JobBankWageRecord[]> {
    const response = await this.api.get("/ops/job-bank/wages").query({
      q: opts.query,
      prov: opts.prov,
      noc: opts.nocCode,
      limit: opts.limit?.toString(),
    });
    return response.body;
  }

  /**
   * Balance / interest totals for a single loan as of `date` (`GET /admin/balance/:id`).
   * Read-only; returns the figures the servicing endpoints compute (all in cents).
   */
  public async getLoanBalance(
    loanId: string,
    { date }: { date: Date }
  ): Promise<{
    date: Date;
    applicationId: string;
    loanId: string;
    fundedAmount?: number;
    originationFee?: number;
    balance: number;
    principal: number;
    postedBalance: number;
    balanceForDraws: number;
    totalDraws: number;
    totalPayments: number;
    totalInterest: number;
    totalAccumulatedInterest: number;
  }> {
    const response = await this.api.get(`/admin/balance/${loanId}`).query({ date: date.toISOString() });
    return response.body;
  }

  /**
   * Recalculate interest for a single loan (`POST /admin/interest/calculate`). When
   * `overwrite` is true this overwrites existing interest-accumulation records and the loan's
   * stored balance. `force` / `zeroOutPaidOutLoan` are intentionally not exposed here.
   */
  public async calculateLoanInterest({
    loanId,
    start,
    end,
    overwrite,
  }: {
    loanId: string;
    start: Date;
    end?: Date;
    overwrite?: boolean;
  }): Promise<{ newRecordsCount: number }> {
    const response = await this.api.post("/admin/interest/calculate").send({
      loanId,
      start,
      end,
      overwrite: overwrite ?? false,
    });
    return response.body;
  }

  /**
   * Kick off an interest recalculation as a background task and return its id to poll.
   *
   * A multi-year loan takes well over the API gateway's 30s to recompute, so the servicing console
   * always runs this async rather than the synchronous `calculateLoanInterest` above.
   */
  public async startLoanInterestCalculation(input: {
    loanId: string;
    start: Date;
    end?: Date;
    overwrite?: boolean;
    force?: boolean;
    /**
     * Cents. When the recomputed balance is a residual smaller than this, a cancelling adjustment is
     * written and the loan is zeroed. Sized against the persisted ledger, so it lands exactly on zero.
     */
    zeroOutPaidOutLoan?: number;
  }): Promise<{ taskId: string }> {
    const response = await this.api.post("/admin/interest/calculate").send({ ...input, async: true });
    return response.body;
  }

  /** Poll a task started by `startLoanInterestCalculation`. */
  public async getLoanInterestCalculation(taskId: string): Promise<{
    id: string;
    status: string;
    friendlyStatus?: string;
    output?: { newRecordsCount?: number };
  }> {
    const response = await this.api.get(`/admin/interest/calculate/${taskId}`);
    return response.body;
  }

  /**
   * Attach a bank-feed transaction to a loan and give it its real type. Re-accrues the loan's interest
   * from the transaction's post date, which hand-editing the columns never did.
   */
  public async assignTransactionToLoan(
    id: string,
    input: { loanId: string; type: enums.TransactionType }
  ): Promise<{ transaction: entities.TransactionRecord }> {
    const response = await this.api.post(`/bank/transaction/${id}/assign`).send(input);
    return response.body;
  }

  /** Record a manual ledger entry without initiating a bank payment. */
  public async createManualLoanTransaction(
    loanId: string,
    input: {
      id: string;
      /** Cents. */
      amount: number;
      type: enums.TransactionType;
      direction: enums.TransactionDirection;
      status: enums.TransactionStatus;
      currency: enums.Currency;
      transactionDate: string;
      postDate?: string | null;
      rejectionDate?: string | null;
      description?: string;
      notesInternal?: string;
    }
  ): Promise<{ transaction: entities.TransactionRecord }> {
    const response = await this.api.post(`/bank/transaction/loan/${loanId}/manual`).send(input);
    return response.body;
  }

  /** Detach a transaction from its loan, returning it to the unassigned inbox. */
  public async unassignTransaction(id: string): Promise<{ transaction: entities.TransactionRecord }> {
    const response = await this.api.post(`/bank/transaction/${id}/unassign`).send({});
    return response.body;
  }

  /**
   * Correct many transactions at once. Every affected loan is re-accrued exactly once, from the
   * earliest date any of its edits touched — so a batch of corrections costs one recalculation per
   * loan rather than one per row.
   *
   * Dates accept `null` to clear them; omitting a field leaves it untouched.
   */
  public async bulkUpdateTransactions(
    updates: {
      id: string;
      type?: enums.TransactionType;
      direction?: enums.TransactionDirection;
      /** Cents. */
      amount?: number;
      transactionDate?: string | null;
      postDate?: string | null;
      rejectionDate?: string | null;
      status?: enums.TransactionStatus;
      notesInternal?: string;
    }[]
  ): Promise<{ updated: number; transactions: entities.TransactionRecord[] }> {
    const response = await this.api.post("/bank/transaction/bulk").send({ updates });
    return response.body;
  }

  public async createAgentSession(opts: {
    applicationId?: string;
    message: string;
    workflowId?: string;
    model?: "opus" | "sonnet" | "haiku";
    /**
     * Persona override. Default behaviour: borrower-authed callers get
     * "borrower", employees get "employee". Pass "borrower" as an employee to
     * open a borrower-style session (intake driver + widgets) for testing.
     */
    source?: "employee" | "borrower";
  }): Promise<{ sessionId: string }> {
    const response = await this.api.post("/ops/agent-session").send({
      ...opts,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return response.body;
  }

  /**
   * Anonymously start a borrower /apply session. Returns a bearer token that
   * subsequent requests pass through the `Authorization: BorrowerSession <token>`
   * header (handled automatically by the interceptor above whenever
   * `setActiveBorrowerSession` is set). No auth required for this call itself.
   */
  public async startBorrowerSession(input: {
    applicantEmail?: string;
    applicantFirstName?: string;
    applicantLastName?: string;
    applicantPhone?: string;
    requestedAmount?: number;
  }): Promise<{
    token: string;
    applicationId: string;
    applicantId: string;
    /** Pre-fired agent session — the backend kicks off the agent with a hidden
     *  seed prompt so the assistant speaks first. The frontend polls this
     *  sessionId as soon as the chat mounts. */
    agentSessionId: string;
    application: Pick<entities.ApplicationT, "id" | "eid" | "status" | "requestedAmount">;
  }> {
    const response = await this.api.post("/apply/session").send(input);
    return response.body;
  }

  /**
   * Submit the borrower's draft application — transitions PARTIALLY_COMPLETED
   * → UNDERWRITING. Requires an active `BorrowerSession` token (the axios
   * interceptor attaches it automatically when `setActiveBorrowerSession` is set).
   */
  public async submitBorrowerApplication(applicationId: string): Promise<{
    id: string;
    status: string;
    /** False when server-side readiness blocked the submit; `missing` lists what's left. */
    submitted: boolean;
    missing: Array<{ kind: string; label: string; note?: string; applicant?: string }>;
  }> {
    const response = await this.api.post(`/apply/${applicationId}/submit`).send({});
    return response.body;
  }

  /**
   * Replace the borrower's "interested products" shortlist. The listed
   * evaluations get `borrowerRequestedAt` stamped; any other previously-flagged
   * evaluations on the application have it cleared. Pass `[]` to clear
   * the shortlist entirely. Independent of `selectedAt` / `forReviewAt`.
   */
  public async setBorrowerInterestedProducts(
    applicationId: string,
    evaluationIds: string[]
  ): Promise<{ applicationId: string; interestedEvaluationIds: string[]; reverted: number; set: number }> {
    const response = await this.api
      .post(`/apply/${applicationId}/products/interested`)
      .send({ evaluationIds });
    return response.body;
  }

  /**
   * Employee multi-select: stamp `forReviewAt` on the listed evaluations so
   * the broker / applicant sees them in their "Recommended by Fraction"
   * section. Set semantics — anything previously flagged but not in the new
   * list is cleared. Independent of borrower-requested and selected flags.
   */
  public async setEvaluationsForReview(
    applicationId: string,
    evaluationIds: string[]
  ): Promise<{ applicationId: string; forReviewEvaluationIds: string[]; cleared: number; set: number }> {
    const response = await this.api
      .post(`/application/${applicationId}/products/for-review`)
      .send({ evaluationIds });
    return response.body;
  }

  public async sendProductsForReviewNotifications(
    applicationId: string,
    recipients: ("applicant" | "broker")[]
  ): Promise<{
    applicationId: string;
    sentAt: string;
    sentByUserId: string | null;
    recipients: ("applicant" | "broker")[];
  }> {
    const response = await this.api
      .post(`/application/${applicationId}/products/for-review/send-notifications`)
      .send({ recipients });
    return response.body;
  }

  /**
   * Single-evaluation toggle of `borrowerRequestedAt`. Used by the broker /
   * applicant "I'm Interested" button on the ProductOffers card.
   */
  public async setEvaluationBorrowerRequested(
    applicationId: string,
    evaluationId: string,
    requested: boolean
  ): Promise<{
    applicationId: string;
    evaluationId: string;
    requested: boolean;
    applicationAdvanced: boolean;
    applicationStatus: enums.ApplicationStatus;
  }> {
    const response = await this.api
      .post(`/application/${applicationId}/products/borrower-request`)
      .send({ evaluationId, requested });
    return response.body;
  }

  /**
   * Get a presigned S3 PUT URL for a borrower-side file upload. The URL is
   * scoped to the borrower's applicationId (the backend rejects anything
   * trying to write outside the scoped prefix). Pair with `registerBorrowerFile`
   * after the PUT completes.
   */
  public async createBorrowerFileUploadUrl(filename: string): Promise<{
    fileURL: string;
    key: string;
    bucket: string;
  }> {
    const response = await this.api.get("/apply/file/create").query({ filename });
    return response.body;
  }

  /**
   * After PUTting a file to S3 with the presigned URL, register it so the
   * backend creates the UploadedFile row + kicks off classification.
   */
  public async registerBorrowerFile(input: {
    s3Key: string;
    fileType: string;
    name?: string;
    notes?: string;
    /**
     * Optional `DocumentType.name` to use as a strong classifier prior. When the
     * agent is asking for a specific document (bank statement, void cheque,
     * etc.) we forward this so the file lands on the right shelf without an
     * LLM classification pass.
     */
    documentTypeName?: string;
  }): Promise<{ id: string; filename?: string; s3Key: string }> {
    const response = await this.api.post("/apply/file/register").send(input);
    return response.body;
  }

  /**
   * List files the borrower has uploaded for this application via the chat
   * widget, the side-panel tray, or the page-wide dropzone. Filters server-
   * side to `source = "borrower-upload"` — employee-curated docs aren't here.
   *
   * `applicationId` is required when the caller is an employee (the request
   * carries a Bearer JWT and the backend can't derive the app from the
   * session). For a borrower session the param is ignored server-side, but
   * we always send it so the call works from either auth mode.
   */
  public async listBorrowerFiles(applicationId: string): Promise<{
    files: Array<{
      id: string;
      filename: string | null;
      documentType: string | null;
      uploadedOn: string | number | null;
    }>;
  }> {
    const response = await this.api.get("/apply/file/list").query({ applicationId });
    return response.body;
  }

  /**
   * Soft-delete a borrower-uploaded file. Backend re-verifies the file's
   * `source` tag — only files this borrower's session uploaded can be
   * deleted, so a craftily-guessed fileId can't nuke an employee doc.
   */
  public async deleteBorrowerFile(fileId: string): Promise<{ deleted: boolean; id: string }> {
    const response = await this.api.delete(`/apply/file/${fileId}`);
    return response.body;
  }

  public async getAgentSession(sessionId: string): Promise<{
    sessionId: string;
    applicationId: string;
    status: "processing" | "needs_review" | "completed" | "failed";
    messages: Array<{
      role: "user" | "assistant" | "interactive" | "upload_request" | "employee" | "system";
      content: string;
      timestamp: number;
      messageId?: string;
      widget?: BorrowerWidgetSpec;
      answered?: { value: string; label: string };
      uploadRequest?: BorrowerUploadRequest;
      // Legacy singular shape (pre-multi-upload) is normalised to an array on
      // load by `normalizeUploaded` in the store, so keep both shapes typed
      // here for compatibility with older persisted sessions.
      uploaded?: { fileId: string; filename: string } | Array<{ fileId: string; filename: string }>;
      uploadDone?: boolean;
      employeeUserId?: string;
      employeeName?: string;
    }>;
    error?: string;
    intakeMode?: "intake" | "conversation";
    /** Scripted-intake position — restores the borrower progress bar on reload. */
    intakeProgress?: { current: number; total: number };
    takeoverByUserId?: string | null;
    takeoverByName?: string;
    createdAt: number;
    updatedAt: number;
  }> {
    const response = await this.api.get(`/ops/agent-session/${sessionId}`);
    return response.body;
  }

  public async sendAgentMessage(
    sessionId: string,
    message: string,
    opts?: {
      model?: "opus" | "sonnet" | "haiku";
      /**
       * Client-generated UUID for the message. The backend persists it as
       * the user row's messageId and treats a re-send carrying the same id
       * as an idempotent no-op — so retries can never double-send.
       */
      clientMessageId?: string;
    }
  ): Promise<{ sessionId: string; status: string }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/message`).send({
      message,
      ...(opts?.model ? { model: opts.model } : {}),
      ...(opts?.clientMessageId ? { clientMessageId: opts.clientMessageId } : {}),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return response.body;
  }

  public async cancelAgentSession(sessionId: string): Promise<{ sessionId: string; status: string }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/cancel`).send({});
    return response.body;
  }

  /**
   * Re-invoke the agent for a session that died mid-turn (lambda crash /
   * timeout) — backend finds the last user message in the saved transcript
   * and re-seeds the loop without double-stamping a new user bubble. Used by
   * the borrower frontend's auto-resume on page refresh.
   */
  public async resumeAgentSession(sessionId: string): Promise<{ sessionId: string; status: string }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/resume`).send({});
    return response.body;
  }

  /**
   * Borrower-only. Flip a session out of conversation mode and back into
   * scripted intake. Backend re-invokes the agent with a synthetic kickoff
   * so the next pending intake widget gets re-emitted without the borrower
   * having to type anything.
   */
  public async resumeBorrowerIntake(sessionId: string): Promise<{ sessionId: string; status: string }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/resume-intake`).send({});
    return response.body;
  }

  public async submitAgentVerificationCode(
    sessionId: string,
    opts: { service: string; code: string }
  ): Promise<{ ok: true }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/verification-code`).send(opts);
    return response.body;
  }

  public async takeoverAgentSession(sessionId: string): Promise<{ ok: true }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/takeover`).send({});
    return response.body;
  }

  public async releaseAgentSession(sessionId: string): Promise<{ ok: true }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/release`).send({});
    return response.body;
  }

  public async sendEmployeeAgentMessage(
    sessionId: string,
    message: string
  ): Promise<{ ok: true; messageId: string }> {
    const response = await this.api
      .post(`/ops/agent-session/${sessionId}/employee-message`)
      .send({ message });
    return response.body;
  }

  public async syncAgentSession(sessionId: string): Promise<{ ok: true }> {
    const response = await this.api.post(`/ops/agent-session/${sessionId}/sync`).send({});
    return response.body;
  }

  public async listAgentSessionsBySource(opts: { source: "borrower" | "employee" | "slack" }): Promise<
    Array<{
      sessionId: string;
      applicationId?: string;
      applicationDisplayName?: string;
      status: string;
      borrowerSessionId?: string;
      takeoverByUserId?: string | null;
      takeoverByName?: string;
      lastMessagePreview?: string;
      lastMessageRole?: string;
      intakeMode?: "intake" | "conversation";
      createdAt: string;
      updatedAt: string;
    }>
  > {
    const response = await this.api.get(`/ops/agent-session?source=${opts.source}`);
    return response.body;
  }

  public async listAgentSessions(applicationId: string): Promise<
    Array<{
      sessionId: string;
      applicationId: string;
      status: string;
      messages: Array<{
        role: string;
        content: string;
        timestamp: number;
        messageId?: string;
        widget?: BorrowerWidgetSpec;
        answered?: { value: string; label: string };
        uploadRequest?: BorrowerUploadRequest;
        uploaded?: { fileId: string; filename: string };
      }>;
      error?: string;
      actionsCompleted?: string[];
      intakeMode?: "intake" | "conversation";
      createdAt: string;
      updatedAt: string;
    }>
  > {
    const response = await this.api.get(`/ops/agent-session?applicationId=${applicationId}`);
    return response.body;
  }

  public async trackUserEvent({ type }: { type: enums.EventType }): Promise<void> {
    await this.api.post("/user/event").send({ type });
  }

  public async sendChat({
    message,
    applicationId,
    type,
    ts,
  }: {
    message: string;
    applicationId: string;
    type: enums.ChatType;
    ts?: number;
  }): Promise<void> {
    await this.api.post(`/application/${applicationId}/chat`).send({ message, type, ts });
  }

  public async getChat({
    applicationId,
    type,
    cursor,
    limit,
  }: {
    applicationId: string;
    type: enums.ChatType;
    cursor?: string;
    limit?: number;
  }): Promise<{ data: entities.Chat[]; nextCursor: string | null }> {
    const query: Record<string, any> = { type };
    if (cursor) query.cursor = cursor;
    if (limit) query.limit = limit;
    const response = await this.api.get(`/application/${applicationId}/chat`).query(query);
    return {
      data: plainToInstance(entities.Chat, response.body?.data),
      nextCursor: response.body?.nextCursor,
    };
  }

  public async getChatMembers({
    applicationId,
    type,
  }: {
    applicationId: string;
    type: enums.ChatType;
  }): Promise<entities.User[]> {
    const response = await this.api.get(`/application/${applicationId}/chat/members`).query({ type });
    return plainToInstance(entities.User, response.body?.data);
  }

  public async updateApplicationEmployee({
    applicationId,
    role,
    employeeId,
  }: {
    applicationId: string;
    role: enums.InternalEmployeeType;
    employeeId: string;
  }): Promise<void> {
    await this.api.patch(`/application/${applicationId}/employee/${role}`).send({ employeeId });
  }

  public async setApplicationFlag(
    applicationId: string,
    flag: enums.EmployeeApplicationFlagType,
    validUntil: Date
  ): Promise<void> {
    await this.api
      .patch(`/application/${applicationId}/flag`)
      .send({ flag, validUntil: validUntil.toISOString() });
  }

  public async clearApplicationFlag(
    applicationId: string,
    flag: enums.EmployeeApplicationFlagType
  ): Promise<void> {
    await this.api.delete(`/application/${applicationId}/flag/${flag}`);
  }

  public async rescindDeal(dealId: string, withdrawnReason: string): Promise<void> {
    await this.api
      .patch("/application/status")
      .send([
        { withdrawnReason, applicationId: dealId, status: enums.ApplicationStatus.HOMEOWNER_RESCINDED },
      ]);
  }

  /**
   * Soft-delete a deal. The backend rescinds it, moves the Hubspot deal to Closed Lost
   * and archives the Slack channel before hiding the row — no notification emails.
   * Available to the brokers on the file as well as employees.
   */
  /**
   * Archive a deal: soft-delete it and archive its Slack channel, nothing else.
   *
   * Not `closeApplication` — that rescinds the deal, moves the Hubspot stage to Closed Lost
   * and posts a closing message. Archiving is purely a filing action and is reversible via
   * {@link unarchiveApplication}.
   */
  public async archiveApplication(applicationId: string): Promise<void> {
    await this.api.post(`/application/${applicationId}/archive`);
  }

  /** Restore an archived deal. The Slack channel stays archived. */
  public async unarchiveApplication(applicationId: string): Promise<void> {
    await this.api.post(`/application/${applicationId}/unarchive`);
  }

  public async closeApplication(dealId: string, withdrawnReason?: string): Promise<void> {
    await this.api.post(`/application/${dealId}/close`).send({ withdrawnReason });
  }

  public async rejectDeal(dealId: string, declineReasons: enums.DeclineReason[]): Promise<void> {
    await this.api.patch("/application/status").send([
      {
        declineReasons,
        applicationId: dealId,
        status: enums.ApplicationStatus.PENDING_DECLINE,
        force: true,
      },
    ]);
  }

  public async approveDeal(
    dealId: string,
    underwritingNotes: string,
    status: enums.ApplicationStatus = enums.ApplicationStatus.APPROVED_FOR_COMMITMENT_LETTER
  ): Promise<void> {
    await this.api.patch("/application/status").send([
      {
        underwritingNotes,
        applicationId: dealId,
        status,
        force: true,
      },
    ]);
  }

  public async reopenDeal(dealId: string): Promise<entities.ApplicationT> {
    const response = await this.api.post(`/application/${dealId}/reopen`);
    return plainToInstance(entities.Application, response.body as object);
  }

  public async updateInsurancePolicy(
    applicationId: string,
    insurancePolicyId: string,
    uploadedFileId: string | undefined,
    insurancePolicy: Partial<entities.InsurancePolicy>
  ): Promise<entities.InsurancePolicy> {
    const response = await this.api
      .patch(`/application/${applicationId}/insurance-policy/${insurancePolicyId}`)
      .send({ ...insurancePolicy, uploadedFileId });
    return plainToInstance(entities.InsurancePolicy, response.body as object);
  }

  public async createInsurancePolicy(
    applicationId: string,
    uploadedFileId: string,
    insurancePolicy?: Partial<entities.InsurancePolicy>
  ): Promise<entities.InsurancePolicy> {
    const response = await this.api
      .post(`/application/${applicationId}/insurance-policy`)
      .send({ ...insurancePolicy, uploadedFileId });
    return plainToInstance(entities.InsurancePolicy, response.body as object);
  }

  public async getInsurancePolicy(
    applicationId: string,
    insurancePolicyId: string
  ): Promise<entities.InsurancePolicy> {
    const response = await this.api.get(
      `/application/${applicationId}/insurance-policy/${insurancePolicyId}`
    );
    return plainToInstance(entities.InsurancePolicy, response.body as object);
  }

  /**
   * Delete just the insurance policy row (unlinks files but does not delete them).
   */
  public async deleteInsurancePolicyRow(applicationId: string, insurancePolicyId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/insurance-policy/${insurancePolicyId}`);
  }

  /**
   * Delete an insurance policy file based on the uploaded file id.
   * If that is the last file in the insurance policy, the insurance policy will be deleted.
   */
  public async deleteInsurancePolicyFile(applicationId: string, uploadedFileId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/insurance-policy/${uploadedFileId}`);
  }

  public async getDocumentsNeedingApproval(
    filterTypes?: ("broker" | "applicant" | "conveyancer")[]
  ): Promise<entities.UploadedFile[]> {
    const results = await this.api
      .get("/document/file/approval")
      .query(filterTypes ? { types: filterTypes.join(",") } : {});
    return plainToInstance(entities.UploadedFile, results.body as object[]);
  }

  public async requestDocumentApproval({
    id,
    reviewerId,
    notes,
  }: {
    id: string;
    reviewerId?: string;
    notes?: string;
  }): Promise<void> {
    await this.api.post(`/document/file/approval/${id}/request`).send({ reviewerId, notes });
  }

  public async setDocumentType({ id, typeId }: { id: string; typeId: string }): Promise<void> {
    await this.api.patch(`/document/file/${id}`).send({ typeId });
  }

  /**
   * Give a document another type it also is (see `UploadedFileAdditionalType`). A document typed
   * only as a placeholder takes it as its primary type instead. Returns the document's types after.
   */
  public async addAdditionalDocumentType({
    id,
    documentTypeId,
  }: {
    id: string;
    documentTypeId: string;
  }): Promise<entities.UploadedFile> {
    const response = await this.api.post(`/document/file/${id}/additional-types`).send({ documentTypeId });
    return plainToInstance(entities.UploadedFile, response.body as object);
  }

  /** Take an additional type off a document. Returns the document's types after. */
  public async removeAdditionalDocumentType({
    id,
    documentTypeId,
  }: {
    id: string;
    documentTypeId: string;
  }): Promise<entities.UploadedFile> {
    const response = await this.api.delete(`/document/file/${id}/additional-types/${documentTypeId}`);
    return plainToInstance(entities.UploadedFile, response.body as object);
  }

  /**
   * Reject the robot's guess at a document's type, and record that it was rejected.
   *
   * Used to be a `PATCH` nulling `robotGuessedTypeId`, which forgot the one thing worth keeping:
   * the desktop indexer re-types every document of an opened deal, so a cleared column is refilled
   * with the same guess on the next pass. The endpoint stores what was dismissed and refuses to
   * propose it again for that document.
   */
  public async dismissRobotGuessedType(
    applicationId: string,
    fileId: string
  ): Promise<{ dismissed: boolean; dismissals: entities.DismissedDocumentTypeGuessT[] }> {
    const response = await this.getCookieFilledRequest(
      this.api.post(`/document/application/${applicationId}/typing/dismiss`).send({ documentId: fileId })
    );
    const body = response.body as { dismissed: boolean; dismissals?: object[] };
    return {
      dismissed: body.dismissed,
      // Rebuilt rather than handed back raw: `at` crosses the wire as the string JSON makes of a
      // Date, and a caller putting that straight into an UploadedFile would be storing a string
      // under a field the entity declares as a Date.
      dismissals: plainToInstance(entities.DismissedDocumentTypeGuess, body.dismissals ?? []),
    };
  }

  public async addBrokerToApp({
    brokerId,
    applicationId,
  }: {
    brokerId: string;
    applicationId: string;
  }): Promise<void> {
    await this.api.post(`/application/${applicationId}/broker/${brokerId}/connect`);
  }

  public async removeBrokerFromApp({
    brokerId,
    applicationId,
  }: {
    brokerId: string;
    applicationId: string;
  }): Promise<{ newPrimaryBrokerId: string | null }> {
    const response = await this.api.delete(`/application/${applicationId}/broker/${brokerId}`);
    return response.body;
  }

  public async deleteInvite({ inviteId }: { inviteId: string }): Promise<void> {
    await this.api.delete(`/user/invite/${inviteId}`);
  }

  public async brokerToBrokerAppInvite({
    applicationId,
    emails,
  }: {
    emails: string[];
    applicationId: string;
  }): Promise<entities.Invite[]> {
    const response = await this.api.post(`/application/${applicationId}/broker/invite`).send({ emails });
    return plainToInstance(entities.Invite, response.body as object[]);
  }

  public async brokerToBrokerTeamInvite({ emails }: { emails: string[] }): Promise<entities.Invite[]> {
    const response = await this.api
      .post("/user/invite")
      .send({ emails, type: enums.InviteType.BROKER_TEAM_CONNECTION });
    return plainToInstance(entities.Invite, response.body as object[]);
  }

  public async setBrokerAsPrimaryForApp({
    brokerId,
    applicationId,
  }: {
    brokerId: string;
    applicationId: string;
  }): Promise<void> {
    await this.api.patch(`/application/${applicationId}/broker/${brokerId}/primary`);
  }

  public async getBrokerAccount(): Promise<Partial<entities.BrokerT> & { numberOfDealsLastYear: number }> {
    const response = await this.api.get("/broker");
    return response.body;
  }

  public async mergeBrokers({
    targetBrokerId,
    sourceBrokerIds,
    applicationId,
  }: {
    targetBrokerId: string;
    sourceBrokerIds: string[];
    applicationId: string;
  }): Promise<entities.BrokerT> {
    const response = await this.api
      .post("/broker/merge")
      .send({ targetBrokerId, sourceBrokerIds, applicationId });
    return response.body;
  }

  public async createBrokerLicense(
    brokerId: string,
    license: {
      type: enums.LicenseType;
      status: enums.LicenseStatus;
      regions: enums.Region[];
      licenseId: string;
      activeFrom?: Date | null;
      activeTo?: Date | null;
    }
  ): Promise<entities.License> {
    const response = await this.api.post(`/broker/${brokerId}/license`).send(license);
    return response.body;
  }

  public async updateBrokerLicense(
    brokerId: string,
    licenseId: string,
    updates: Partial<{
      type: enums.LicenseType;
      status: enums.LicenseStatus;
      regions: enums.Region[];
      licenseId: string;
      activeFrom: Date | null;
      activeTo: Date | null;
    }>
  ): Promise<entities.License> {
    const response = await this.api.patch(`/broker/${brokerId}/license/${licenseId}`).send(updates);
    return response.body;
  }

  public async deleteBrokerLicense(brokerId: string, licenseId: string): Promise<void> {
    await this.api.delete(`/broker/${brokerId}/license/${licenseId}`);
  }

  public async changeBrokerBrokerage(brokerId: string, brokerGroupId: string): Promise<void> {
    await this.api.post(`/broker/${brokerId}/brokerage`).send({ brokerGroupId });
  }

  public generateDocumentUrl({
    type,
    applicationId,
    additionalProps,
    signingAuthority,
  }: {
    type: parsers.docGen.DocGenDocumentType;
    applicationId: string;
    additionalProps?: Record<string, any>;
    signingAuthority?: parsers.docGen.DocGenArgs["signingAuthority"];
  }) {
    return {
      url: `${this._baseURL}/document/generate/${type}/preview?${formatters.querystring.stringify({
        applicationId,
        additionalProps,
        signingAuthority,
        token: this.token,
      })}`,
    };
  }

  /**
   * Fetch the rendered PDF bytes for a doc-gen preview (`POST /document/generate/:type/preview`).
   * Non-mutating. Uses `fetch` (not superagent) so the binary body works the same in Node/Bun and
   * the browser; reuses the client's installed `Authorization` header.
   */
  public async fetchDocumentPreviewPdf({
    type,
    applicationId,
    additionalProps,
    signingAuthority,
  }: {
    type: parsers.docGen.DocGenDocumentType;
    applicationId: string;
    additionalProps?: Record<string, any>;
    signingAuthority?: parsers.docGen.DocGenArgs["signingAuthority"];
  }): Promise<Uint8Array> {
    const res = await fetch(`${this._baseURL}/document/generate/${type}/preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...this.headers },
      body: JSON.stringify({ applicationId, additionalProps, signingAuthority }),
    });
    if (!res.ok) {
      throw new Error(`Document preview failed (${res.status}): ${await res.text().catch(() => "")}`);
    }
    return new Uint8Array(await res.arrayBuffer());
  }

  public async saveDocument({
    type,
    applicationId,
    additionalProps,
    signingAuthority,
  }: {
    type: parsers.docGen.DocGenDocumentType;
    applicationId: string;
    additionalProps?: Record<string, any>;
    signingAuthority?: { firstName: string; lastName: string; email: string; title?: string }[];
  }): Promise<{ message: string }> {
    const response = await this.api.post(`/document/generate/${type}/save`).send({
      applicationId,
      additionalProps,
      signingAuthority,
    });
    return response.body;
  }

  public async sendDocumentToBroker({
    type,
    applicationId,
    additionalProps,
    signingAuthority,
    customEmailText,
    customEmailSubject,
  }: {
    type: parsers.docGen.DocGenDocumentType;
    applicationId: string;
    additionalProps?: Record<string, any>;
    signingAuthority?: parsers.docGen.DocGenArgs["signingAuthority"];
    customEmailText?: string;
    customEmailSubject?: string;
  }): Promise<{ message: string }> {
    const response = await this.api.post(`/document/generate/${type}/send-pdf-to-broker`).send({
      applicationId,
      additionalProps,
      signingAuthority,
      customEmailText,
      customEmailSubject,
    });
    return response.body;
  }

  public async sendDocumentForReview({
    type,
    applicationId,
    additionalProps,
    signingAuthority,
  }: {
    type: parsers.docGen.DocGenDocumentType;
    applicationId: string;
    additionalProps?: Record<string, any>;
    signingAuthority?: parsers.docGen.DocGenArgs["signingAuthority"];
  }): Promise<{ message: string }> {
    const response = await this.api.post(`/document/generate/${type}/send-pdf-for-review`).send({
      applicationId,
      additionalProps,
      signingAuthority,
    });
    return response.body;
  }

  public async sendDocumentToBrokerage({
    type,
    applicationId,
    additionalProps,
    signingAuthority,
    customEmailText,
    customEmailSubject,
  }: {
    type: parsers.docGen.DocGenDocumentType;
    applicationId: string;
    additionalProps?: Record<string, any>;
    signingAuthority?: parsers.docGen.DocGenArgs["signingAuthority"];
    customEmailText?: string;
    customEmailSubject?: string;
  }): Promise<{ message: string }> {
    const response = await this.api.post(`/document/generate/${type}/send-pdf-to-brokerage`).send({
      applicationId,
      additionalProps,
      signingAuthority,
      customEmailText,
      customEmailSubject,
    });
    return response.body;
  }

  public async sendDocumentForSignature({
    type,
    applicationId,
    additionalProps,
    signingAuthority,
    customEmailText,
    customEmailSubject,
  }: {
    type: parsers.docGen.DocGenDocumentType;
    applicationId: string;
    additionalProps?: Record<string, any>;
    signingAuthority?: { firstName: string; lastName: string; email: string; title?: string }[];
    customEmailText?: string;
    customEmailSubject?: string;
  }): Promise<{ message: string }> {
    const response = await this.api.post(`/document/generate/${type}/send-signature`).send({
      applicationId,
      additionalProps,
      signingAuthority,
      customEmailText,
      customEmailSubject,
    });
    return response.body;
  }

  public async deleteIncome(applicationId: string, incomeId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/income/${incomeId}`);
  }

  public async createIncome(
    applicationId: string,
    income: Partial<entities.Income>
  ): Promise<entities.Income> {
    const { owners, ...rest } = income as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.post(`/application/${applicationId}/income`).send(body);
    return plainToInstance(entities.Income, response.body as object);
  }

  public async persistAssetDrawdownIncome(
    applicationId: string,
    input: {
      evaluationId: string;
      sourceIds: string[];
      assetDrawdownPrincipalPercent?: number;
    }
  ): Promise<{ createdIncomeIds: string[]; createdAssetIds: string[] }> {
    const response = await this.api.post(`/application/${applicationId}/income/asset-drawdown`).send(input);
    return response.body as { createdIncomeIds: string[]; createdAssetIds: string[] };
  }

  public async updateIncome(
    applicationId: string,
    incomeId: string,
    updates: Partial<entities.Income>
  ): Promise<entities.Income> {
    const { owners, ...rest } = updates as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.patch(`/application/${applicationId}/income/${incomeId}`).send(body);
    return plainToInstance(entities.Income, response.body as object);
  }

  public getIncomeExplanation = async (
    applicationId: string,
    incomeId: string
  ): Promise<IncomeExplanation> => {
    const response = await this.api.get(`/application/${applicationId}/income/${incomeId}/explanation`);
    return response.body;
  };

  public async getIncomeCalculations(applicationId: string): Promise<entities.IncomeCalculation[]> {
    const response = await this.api.get(`/application/${applicationId}/income/calculations`);
    return plainToInstance(entities.IncomeCalculation, response.body as object[]);
  }

  public async saveIncomeCalculations(
    applicationId: string,
    incomeId: string,
    body: {
      source: entities.IncomeCalculationOrigin;
      generatedAt: string;
      selectedType?: enums.IncomeCalculationType;
      calculations: {
        type: enums.IncomeCalculationType;
        annualAmountCents: number;
        calculation: string;
        confidence: number;
        isRecommended: boolean;
        sources: (
          | {
              kind: "document";
              documentId: string;
              observedAmountCents: number;
              observedFrequency: enums.Frequency;
              observedAmountKind: entities.IncomeCalculationAmountKind;
              observedPeriod?: string;
            }
          | {
              kind: "parsed_bank_statement";
              documentId: string;
            }
        )[];
      }[];
    }
  ): Promise<entities.IncomeCalculation[]> {
    const response = await this.api
      .put(`/application/${applicationId}/income/${incomeId}/calculations`)
      .send(body);
    return plainToInstance(entities.IncomeCalculation, response.body as object[]);
  }

  public async getWatchlistEvents(applicationId: string): Promise<entities.Event[]> {
    const response = await this.api.get(`/application/${applicationId}/event`);
    return Array.isArray(response.body) ? plainToInstance(entities.Event, response.body as object[]) : [];
  }

  public async createWatchlistEvent(
    applicationId: string,
    event: parsers.applicationEvents.WatchlistEventCreate
  ): Promise<{ id: string }> {
    const response = await this.api.post(`/application/${applicationId}/event`).send(event);
    return response.body;
  }

  public async updateWatchlistEvent(
    applicationId: string,
    eventId: string,
    updates: parsers.applicationEvents.WatchlistEventPatch
  ): Promise<entities.Event> {
    const response = await this.api.patch(`/application/${applicationId}/event/${eventId}`).send(updates);
    return plainToInstance(entities.Event, response.body as object);
  }

  public async deleteWatchlistEvent(applicationId: string, eventId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/event/${eventId}`);
  }

  public async deleteAsset(applicationId: string, assetId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/asset/${assetId}`);
  }

  public async createAsset(
    applicationId: string,
    asset: Partial<entities.FinancialAsset>
  ): Promise<entities.FinancialAsset> {
    const { owners, ...rest } = asset as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.post(`/application/${applicationId}/asset`).send(body);
    return plainToInstance(entities.FinancialAsset, response.body as object);
  }

  public async updateAsset(
    applicationId: string,
    assetId: string,
    updates: Partial<entities.FinancialAsset>
  ): Promise<entities.FinancialAsset> {
    const { owners, ...rest } = updates as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.patch(`/application/${applicationId}/asset/${assetId}`).send(body);
    return plainToInstance(entities.FinancialAsset, response.body as object);
  }

  public async createDownpaymentSource(
    applicationId: string,
    source: Partial<entities.DownpaymentSource>
  ): Promise<entities.DownpaymentSource> {
    const response = await this.api.post(`/application/${applicationId}/downpaymentSource`).send(source);
    return plainToInstance(entities.DownpaymentSource, response.body as object);
  }

  public async updateDownpaymentSource(
    applicationId: string,
    downpaymentSourceId: string,
    updates: Partial<entities.DownpaymentSource>
  ): Promise<entities.DownpaymentSource> {
    const response = await this.api
      .patch(`/application/${applicationId}/downpaymentSource/${downpaymentSourceId}`)
      .send(updates);
    return plainToInstance(entities.DownpaymentSource, response.body as object);
  }

  public async deleteDownpaymentSource(applicationId: string, downpaymentSourceId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/downpaymentSource/${downpaymentSourceId}`);
  }

  public async bulkDeleteDownpaymentSources(
    applicationId: string,
    downpaymentSourceIds: string[]
  ): Promise<void> {
    await this.api
      .post(`/application/${applicationId}/downpaymentSource/bulk-delete`)
      .send({ downpaymentSourceIds });
  }

  public async deleteDebt(applicationId: string, debtId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/debt/${debtId}`);
  }

  public async createDebt(applicationId: string, debt: Partial<entities.Debt>): Promise<entities.Debt> {
    const { owners, ...rest } = debt as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.post(`/application/${applicationId}/debt`).send(body);
    return plainToInstance(entities.Debt, response.body as object);
  }

  public async updateDebt(
    applicationId: string,
    debtId: string,
    updates: Partial<entities.Debt>
  ): Promise<entities.Debt> {
    const { owners, ...rest } = updates as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.patch(`/application/${applicationId}/debt/${debtId}`).send(body);
    return plainToInstance(entities.Debt, response.body as object);
  }

  public async createExpense(
    applicationId: string,
    expense: Partial<entities.Expense>
  ): Promise<entities.Expense> {
    const { owners, ...rest } = expense as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.post(`/application/${applicationId}/expense`).send(body);
    return plainToInstance(entities.Expense, response.body as object);
  }

  public async updateExpense(
    applicationId: string,
    expenseId: string,
    updates: Partial<entities.Expense>
  ): Promise<entities.Expense> {
    const { owners, ...rest } = updates as any;
    const body =
      owners !== undefined ? { ...rest, ownerIds: owners.map((o: any) => o.id).filter(Boolean) } : rest;
    const response = await this.api.patch(`/application/${applicationId}/expense/${expenseId}`).send(body);
    return plainToInstance(entities.Expense, response.body as object);
  }

  public async deleteExpense(applicationId: string, expenseId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/expense/${expenseId}`);
  }

  public async deleteProperty(applicationId: string, propertyId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/property/${propertyId}`);
  }

  public async createPropertyValuation(
    applicationId: string,
    propertyId: string,
    valuation: Partial<entities.PropertyValuation>
  ): Promise<entities.PropertyValuation> {
    const response = await this.api
      .post(`/application/${applicationId}/property/${propertyId}/valuation`)
      .send(valuation);
    return plainToInstance(entities.PropertyValuation, response.body as object);
  }

  public async updatePropertyValuation(
    applicationId: string,
    propertyId: string,
    valuationId: string,
    updates: Partial<entities.PropertyValuation>
  ): Promise<entities.PropertyValuation> {
    const response = await this.api
      .patch(`/application/${applicationId}/property/${propertyId}/valuation/${valuationId}`)
      .send(updates);
    return plainToInstance(entities.PropertyValuation, response.body as object);
  }

  public async deletePropertyValuation(
    applicationId: string,
    propertyId: string,
    valuationId: string
  ): Promise<void> {
    await this.api.delete(`/application/${applicationId}/property/${propertyId}/valuation/${valuationId}`);
  }

  public async updatePropertyListing(
    applicationId: string,
    propertyId: string,
    listingId: string,
    updates: Pick<entities.PropertyListing, "status" | "endDate">
  ): Promise<void> {
    await this.api
      .patch(`/application/${applicationId}/property/${propertyId}/listing/${listingId}`)
      .send(updates);
  }

  public async deletePropertyListing(
    applicationId: string,
    propertyId: string,
    listingId: string
  ): Promise<void> {
    await this.api.delete(`/application/${applicationId}/property/${propertyId}/listing/${listingId}`);
  }

  public async createApplicant(
    applicationId: string,
    applicant: Partial<entities.Applicant>
  ): Promise<entities.Applicant> {
    const response = await this.api.post(`/application/${applicationId}/applicant`).send(applicant);
    return plainToInstance(entities.Applicant, response.body as object);
  }

  public async updateApplicant(
    applicationId: string,
    applicantId: string,
    updates: Partial<entities.Applicant>
  ): Promise<entities.Applicant> {
    const response = await this.api
      .patch(`/application/${applicationId}/applicant/${applicantId}`)
      .send(updates);
    return plainToInstance(entities.Applicant, response.body as object);
  }

  public async deleteApplicant(applicationId: string, applicantId: string): Promise<void> {
    await this.api.delete(`/application/${applicationId}/applicant/${applicantId}`);
  }

  // Applicant Address (living history)
  public async createApplicantAddress(
    applicationId: string,
    applicantId: string,
    address: Partial<entities.ApplicantAddress>
  ): Promise<entities.ApplicantAddress> {
    const response = await this.api
      .post(`/application/${applicationId}/applicant/${applicantId}/address`)
      .send(address);
    return plainToInstance(entities.ApplicantAddress, response.body as object);
  }

  public async updateApplicantAddress(
    applicationId: string,
    applicantId: string,
    addressId: string,
    updates: Partial<entities.ApplicantAddress>
  ): Promise<entities.ApplicantAddress> {
    const response = await this.api
      .patch(`/application/${applicationId}/applicant/${applicantId}/address/${addressId}`)
      .send(updates);
    return plainToInstance(entities.ApplicantAddress, response.body as object);
  }

  public async deleteApplicantAddress(
    applicationId: string,
    applicantId: string,
    addressId: string
  ): Promise<void> {
    await this.api.delete(`/application/${applicationId}/applicant/${applicantId}/address/${addressId}`);
  }

  // Bulk operations for Debts
  public async bulkDeleteDebts(applicationId: string, debtIds: string[]): Promise<void> {
    await this.api.post(`/application/${applicationId}/debt/bulk-delete`).send({ debtIds });
  }

  public async bulkPatchDebts(
    applicationId: string,
    debtIds: string[],
    updates: Partial<entities.Debt>
  ): Promise<void> {
    await this.api.patch(`/application/${applicationId}/debt/bulk-patch`).send({ debtIds, updates });
  }

  public async mergeDebts(applicationId: string, debtIds: string[]): Promise<any> {
    const response = await this.api.post(`/application/${applicationId}/debt/merge`).send({ debtIds });
    return response.body;
  }

  // Bulk operations for Assets
  public async bulkDeleteAssets(applicationId: string, assetIds: string[]): Promise<void> {
    await this.api.post(`/application/${applicationId}/asset/bulk-delete`).send({ assetIds });
  }

  public async bulkPatchAssets(
    applicationId: string,
    assetIds: string[],
    updates: Partial<entities.FinancialAsset>
  ): Promise<void> {
    await this.api.patch(`/application/${applicationId}/asset/bulk-patch`).send({ assetIds, updates });
  }

  // Bulk operations for Incomes
  public async bulkDeleteIncomes(applicationId: string, incomeIds: string[]): Promise<void> {
    await this.api.post(`/application/${applicationId}/income/bulk-delete`).send({ incomeIds });
  }

  public async bulkPatchIncomes(
    applicationId: string,
    incomeIds: string[],
    updates: Partial<entities.Income>
  ): Promise<void> {
    await this.api.patch(`/application/${applicationId}/income/bulk-patch`).send({ incomeIds, updates });
  }

  // Bulk operations for Expenses
  public async bulkDeleteExpenses(applicationId: string, expenseIds: string[]): Promise<void> {
    await this.api.post(`/application/${applicationId}/expense/bulk-delete`).send({ expenseIds });
  }

  public async bulkPatchExpenses(
    applicationId: string,
    expenseIds: string[],
    updates: Partial<entities.Expense>
  ): Promise<void> {
    await this.api.patch(`/application/${applicationId}/expense/bulk-patch`).send({ expenseIds, updates });
  }

  public async mergeIncomes(applicationId: string, incomeIds: string[]): Promise<any> {
    const response = await this.api.post(`/application/${applicationId}/income/merge`).send({ incomeIds });
    return response.body;
  }

  public async mergeAssets(applicationId: string, assetIds: string[]): Promise<any> {
    const response = await this.api.post(`/application/${applicationId}/asset/merge`).send({ assetIds });
    return response.body;
  }

  public async mergeExpenses(applicationId: string, expenseIds: string[]): Promise<any> {
    const response = await this.api.post(`/application/${applicationId}/expense/merge`).send({ expenseIds });
    return response.body;
  }

  // Bulk operations for Applicants
  public async bulkDeleteApplicants(applicationId: string, applicantIds: string[]): Promise<void> {
    await this.api.post(`/application/${applicationId}/applicant/bulk-delete`).send({ applicantIds });
  }

  public async bulkPatchApplicants(
    applicationId: string,
    applicantIds: string[],
    updates: Partial<entities.Applicant>
  ): Promise<void> {
    await this.api
      .patch(`/application/${applicationId}/applicant/bulk-patch`)
      .send({ applicantIds, updates });
  }

  /**
   * Workflows (Admin)
   */
  public getWorkflows = async (): Promise<entities.Workflow[]> => {
    const response = await this.api.get("/admin/workflow");
    return plainToInstance(entities.Workflow, response.body as object[]);
  };

  public getWorkflow = async (id: string): Promise<entities.Workflow> => {
    const response = await this.api.get(`/admin/workflow/${id}`);
    return plainToInstance(entities.Workflow, response.body as object);
  };

  /**
   * Lenders (Admin)
   */
  public getLenders = async (): Promise<entities.Lender[]> => {
    const response = await this.api.get("/admin/lender");
    return plainToInstance(entities.Lender, response.body as object[]);
  };

  public updateLender = async (id: string, updates: Partial<entities.Lender>): Promise<entities.Lender> => {
    const response = await this.api.patch(`/admin/lender/${id}`).send(updates);
    return plainToInstance(entities.Lender, response.body as object);
  };

  public createLender = async (lender: Partial<entities.Lender>): Promise<entities.Lender> => {
    const response = await this.api.post("/admin/lender").send(lender);
    return plainToInstance(entities.Lender, response.body as object);
  };

  /**
   * Underwriting desks (Admin)
   */
  public getUnderwritingDesks = async (): Promise<entities.UnderwritingDesk[]> => {
    const response = await this.api.get("/admin/underwriting-desk");
    return plainToInstance(entities.UnderwritingDesk, response.body as object[]);
  };

  public createUnderwritingDesk = async (body: {
    name: string;
    tradeName?: string;
    brand?: enums.Brand | null;
    email?: string;
    phone?: string;
    acceptableDomains?: string[];
    enabledLenderIds?: string[];
  }): Promise<entities.UnderwritingDesk> => {
    const response = await this.api.post("/admin/underwriting-desk").send(body);
    return plainToInstance(entities.UnderwritingDesk, response.body as object);
  };

  public updateUnderwritingDesk = async (
    id: string,
    body: {
      name?: string;
      tradeName?: string | null;
      brand?: enums.Brand | null;
      email?: string | null;
      phone?: string | null;
      acceptableDomains?: string[] | null;
      enabledLenderIds?: string[];
    }
  ): Promise<entities.UnderwritingDesk> => {
    const response = await this.api.patch(`/admin/underwriting-desk/${id}`).send(body);
    return plainToInstance(entities.UnderwritingDesk, response.body as object);
  };

  public inviteUnderwritingDeskMember = async (
    deskId: string,
    body: {
      email: string;
      firstName?: string;
      lastName?: string;
      canBeAssignedToApplications?: boolean;
      shouldReceiveAppDocuments?: boolean;
      isDeskAdmin?: boolean;
    }
  ): Promise<entities.UnderwritingDeskMember> => {
    const response = await this.api.post(`/admin/underwriting-desk/${deskId}/members`).send(body);
    return plainToInstance(entities.UnderwritingDeskMember, response.body as object);
  };

  public updateUnderwritingDeskMember = async (
    deskId: string,
    memberId: string,
    body: {
      canBeAssignedToApplications?: boolean;
      shouldReceiveAppDocuments?: boolean;
      isDeskAdmin?: boolean;
    }
  ): Promise<entities.UnderwritingDeskMember> => {
    const response = await this.api
      .patch(`/admin/underwriting-desk/${deskId}/members/${memberId}`)
      .send(body);
    return plainToInstance(entities.UnderwritingDeskMember, response.body as object);
  };

  public removeUnderwritingDeskMember = async (deskId: string, memberId: string): Promise<void> => {
    await this.api.delete(`/admin/underwriting-desk/${deskId}/members/${memberId}`);
  };

  public assignUnderwritingDeskToBroker = async (
    brokerId: string,
    underwritingDeskId: string | null
  ): Promise<void> => {
    await this.api.patch(`/admin/underwriting-desk/assign/broker/${brokerId}`).send({ underwritingDeskId });
  };

  public assignUnderwritingDeskToBrokerGroup = async (
    groupId: string,
    underwritingDeskId: string | null
  ): Promise<void> => {
    await this.api
      .patch(`/admin/underwriting-desk/assign/broker-group/${groupId}`)
      .send({ underwritingDeskId });
  };

  public assignUnderwritingDeskToApplication = async (
    applicationId: string,
    underwritingDeskId: string | null
  ): Promise<void> => {
    await this.api
      .patch(`/admin/underwriting-desk/assign/application/${applicationId}`)
      .send({ underwritingDeskId });
  };

  /**
   * Memories (Admin)
   */
  public getMemories = async (filters?: {
    lenderId?: string;
    productId?: string;
    tag?: string;
    query?: string;
    globalOnly?: boolean;
    isPolicy?: boolean;
  }): Promise<entities.Memory[]> => {
    const response = await this.api.get("/admin/memory").query(filters || {});
    return plainToInstance(entities.Memory, response.body as object[]);
  };

  public getMemoryTags = async (): Promise<Array<{ tag: string; count: number }>> => {
    const response = await this.api.get("/admin/memory/tags");
    return response.body as Array<{ tag: string; count: number }>;
  };

  public createMemory = async (memory: Partial<entities.Memory>): Promise<entities.Memory> => {
    const response = await this.api.post("/admin/memory").send(memory);
    return plainToInstance(entities.Memory, response.body as object);
  };

  public updateMemory = async (id: string, updates: Partial<entities.Memory>): Promise<entities.Memory> => {
    const response = await this.api.patch(`/admin/memory/${id}`).send(updates);
    return plainToInstance(entities.Memory, response.body as object);
  };

  public deleteMemory = async (id: string): Promise<{ success: boolean }> => {
    const response = await this.api.delete(`/admin/memory/${id}`);
    return response.body as { success: boolean };
  };

  /**
   * My memories — personal memories owned by the authenticated employee.
   * Backed by /user/memory endpoints; userId is auto-scoped server-side.
   */
  public getMyMemories = async (): Promise<entities.Memory[]> => {
    const response = await this.api.get("/user/memory");
    return plainToInstance(entities.Memory, response.body as object[]);
  };

  public createMyMemory = async (memory: Partial<entities.Memory>): Promise<entities.Memory> => {
    const response = await this.api.post("/user/memory").send(memory);
    return plainToInstance(entities.Memory, response.body as object);
  };

  public updateMyMemory = async (id: string, updates: Partial<entities.Memory>): Promise<entities.Memory> => {
    const response = await this.api.patch(`/user/memory/${id}`).send(updates);
    return plainToInstance(entities.Memory, response.body as object);
  };

  public deleteMyMemory = async (id: string): Promise<{ success: boolean }> => {
    const response = await this.api.delete(`/user/memory/${id}`);
    return response.body as { success: boolean };
  };

  public preseedMyTone = async (
    limit?: number
  ): Promise<{ scannedEmailCount: number; insertedMemoryIds: string[]; replacedMemoryCount: number }> => {
    const response = await this.api.post("/user/memory/preseed-tone").send({ limit });
    return response.body as {
      scannedEmailCount: number;
      insertedMemoryIds: string[];
      replacedMemoryCount: number;
    };
  };

  /**
   * Products (Admin)
   */
  public getProducts = async (lenderId?: string): Promise<entities.Product[]> => {
    const response = await this.api.get("/admin/product").query({ lenderId });
    return plainToInstance(entities.Product, response.body as object[]);
  };

  public createProduct = async (product: Partial<entities.Product>): Promise<entities.Product> => {
    const response = await this.api.post("/admin/product").send(product);
    return plainToInstance(entities.Product, response.body as object);
  };

  public updateProduct = async (
    id: string,
    updates: Partial<entities.Product>
  ): Promise<entities.Product> => {
    const response = await this.api.patch(`/admin/product/${id}`).send(updates);
    return plainToInstance(entities.Product, response.body as object);
  };

  public deleteProduct = async (id: string): Promise<{ success: boolean }> => {
    const response = await this.api.delete(`/admin/product/${id}`);
    return response.body;
  };

  public bulkDeleteProducts = async (ids: string[]): Promise<{ success: boolean; count: number }> => {
    const response = await this.api.post("/admin/product/bulk/delete").send({ ids });
    return response.body;
  };

  public bulkPatchProducts = async (
    ids: string[],
    updates: Partial<entities.Product>
  ): Promise<{ success: boolean; count: number }> => {
    const response = await this.api.post("/admin/product/bulk/patch").send({ ids, updates });
    return response.body;
  };

  public duplicateProduct = async (id: string): Promise<entities.Product> => {
    const response = await this.api.post(`/admin/product/${id}/duplicate`);
    return plainToInstance(entities.Product, response.body as object);
  };

  /**
   * Kick off a brainmb catalog sync. Returns as soon as the job is queued (202) — the run itself
   * takes minutes and reports to the dev Slack channel, so there is nothing to await here.
   */
  public syncBrainmbProducts = async (): Promise<{ status: string }> => {
    const response = await this.api.post("/admin/product/sync-brainmb");
    return response.body;
  };

  /**
   * Rates (Admin)
   */
  public createRate = async (rate: parsers.rate.Rate): Promise<parsers.rate.Rate> => {
    const response = await this.api.post("/admin/rate").send(rate);
    return parsers.rate.parseRate(response.body);
  };

  public updateRate = async (id: string, updates: Partial<parsers.rate.Rate>): Promise<parsers.rate.Rate> => {
    const response = await this.api.patch(`/admin/rate/${id}`).send(updates);
    return parsers.rate.parseRate(response.body);
  };

  public deleteRate = async (id: string): Promise<{ success: boolean }> => {
    const response = await this.api.delete(`/admin/rate/${id}`);
    return response.body;
  };

  public bulkDeleteRates = async (ids: string[]): Promise<{ success: boolean; count: number }> => {
    const response = await this.api.post("/admin/rate/bulk/delete").send({ ids });
    return response.body;
  };

  public duplicateRate = async (id: string): Promise<parsers.rate.Rate> => {
    const response = await this.api.post(`/admin/rate/${id}/duplicate`);
    return parsers.rate.parseRate(response.body);
  };

  /**
   * Every version of a swap-linked rate, oldest first.
   *
   * A swap-linked rate is versioned on each price change rather than updated, so its history is a
   * chain of rows grouped by (productId, swapRateType, swapRateMonths). A rate that isn't
   * swap-linked returns just itself.
   */
  public getRateHistory = async (id: string): Promise<entities.Rate[]> => {
    const response = await this.api.get(`/admin/rate/${id}/history`);
    return plainToInstance(entities.Rate, response.body as object[]);
  };

  /**
   * Product Import (Admin)
   */
  public getProductImportUploadUrl = async (): Promise<{ fileURL: string; key: string; bucket: string }> => {
    const response = await this.api.get("/admin/product/import/upload-url");
    return response.body;
  };

  public parseProductImport = async (input: {
    inputType: "text" | "pdf";
    text?: string;
    pdfS3Key?: string;
    pdfS3Bucket?: string;
    additionalPrompt?: string;
  }): Promise<{ jobId: string }> => {
    const response = await this.api.post("/admin/product/import/parse").send(input);
    return response.body;
  };

  public getProductImportJob = async (jobId: string): Promise<any> => {
    const response = await this.api.get(`/admin/product/import/job/${jobId}`);
    return response.body;
  };

  public applyProductImport = async (changes: {
    creates: any[];
    updates: any[];
    deletes: string[];
  }): Promise<{ success: boolean; created: number; updated: number; deleted: number }> => {
    const response = await this.api.post("/admin/product/import/apply").send(changes);
    return response.body;
  };

  /**
   * Knowledge repository (Admin)
   */
  public getKnowledgeUploadUrl = async (): Promise<{ fileURL: string; key: string; bucket: string }> => {
    const response = await this.api.get("/admin/knowledge/upload-url");
    return response.body;
  };

  public ingestKnowledge = async (input: {
    source: "pdf" | "text";
    pdfS3Key?: string;
    pdfS3Bucket?: string;
    rawText?: string;
    title?: string;
    commentary?: string;
    lenderId?: string;
    productId?: string;
    tags?: string[];
    regions?: string[];
  }): Promise<{ jobId: string; documentId: string }> => {
    const response = await this.api.post("/admin/knowledge/ingest").send(input);
    return response.body;
  };

  public getKnowledgeJob = async (jobId: string): Promise<any> => {
    const response = await this.api.get(`/admin/knowledge/job/${jobId}`);
    return response.body;
  };

  public listKnowledgeDocuments = async (filters?: {
    lenderId?: string;
    productId?: string;
    sourceType?: string;
    status?: string;
    tags?: string[];
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ documents: any[]; total: number; limit: number; offset: number }> => {
    const response = await this.api.get("/admin/knowledge/documents").query({
      lenderId: filters?.lenderId,
      productId: filters?.productId,
      sourceType: filters?.sourceType,
      status: filters?.status,
      tags: filters?.tags?.join(","),
      search: filters?.search,
      limit: filters?.limit,
      offset: filters?.offset,
    });
    return response.body;
  };

  public getKnowledgeDocument = async (documentId: string): Promise<{ document: any; chunks: any[] }> => {
    const response = await this.api.get(`/admin/knowledge/document/${documentId}`);
    return response.body;
  };

  public searchKnowledge = async (
    q: string,
    opts?: {
      lenderId?: string;
      productId?: string;
      tags?: string[];
    }
  ): Promise<{ hits: any[] }> => {
    const response = await this.api.get("/admin/knowledge/search").query({
      q,
      lenderId: opts?.lenderId,
      productId: opts?.productId,
      tags: opts?.tags?.join(","),
    });
    return response.body;
  };

  public supersedeKnowledgeDocument = async (
    id: string,
    supersededByDocumentId: string
  ): Promise<{ success: boolean }> => {
    const response = await this.api
      .post(`/admin/knowledge/document/${id}/supersede`)
      .send({ supersededByDocumentId });
    return response.body;
  };

  public deleteKnowledgeDocument = async (id: string): Promise<{ success: boolean }> => {
    const response = await this.api.delete(`/admin/knowledge/document/${id}`);
    return response.body;
  };

  public getKnowledgeChunkSource = async (
    chunkId: string
  ): Promise<{
    chunkId: string;
    documentId: string;
    documentTitle: string;
    documentSourceType: string;
    pageNumber: number | null;
    headingPath: string[];
    chunkContent: string;
    pdfUrl: string | null;
    corrections: Array<{
      id: string;
      eid: string;
      content: string;
      reason: string | null;
      userId: string | null;
      createdBySource: string | null;
      createdAt: string;
      dismissedAt: string | null;
    }>;
  }> => {
    const response = await this.api.get(`/admin/knowledge/chunk/${chunkId}/source`);
    return response.body;
  };

  public listKnowledgeChunkCorrections = async (chunkId: string): Promise<{ corrections: any[] }> => {
    const response = await this.api.get(`/admin/knowledge/chunk/${chunkId}/corrections`);
    return response.body;
  };

  public addKnowledgeChunkCorrection = async (
    chunkId: string,
    body: { content: string; reason?: string }
  ): Promise<{ correction: any }> => {
    const response = await this.api.post(`/admin/knowledge/chunk/${chunkId}/corrections`).send(body);
    return response.body;
  };

  public dismissKnowledgeChunkCorrection = async (correctionId: string): Promise<{ success: boolean }> => {
    const response = await this.api.post(`/admin/knowledge/correction/${correctionId}/dismiss`).send({});
    return response.body;
  };

  public listKnowledgeConflicts = async (status?: string): Promise<{ conflicts: any[] }> => {
    const response = await this.api.get("/admin/knowledge/conflicts").query({ status });
    return response.body;
  };

  public resolveKnowledgeConflict = async (
    id: string,
    body: { authoritativeChunkId: string; supersedeLosingDocs?: boolean; notes?: string }
  ): Promise<{ success: boolean; supersededDocumentIds: string[] }> => {
    const response = await this.api.post(`/admin/knowledge/conflicts/${id}/resolve`).send(body);
    return response.body;
  };

  public dismissKnowledgeConflict = async (id: string, notes?: string): Promise<{ success: boolean }> => {
    const response = await this.api.post(`/admin/knowledge/conflicts/${id}/dismiss`).send({ notes });
    return response.body;
  };

  /**
   * `types` widens the search beyond CSDs (the default). Approved-appraiser service areas are
   * linked to CMA/CD rows, which a CSD-only search can't surface.
   */
  public searchLocalityPopulations = async (
    q: string,
    province?: string,
    types?: enums.LocalityPopulationType[]
  ): Promise<{
    results: Array<{
      id: number;
      name: string;
      province: string | null;
      population: number | null;
      type: enums.LocalityPopulationType;
    }>;
  }> => {
    const response = await this.api
      .get("/admin/locality-population/search")
      .query({ q, province, types: types?.join(",") });
    return response.body;
  };

  public listLenderLendingAreas = async (
    lenderId: enums.LenderName
  ): Promise<entities.LenderLendingAreaT[]> => {
    const response = await this.api.get("/admin/lender-lending-area").query({ lenderId });
    return plainToInstance(entities.LenderLendingArea, response.body as object[]);
  };

  public createLenderLendingArea = async (body: {
    lenderId: enums.LenderName;
    localityPopulationId: number;
    tier: enums.LendingAreaTier;
    maxLtv?: number | null;
    maxLoanAmount?: number | null;
    interestRatePremium?: number | null;
  }): Promise<entities.LenderLendingAreaT> => {
    const response = await this.api.post("/admin/lender-lending-area").send(body);
    return plainToInstance(entities.LenderLendingArea, response.body as object);
  };

  public updateLenderLendingArea = async (
    id: string,
    body: {
      tier?: enums.LendingAreaTier;
      maxLtv?: number | null;
      maxLoanAmount?: number | null;
      interestRatePremium?: number | null;
    }
  ): Promise<entities.LenderLendingAreaT> => {
    const response = await this.api.patch(`/admin/lender-lending-area/${id}`).send(body);
    return plainToInstance(entities.LenderLendingArea, response.body as object);
  };

  public deleteLenderLendingArea = async (id: string): Promise<void> => {
    await this.api.delete(`/admin/lender-lending-area/${id}`);
  };

  // ──────────────────────────────────────────────
  // Approved appraisers (admin)
  //
  // These return the endpoint's flat DTO rather than entity instances — `lenderIds` is
  // flattened and `geo` pre-nested — so no plainToInstance. Consumers type themselves off
  // `Awaited<ReturnType<typeof fraction.listApprovedAppraisers>>`.
  // ──────────────────────────────────────────────

  public listApprovedAppraisers = async (): Promise<AdminApprovedAppraiserFirm[]> => {
    const response = await this.api.get("/admin/lender-approved-appraiser");
    return response.body;
  };

  public createApprovedAppraiser = async (
    body: AdminApprovedAppraiserFirmBody
  ): Promise<AdminApprovedAppraiserFirm> => {
    const response = await this.api.post("/admin/lender-approved-appraiser").send(body);
    return response.body;
  };

  public updateApprovedAppraiser = async (
    id: string,
    body: AdminApprovedAppraiserFirmBody
  ): Promise<AdminApprovedAppraiserFirm> => {
    const response = await this.api.patch(`/admin/lender-approved-appraiser/${id}`).send(body);
    return response.body;
  };

  public deleteApprovedAppraiser = async (id: string): Promise<void> => {
    await this.api.delete(`/admin/lender-approved-appraiser/${id}`);
  };

  public bulkApproveApprovedAppraisers = async (
    ids: string[],
    lenderId: enums.LenderName
  ): Promise<{ success: boolean; count: number }> => {
    const response = await this.api
      .post("/admin/lender-approved-appraiser/bulk/approve")
      .send({ ids, lenderId });
    return response.body;
  };

  public bulkUnapproveApprovedAppraisers = async (
    ids: string[],
    lenderId: enums.LenderName
  ): Promise<{ success: boolean; count: number }> => {
    const response = await this.api
      .post("/admin/lender-approved-appraiser/bulk/unapprove")
      .send({ ids, lenderId });
    return response.body;
  };

  public bulkPatchApprovedAppraisers = async (
    ids: string[],
    updates: { blacklisted?: boolean | null; blacklistReason?: string | null }
  ): Promise<{ success: boolean; count: number }> => {
    const response = await this.api
      .post("/admin/lender-approved-appraiser/bulk/patch")
      .send({ ids, updates });
    return response.body;
  };

  public bulkDeleteApprovedAppraisers = async (
    ids: string[]
  ): Promise<{ success: boolean; count: number }> => {
    const response = await this.api.post("/admin/lender-approved-appraiser/bulk/delete").send({ ids });
    return response.body;
  };

  public createApprovedAppraiserOffice = async (
    body: AdminApprovedAppraiserOfficeBody & { firmId: string }
  ): Promise<AdminApprovedAppraiserOffice> => {
    const response = await this.api.post("/admin/lender-approved-appraiser/office").send(body);
    return response.body;
  };

  public updateApprovedAppraiserOffice = async (
    officeId: string,
    body: AdminApprovedAppraiserOfficeBody
  ): Promise<AdminApprovedAppraiserOffice> => {
    const response = await this.api.patch(`/admin/lender-approved-appraiser/office/${officeId}`).send(body);
    return response.body;
  };

  public deleteApprovedAppraiserOffice = async (officeId: string): Promise<void> => {
    await this.api.delete(`/admin/lender-approved-appraiser/office/${officeId}`);
  };

  public geocodeApprovedAppraiserOffice = async (officeId: string): Promise<AdminApprovedAppraiserOffice> => {
    const response = await this.api.post(`/admin/lender-approved-appraiser/office/${officeId}/geocode`);
    return response.body;
  };

  public listPostalScores = async (lenderId: enums.LenderName): Promise<entities.PostalScoreT[]> => {
    const response = await this.api.get("/admin/postal-score").query({ lenderId });
    return plainToInstance(entities.PostalScore, response.body as object[]);
  };

  public createPostalScore = async (body: {
    lenderId: enums.LenderName;
    code: string;
    score: number;
    name?: string | null;
    maxLtv?: number | null;
    maxLoanAmount?: number | null;
    interestRatePremium?: number | null;
  }): Promise<entities.PostalScoreT> => {
    const response = await this.api.post("/admin/postal-score").send(body);
    return plainToInstance(entities.PostalScore, response.body as object);
  };

  public updatePostalScore = async (
    id: string,
    body: {
      score?: number;
      name?: string | null;
      maxLtv?: number | null;
      maxLoanAmount?: number | null;
      interestRatePremium?: number | null;
    }
  ): Promise<entities.PostalScoreT> => {
    const response = await this.api.patch(`/admin/postal-score/${id}`).send(body);
    return plainToInstance(entities.PostalScore, response.body as object);
  };

  public deletePostalScore = async (id: string): Promise<void> => {
    await this.api.delete(`/admin/postal-score/${id}`);
  };

  public deleteWorkflow = async (id: string): Promise<void> => {
    const response = await this.api.delete(`/admin/workflow/${id}`);
    return response.body;
  };

  public toggleWorkflowActive = async (id: string): Promise<entities.Workflow> => {
    const response = await this.api.patch(`/admin/workflow/${id}/toggle-active`);
    return response.body;
  };

  public runWorkflowAsync = async (
    workflowId: string,
    body: {
      version?: string;
      trigger: types.workflow.WorkflowTriggerArguments;
    }
  ): Promise<{ runId: string }> => {
    const response = await this.api.post(`/admin/workflow/run/${workflowId}`).send(body);
    return response.body;
  };

  public createSerialWorkflow = async (
    body: workflow.SerialWorkflowCreateRequest
  ): Promise<workflow.SerialWorkflowRecord> => {
    const response = await this.api.post("/admin/workflow/serial").send(body);
    return response.body;
  };

  public updateSerialWorkflow = async (
    workflowId: string,
    body: workflow.SerialWorkflowUpdateRequest
  ): Promise<workflow.SerialWorkflowRecord> => {
    const response = await this.api.put(`/admin/workflow/serial/${workflowId}`).send(body);
    return response.body;
  };

  public getSerialWorkflow = async (
    workflowId: string,
    { versionId }: { versionId?: string } = {}
  ): Promise<workflow.SerialWorkflowRecord> => {
    const response = await this.api
      .get(`/admin/workflow/serial/${workflowId}`)
      .query(versionId ? { versionId } : {});
    return response.body;
  };

  public getWorkflowRun = async (runId: string): Promise<entities.WorkflowRun> => {
    const response = await this.api.get(`/admin/workflow/run/${runId}`);
    return plainToInstance(entities.WorkflowRun, response.body as object);
  };

  public getAvailableLlmTools = async (): Promise<Array<{ name: string; description: string }>> => {
    const response = await this.api.get(`/admin/workflow/available-tools`);
    return response.body;
  };

  public getWorkflowConfirmation = async (confirmationId: string): Promise<entities.WorkflowConfirmation> => {
    const response = await this.api.get(`/admin/workflow/confirmation/${confirmationId}`);
    return plainToInstance(entities.WorkflowConfirmation, response.body as object);
  };

  public listWorkflowConfirmations = async (params: {
    applicationId?: string;
    status?: "pending" | "resolved" | "expired" | "cancelled";
  }): Promise<entities.WorkflowConfirmation[]> => {
    const response = await this.api.get(`/admin/workflow/confirmation`).query(params);
    return (response.body as object[]).map((b) => plainToInstance(entities.WorkflowConfirmation, b));
  };

  public resolveWorkflowConfirmation = async (
    confirmationId: string,
    body: { selectedOptionId?: string; freeformText?: string; cancel?: boolean }
  ): Promise<{ alreadyResolved?: boolean; cancelled?: boolean; runId?: string }> => {
    const response = await this.api.post(`/admin/workflow/confirmation/${confirmationId}/resolve`).send(body);
    return response.body;
  };

  public startTestLlmAgent = async (input: {
    systemPrompt?: string;
    userPrompt?: string;
    toolNames?: string[];
    maxIterations?: number;
    model?: "opus" | "sonnet" | "haiku";
    applicationId?: string;
    branches?: Array<{ name: string; description: string }>;
  }): Promise<{ sessionId: string }> => {
    const response = await this.api.post(`/admin/workflow/test-llm-agent`).send(input);
    return response.body;
  };

  public getTestLlmAgentSession = async (
    sessionId: string
  ): Promise<
    | { status: "processing"; startedAt: number }
    | {
        status: "completed";
        startedAt: number;
        finishedAt: number;
        result: {
          text: string;
          toolCalls?: Array<{ name: string; input: unknown }>;
          branch?: string;
          branchReasoning?: string;
          __activate?: string[];
          error?: string;
        };
      }
    | { status: "failed"; startedAt: number; finishedAt: number; error: string }
  > => {
    const response = await this.api.get(`/admin/workflow/test-llm-agent/${sessionId}`);
    return response.body;
  };

  /**
   * Convenience: starts the async run and polls until it's done. Loops every
   * `pollIntervalMs` (default 1.5s) up to `timeoutMs` (default 10 minutes — the
   * lambda's own ceiling). Use the lower-level `start*` / `get*` pair when the
   * caller wants to render incremental progress.
   */
  public testLlmAgentNode = async (
    input: Parameters<FractionClient["startTestLlmAgent"]>[0],
    {
      pollIntervalMs = 1500,
      timeoutMs = 10 * 60 * 1000,
    }: { pollIntervalMs?: number; timeoutMs?: number } = {}
  ): Promise<{
    text: string;
    toolCalls?: Array<{ name: string; input: unknown }>;
    branch?: string;
    branchReasoning?: string;
    __activate?: string[];
    error?: string;
  }> => {
    const { sessionId } = await this.startTestLlmAgent(input);
    const startedAt = Date.now();
    while (true) {
      const session = await this.getTestLlmAgentSession(sessionId);
      if (session.status === "completed") {
        return session.result;
      }
      if (session.status === "failed") {
        return { text: "", error: session.error };
      }
      if (Date.now() - startedAt > timeoutMs) {
        return { text: "", error: `Test agent run timed out after ${timeoutMs}ms` };
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
  };

  private static scrubFrontendFields(value: unknown, cleanKeys: string[] = []): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => FractionClient.scrubFrontendFields(item, cleanKeys));
    }
    if (value !== null && typeof value === "object") {
      const result: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
        if (key.startsWith("__FRONTEND__")) {
          continue;
        }
        let shouldContinue = false;
        for (const cleanKey of cleanKeys) {
          if (key === cleanKey) {
            shouldContinue = true;
            break;
          }
        }
        if (shouldContinue) {
          continue;
        }
        result[key] = FractionClient.scrubFrontendFields(val, cleanKeys);
      }
      return result;
    }
    return value;
  }

  public saveWorkflow = async (body: {
    workflowId?: string;
    name: string;
    description?: string;
    productIds?: string[];
    version?: string;
    reactFlowJson: Record<string, unknown>;
    nodeBindings?: Record<string, unknown>;
    constants?: Record<string, unknown>;
    activate?: boolean;
  }): Promise<{
    workflow: entities.Workflow;
    version: entities.WorkflowVersion;
  }> => {
    const cleanedReactFlowJson = FractionClient.scrubFrontendFields(body.reactFlowJson, [
      "_output",
      "_status",
    ]) as Record<string, unknown>;

    const response = await this.api.post("/admin/workflow").send({
      ...body,
      reactFlowJson: cleanedReactFlowJson,
      version: body.version || "v1",
      nodeBindings: body.nodeBindings || {},
    });
    return {
      workflow: plainToInstance(entities.Workflow, response.body.workflow as object),
      version: plainToInstance(entities.WorkflowVersion, response.body.version as object),
    };
  };

  public setApplicationProduct = async (
    applicationId: string,
    { productId, rateId, evaluationId }: { productId: string; rateId?: string; evaluationId?: string }
  ): Promise<Partial<entities.ApplicationT>> => {
    const response = await this.api
      .post(`/application/${applicationId}/product`)
      .send({ productId, rateId, evaluationId });
    return plainToInstance(entities.Application, response.body as object);
  };

  /**
   * Re-price several applications onto named rates in one request.
   *
   * Always resolves — partial success is the normal outcome, reported per application, so
   * the caller can retry only what failed. Capped server-side at 25 applications.
   */
  public bulkUpdateApplicationRates = async (
    updates: Array<{ applicationId: string; productId: string; rateId: string }>
  ): Promise<{
    updated: number;
    outcomes: Array<{
      applicationId: string;
      status: "updated" | "failed" | "skipped";
      error?: string;
    }>;
  }> => {
    const response = await this.api.post("/application/bulk/update-rates").send({ updates });
    return response.body;
  };

  public getProductRateEvaluations = async (
    applicationId: string
  ): Promise<entities.ProductRateEvaluation[]> => {
    const response = await this.api.get(`/application/${applicationId}/product-rate-evaluations`);
    return plainToInstance(entities.ProductRateEvaluation, response.body as object[]);
  };

  public setProductRateEvaluationPricingOverride = async (
    applicationId: string,
    evaluationId: string,
    patch: {
      employeeRateOverride?: string | null;
      employeeLenderFeeBpsOverride?: number | null;
      employeeFractionFeeBpsOverride?: number | null;
      employeeBrokerFeeBpsOverride?: number | null;
      employeeLenderFeeCentsOverride?: string | null;
      employeeMaxLTVOverride?: string | null;
      rationale: string;
    }
  ): Promise<entities.ProductRateEvaluation> => {
    const response = await this.api
      .patch(`/application/${applicationId}/product-rate-evaluations/${evaluationId}/pricing-override`)
      .send(patch);
    return plainToInstance(entities.ProductRateEvaluation, response.body as object);
  };

  public getRecommendedAppraisers = async (
    applicationId: string
  ): Promise<{
    appraisers: Array<{
      firmId: string;
      companyName: string;
      website: string | null;
      score: number;
      lenderCount: number;
      officeId: string;
      phone: string | null;
      email: string | null;
      address: string | null;
      locality: string | null;
      geo: { lat: number; lng: number } | null;
      serviceAreaDistanceM: number | null;
      lenderNames?: string[];
    }>;
    /** The subject property, so the map can anchor appraisers to it. Null when ungeocoded. */
    property: { geo: { lat: number; lng: number }; address: string | null } | null;
  }> => {
    const response = await this.api.get(`/application/${applicationId}/recommended-appraisers`);
    return response.body as any;
  };

  /**
   * @param runAll - Evaluate every lender's products. Deals submitted through a broker's
   *   Velocity / Lendesk tool otherwise match against Fraction's own products only.
   */
  public refreshProductMatching = async (
    applicationId: string,
    { runAll, includeAssetIncome }: { runAll?: boolean; includeAssetIncome?: boolean } = {}
  ): Promise<{ jobId: string }> => {
    const response = await this.api
      .post(`/application/${applicationId}/refresh-product-matching`)
      .send({ runAll, includeAssetIncome });
    return response.body as { jobId: string };
  };

  public getProductMatchingRefreshJob = async (
    applicationId: string,
    jobId: string
  ): Promise<entities.AsyncProductMatchingRefreshJob> => {
    const response = await this.api.get(`/application/${applicationId}/refresh-product-matching/${jobId}`);
    return plainToInstance(entities.AsyncProductMatchingRefreshJob, response.body as object);
  };

  /**
   * An offer re-qualified against the file as it is now (nothing is persisted), with any unsaved
   * income-exclusion and debt-payoff (`targetEndBalance`) drafts applied.
   */
  public getLiveProductRateEvaluation = async (
    applicationId: string,
    evaluationId: string,
    {
      incomeExclusions,
      debtPayoffs,
    }: { incomeExclusions?: Record<string, boolean>; debtPayoffs?: Record<string, number | null> } = {}
  ): Promise<entities.ProductRateEvaluation> => {
    const response = await this.api
      .post(`/application/${applicationId}/product-rate-evaluations/${evaluationId}/live`)
      .send({ incomeExclusions, debtPayoffs });
    const { evaluation } = response.body as { evaluation: object };
    return plainToInstance(entities.ProductRateEvaluation, evaluation);
  };

  /** When the inputs product matching reads last changed; evaluations scored before it are stale. */
  public getProductMatchingFreshness = async (
    applicationId: string
  ): Promise<{ matchingInputsChangedAt: Date | null }> => {
    const response = await this.api.get(`/application/${applicationId}/product-matching-freshness`);
    const { matchingInputsChangedAt } = response.body as { matchingInputsChangedAt: string | null };
    return { matchingInputsChangedAt: matchingInputsChangedAt ? new Date(matchingInputsChangedAt) : null };
  };

  /**
   * Kick off an async enrichment job for the given properties. Returns the jobId;
   * caller should poll `getPropertyEnrichmentJob` and refetch the application
   * once the job flips to DONE/FAILED. The synchronous per-property endpoint was
   * removed because enrichment fans out to 3-4 external APIs and a bulk run
   * can exceed the 30s HTTP lambda cap.
   */
  public enrichProperties = async (propertyIds: string[]): Promise<{ jobId: string }> => {
    const response = await this.api.post(`/ops/property/enrich`).send({ propertyIds });
    return response.body as { jobId: string };
  };

  /**
   * Fetch one listing photo through the backend proxy.
   *
   * Returns a Blob rather than a URL because the request has to carry the
   * employee's bearer token — an `<img src>` can't. Callers turn it into an
   * object URL and are responsible for revoking it.
   */
  public getListingPhoto = async (url: string): Promise<Blob> => {
    const response = await this.api.get(`/ops/property/listing-photo`).query({ url }).responseType("blob");
    return response.body as Blob;
  };

  public getPropertyEnrichmentJob = async (
    jobId: string
  ): Promise<{
    id: string;
    status: enums.AsyncJobStatus;
    tasks: { id: string; status: enums.AsyncJobStatus }[];
  }> => {
    const response = await this.api.get(`/ops/property/enrichment-job/${jobId}`);
    return response.body as {
      id: string;
      status: enums.AsyncJobStatus;
      tasks: { id: string; status: enums.AsyncJobStatus }[];
    };
  };

  // Whitelabel API methods

  public createWhitelabelApplication = async (applicationId: string): Promise<entities.ApplicationT> => {
    const response = await this.api.post(`/application/${applicationId}/whitelabel`).send();
    return plainToInstance(entities.Application, response.body as object);
  };

  public validateWhitelabelApplication = async (
    applicationId: string
  ): Promise<{ isValid: boolean; validations: FinmoValidation[] }> => {
    const response = await this.api.get(`/application/${applicationId}/whitelabel/validate`);
    return response.body;
  };

  public submitWhitelabelApplication = async (
    applicationId: string,
    body: { notesForWhitelabelPartner?: string }
  ): Promise<entities.ApplicationT> => {
    const response = await this.api.post(`/application/${applicationId}/whitelabel/submit`).send(body);
    return plainToInstance(entities.Application, response.body as object);
  };

  public uploadWhitelabelDocuments = async (
    applicationId: string,
    documentIds: string[]
  ): Promise<{ message: string }> => {
    const response = await this.api
      .post(`/application/${applicationId}/whitelabel/document-upload`)
      .send({ documentIds });
    return response.body;
  };

  // Gmail API methods

  public sendGmailEmail = async (params: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    html: string;
    /** Optional alias / group address to send as (validated server-side). */
    from?: string;
    applicationId?: string;
    attachments?: Array<{ filename: string; mimeType: string; content: string }>;
    uploadedFileIds?: string[];
    s3Attachments?: Array<{ s3Key: string; bucket: string; filename: string }>;
    attachmentInspections?: parsers.email.AttachmentInspection[];
    docGen?: {
      type: parsers.docGen.DocGenDocumentType;
      additionalProps?: Record<string, any>;
      signingAuthority?: Array<{ firstName: string; lastName: string; email: string; title?: string }>;
    };
  }): Promise<{
    success: boolean;
    messageId: string;
    threadId: string;
    communicationId: string;
    communication: entities.Communication;
  }> => {
    const response = await this.api.post("/comms/gmail/send").send(params);
    return {
      ...response.body,
      communication: plainToInstance(entities.Communication, response.body.communication),
    };
  };

  public sendGmailReply = async (
    communicationId: string,
    params: {
      to: string[];
      cc?: string[];
      bcc?: string[];
      html: string;
      /** Optional alias / group address to send as (validated server-side). */
      from?: string;
      attachments?: Array<{ filename: string; mimeType: string; content: string }>;
      uploadedFileIds?: string[];
      s3Attachments?: Array<{ s3Key: string; bucket: string; filename: string }>;
      attachmentInspections?: parsers.email.AttachmentInspection[];
      docGen?: {
        type: parsers.docGen.DocGenDocumentType;
        additionalProps?: Record<string, any>;
        signingAuthority?: Array<{
          firstName: string;
          lastName: string;
          email: string;
          title?: string;
        }>;
      };
      applicationId?: string;
    }
  ): Promise<{
    success: boolean;
    messageId: string;
    threadId: string;
    communicationId: string;
    communication: entities.Communication;
  }> => {
    const response = await this.api.post(`/comms/gmail/reply/${communicationId}`).send(params);
    return {
      ...response.body,
      communication: plainToInstance(entities.Communication, response.body.communication),
    };
  };

  public getGmailRecipients = async (
    applicationId: string
  ): Promise<{
    recipients: Array<{
      email: string;
      name: string;
      role: string;
      userId?: string;
    }>;
  }> => {
    const response = await this.api.get(`/comms/gmail/recipients/${applicationId}`);
    return response.body;
  };

  public getGmailConnectionStatus = async (): Promise<{
    connected: boolean;
    email: string | null;
    connectedAt: string | null;
    syncEmail: boolean;
  }> => {
    const response = await this.api.get("/comms/gmail/connection-status");
    return response.body;
  };

  public getGmailSendAsAddresses = async (): Promise<{
    addresses: Array<{
      email: string;
      displayName?: string;
      isPrimary: boolean;
      isDefault: boolean;
    }>;
  }> => {
    const response = await this.api.get("/comms/gmail/send-as");
    return response.body;
  };

  public updateGmailSyncEmail = async (
    syncEmail: boolean
  ): Promise<{ success: boolean; syncEmail: boolean }> => {
    const response = await this.api.put("/comms/gmail/sync-email").send({ syncEmail });
    return response.body;
  };

  public disconnectGmail = async (): Promise<{ success: boolean }> => {
    const response = await this.api.delete("/comms/gmail/disconnect");
    return response.body;
  };

  public downloadGmailAttachment = async (messageId: string, attachmentId: string): Promise<Blob> => {
    const response = await this.api
      .get(`/comms/gmail/attachments/${messageId}/${attachmentId}`)
      .responseType("blob");
    return response.body;
  };

  public getGmailAttachments = async (
    applicationId: string
  ): Promise<{
    files: Array<{
      id: string;
      filename: string;
      mimeType: string;
      uploadedAt: string;
      documentType?: string;
    }>;
  }> => {
    const response = await this.api.get(`/comms/gmail/attachments/${applicationId}`);
    return response.body;
  };

  public createChecklistException = async (
    applicationId: string,
    exception: checklist.ChecklistExceptionTarget & {
      checker: string;
      checklist: SyntheticApplicationStatus | enums.LoanStatus | null;
      notes?: string;
    }
  ): Promise<ChecklistExceptionMutationResult> => {
    const response = await this.api.post(`/application/${applicationId}/checklist-exception`).send(exception);
    return {
      exception: plainToInstance(entities.ChecklistException, response.body.exception as object),
      checklists: response.body.checklists || {},
    };
  };

  public expandChecklistException = async (
    applicationId: string,
    exceptionId: string,
    notes: string
  ): Promise<ChecklistExceptionMutationResult> => {
    const response = await this.api
      .patch(`/application/${applicationId}/checklist-exception/${exceptionId}`)
      .send({ checklist: null, notes });
    return {
      exception: plainToInstance(entities.ChecklistException, response.body.exception as object),
      checklists: response.body.checklists || {},
    };
  };

  public deleteChecklistException = async (
    applicationId: string,
    exceptionId: string
  ): Promise<ChecklistExceptionChecklists> => {
    const response = await this.api.delete(
      `/application/${applicationId}/checklist-exception/${exceptionId}`
    );
    return response.body.checklists || {};
  };

  public getChecklistExceptions = async (applicationId: string): Promise<entities.ChecklistException[]> => {
    const response = await this.api.get(`/application/${applicationId}/checklist-exception`);
    return plainToInstance(entities.ChecklistException, response.body as object[]);
  };

  public updateApplicationTaskStatus = async (
    applicationId: string,
    taskType: string,
    status: enums.ApplicationTaskStatus,
    opts?: { applicantId?: string }
  ): Promise<any> => {
    const response = await this.api
      .patch(`/application/${applicationId}/task/${taskType}/status`)
      .send({ status, ...(opts?.applicantId ? { applicantId: opts.applicantId } : {}) });
    return response.body;
  };

  // ── User Notifications ──────────────────────────────────────────────

  public getNotifications = async ({
    limit,
    cursor,
  }: {
    limit?: number;
    cursor?: string;
  } = {}): Promise<{
    data: entities.UserNotification[];
    nextCursor: string | null;
  }> => {
    const query: Record<string, unknown> = {};
    if (limit) query.limit = limit;
    if (cursor) query.cursor = cursor;
    const response = await this.api.get("/user/notifications").query(query);
    return {
      data: plainToInstance(entities.UserNotification, response.body?.data),
      nextCursor: response.body?.nextCursor ?? null,
    };
  };

  public markNotificationsRead = async (ids: string[]): Promise<void> => {
    await this.api.patch("/user/notifications/read").send({ ids });
  };

  /** Cards shown in a visible, focused tab — suppresses the delayed fallback email without dismissing. */
  public markNotificationsSeen = async (ids: string[]): Promise<void> => {
    await this.api.patch("/user/notifications/seen").send({ ids });
  };

  public markAllNotificationsRead = async (): Promise<void> => {
    await this.api.patch("/user/notifications/read-all").send({});
  };

  public getQuickbooksAuthUrl = async (
    applicantId: string,
    applicationId?: string,
    returnTarget?: "web" | "desktop"
  ): Promise<{ url: string; state: string }> => {
    const response = await this.api
      .post("/quickbooks/auth/url")
      .send({ applicantId, applicationId, returnTarget });
    return response.body;
  };

  public listQuickbooksConnections = async (params: {
    applicationId?: string;
    applicantId?: string;
  }): Promise<{ connections: QuickbooksConnectionSummary[] }> => {
    const response = await this.api.get("/quickbooks/connections").query(params);
    return response.body;
  };

  public syncQuickbooksConnection = async (connectionId: string): Promise<{ jobId: string }> => {
    const response = await this.api.post(`/quickbooks/connections/${connectionId}/sync`).send({});
    return response.body;
  };

  public getQuickbooksSyncJob = async (
    jobId: string
  ): Promise<{
    id: string;
    status: enums.AsyncJobStatus;
    output?: { snapshotCount?: number; transactionCount?: number; incomeCount?: number; error?: string };
  }> => {
    const response = await this.api.get(`/quickbooks/connections/sync-job/${jobId}`);
    return response.body;
  };

  public getDebtReconciliationJob = async (
    jobId: string
  ): Promise<{
    id: string;
    status: enums.AsyncJobStatus;
    output?: { debtCount?: number; mergedCount?: number; transactionCount?: number; error?: string };
  }> => {
    const response = await this.api.get(`/bank/reconciliation-job/${jobId}`);
    return response.body;
  };

  public disconnectQuickbooks = async (connectionId: string): Promise<{ id: string }> => {
    const response = await this.api.delete(`/quickbooks/connections/${connectionId}`).send();
    return response.body;
  };

  public getStatementReview = async (
    applicationId: string,
    audience?: "applicant" | "broker"
  ): Promise<StatementReviewSummary> => {
    const req = this.api.get(`/statement-review/application/${applicationId}`);
    const response = audience ? await req.query({ audience }).send() : await req.send();
    return response.body;
  };

  /** Commit one reviewed Statement Review working copy. The endpoint applies
   * every operation in a single database transaction and rejects a stale base
   * revision before making any writes. */
  public applyStatementReviewDraft = async (
    applicationId: string,
    input: StatementReviewApplyInput
  ): Promise<StatementReviewApplyResult> => {
    const response = await this.api.post(`/statement-review/application/${applicationId}/apply`).send(input);
    return response.body;
  };

  public bulkPromoteClusterQuestions = async (input: {
    questionIds: string[];
    recipientRole: "broker" | "applicant";
    commentary?: string;
    subject?: string;
  }): Promise<{
    applied: boolean;
    proposedCommunicationId?: string;
    attachedQuestionIds: string[];
    skippedQuestionIds: string[];
  }> => {
    const response = await this.api.post(`/statement-review/bulk-promote`).send(input);
    return response.body;
  };

  public answerClusterQuestion = async (
    questionId: string,
    body: {
      choiceLabel?: string;
      freeText?: string;
      /** Underwriter/desk sets the income taxonomy directly on every member. */
      incomeCategory?: BankStatementIncomeCategory;
      /** Human label for the picked category, stored as the answer text. */
      categoryLabel?: string;
    }
  ): Promise<{ applied: boolean; membersAffected: number; freeTextJobId?: string }> => {
    const response = await this.api.post(`/statement-review/question/${questionId}/answer`).send(body);
    return response.body;
  };

  public promoteClusterQuestion = async (
    questionId: string,
    recipientRole: "broker" | "applicant"
  ): Promise<{ applied: boolean; proposedCommunicationId?: string }> => {
    const response = await this.api
      .post(`/statement-review/question/${questionId}/promote`)
      .send({ recipientRole });
    return response.body;
  };

  /** Employee-only undo for an answered cluster question (e.g. a Stage A LLM auto-answer). */
  public reopenClusterQuestion = async (
    questionId: string
  ): Promise<{ applied: boolean; membersReverted: number }> => {
    const response = await this.api.post(`/statement-review/question/${questionId}/reopen`).send({});
    return response.body;
  };

  public dismissClusterQuestion = async (questionId: string, reason?: string): Promise<{ id: string }> => {
    const response = await this.api.post(`/statement-review/question/${questionId}/dismiss`).send({ reason });
    return response.body;
  };

  /** Employee / desk: replace a pending question's prompt with a custom
   *  underwriter-authored ask (turns it free-text, clears the preset choices). */
  public updateClusterQuestion = async (
    questionId: string,
    body: { prompt: string }
  ): Promise<{ applied: boolean; questionId: string }> => {
    const response = await this.api.post(`/statement-review/question/${questionId}/edit`).send(body);
    return response.body;
  };

  /** Employee / desk: also ask the *other* recipient — forks a pending (unsent)
   *  sibling question on the same cluster addressed to `recipientRole`, returned
   *  so the caller can queue it into the batch send. */
  public forkClusterQuestion = async (
    questionId: string,
    recipientRole: "broker" | "applicant"
  ): Promise<{ applied: boolean; questionId?: string }> => {
    const response = await this.api
      .post(`/statement-review/question/${questionId}/fork`)
      .send({ recipientRole });
    return response.body;
  };

  public enrichStatementTransaction = async (
    bstId: string,
    patch: {
      category?: "business" | "personal" | "unknown";
      incomeCategory?: BankStatementIncomeCategory;
      appendTags?: string[];
      removeTags?: string[];
      note?: string;
      reason?: string;
    }
  ): Promise<{ id: string }> => {
    const response = await this.api.post(`/statement-review/transaction/${bstId}/enrich`).send(patch);
    return response.body;
  };

  /** Set the income-view classification for a single line (routes through enrich). */
  public setStatementIncomeCategory = async (
    bstId: string,
    incomeCategory: BankStatementIncomeCategory
  ): Promise<{ id: string }> => {
    const response = await this.api
      .post(`/statement-review/transaction/${bstId}/enrich`)
      .send({ incomeCategory });
    return response.body;
  };

  /** Connect a line to applicant(s) with fractional shares (even split via
   * `applicantIds`, explicit via `shares`). Replaces existing attribution. */
  public assignStatementApplicants = async (
    bstId: string,
    body: { applicantIds: string[] } | { shares: Array<{ applicantId: string; fraction: number }> }
  ): Promise<{ id: string; attribution: Array<{ applicantId: string; fraction: number }> }> => {
    const response = await this.api.post(`/statement-review/transaction/${bstId}/attribution`).send(body);
    return response.body;
  };

  /** Carve part of a line into a new synthetic child transaction. */
  public splitOutStatementTransaction = async (
    bstId: string,
    body: {
      amountCents: number;
      incomeCategory: BankStatementIncomeCategory;
      applicantIds?: string[];
      note?: string;
    }
  ): Promise<{ id: string; syntheticId: string }> => {
    const response = await this.api.post(`/statement-review/transaction/${bstId}/split-out`).send(body);
    return response.body;
  };

  /** Persist the business + each applicant's equity stake for a saved income. */
  public saveOwnedBusiness = async (body: {
    applicationId: string;
    incomeId: string;
    ownedBusinessId?: string;
    name?: string;
    owners: Array<{ applicantId: string; equity: number }>;
  }): Promise<{ id: string }> => {
    const response = await this.api.post(`/statement-review/owned-business`).send(body);
    return response.body;
  };

  /** "Batch into a group" — create a manual cluster from selected lines, with
   * an optional question to resolve in-app or promote. */
  public createManualCluster = async (input: {
    applicationId: string;
    transactionIds: string[];
    name: string;
    rationale?: string;
    question?: {
      prompt: string;
      choices?: StatementReviewClusterQuestion["choices"];
      audience?: "underwriter" | "broker" | "applicant" | "any";
      allowFreeText?: boolean;
    };
  }): Promise<{
    applied: boolean;
    clusterId?: string;
    questionId?: string;
    memberCount: number;
  }> => {
    const response = await this.api.post(`/statement-review/cluster`).send(input);
    return response.body;
  };

  /** Applicant / broker re-sorts a wrongly grouped cluster into their own
   * labelled sub-buckets, each with optional notes. */
  public splitClusterByApplicant = async (
    questionId: string,
    body: { subgroups: Array<{ label: string; transactionIds: string[]; comment?: string }> }
  ): Promise<{ applied: boolean; newClusterIds: string[]; movedCount: number }> => {
    const response = await this.api.post(`/statement-review/question/${questionId}/split`).send(body);
    return response.body;
  };

  /** Drop a group; its lines fall back to the unexplained pile. */
  public dismissCluster = async (clusterId: string): Promise<{ id: string }> => {
    const response = await this.api.post(`/statement-review/cluster/${clusterId}/dismiss`).send({});
    return response.body;
  };

  /** Employee-only: kick off (or join) an async auto-classification run over
   * the application's bank lines. Poll via {@link getStatementClassifyJob}. */
  public autoClassifyStatement = async (applicationId: string): Promise<{ jobId: string }> => {
    const response = await this.api
      .post(`/statement-review/application/${applicationId}/auto-classify`)
      .send({});
    return response.body;
  };

  public getStatementClassifyJob = async (
    jobId: string
  ): Promise<{
    id: string;
    status: string;
    output?: { incomeClassified?: number; attributed?: number; error?: string } | null;
  }> => {
    const response = await this.api.get(`/statement-review/classify-job/${jobId}`).send();
    return response.body;
  };

  public submitToConveyancer = async ({
    emailSubject,
    emailBody,
    applicationId,
    deliveryMethod,
  }: {
    emailSubject?: string;
    emailBody?: string;
    applicationId: string;
    deliveryMethod?: "auto" | "email" | "portal";
  }) => {
    const response = await this.api
      .post(`/application/${applicationId}/submit-to-conveyancer`)
      .timeout(30000)
      .send({ emailSubject, emailBody, deliveryMethod });
    return response.body;
  };
}

/**
 * Approved-appraiser admin DTOs, mirroring what
 * `backend/src/lambda/http/lenderApprovedAppraiser.ts` returns. Not entity shapes: `lenderIds`
 * is flattened and `geo` is pre-nested so the page can filter and render without hydrating
 * `Lender` rows.
 */
export interface AdminApprovedAppraiserServiceArea {
  id: number;
  name: string;
  province: string | null;
  population: number | null;
  type: enums.LocalityPopulationType;
}

export interface AdminApprovedAppraiserOffice {
  id: string;
  firmId: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  locality: string | null;
  geo: { lat: number; lng: number } | null;
  serviceAreas: AdminApprovedAppraiserServiceArea[];
}

export interface AdminApprovedAppraiserFirm {
  id: string;
  companyName: string;
  website: string | null;
  provinces: enums.Region[] | null;
  blacklisted: boolean | null;
  blacklistReason: string | null;
  createdAt: string;
  updatedAt: string;
  lenderIds: enums.LenderName[];
  offices: AdminApprovedAppraiserOffice[];
}

export interface AdminApprovedAppraiserFirmBody {
  companyName?: string;
  website?: string | null;
  blacklisted?: boolean | null;
  blacklistReason?: string | null;
  /** Replaces the firm's approvals wholesale. Omit to leave them alone; `[]` removes all. */
  lenderIds?: enums.LenderName[];
}

export interface AdminApprovedAppraiserOfficeBody {
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  locality?: string | null;
  geo?: { lat: number; lng: number } | null;
  /** Replaces the office's service-area links wholesale. Omit to leave alone; `[]` removes all. */
  serviceAreaIds?: number[];
}

export interface QuickbooksConnectionSummary {
  id: string;
  applicantId: string;
  applicationId?: string | null;
  realmId: string;
  companyName?: string | null;
  companyLegalName?: string | null;
  environment: "sandbox" | "production";
  connectedAt: string;
  lastSyncedAt?: string | null;
  disconnectedAt?: string | null;
  lastSyncError?: string | null;
  /** Id of an in-progress sync job for this connection, if one is running. */
  activeSyncJobId?: string | null;
}

// ---- Income explanation ("how was this income determined") -----------------
// Mirrors the backend payload from buildIncomeExplanation / explainQuickbooksAnnualIncome.

export interface QuickbooksIncomeMonthlyRow {
  periodStart: string;
  periodEnd?: string;
  netIncomeCents: number;
  ownerDrawsCents: number;
  shareholderDistributionsCents: number;
  totalRevenueCents?: number;
  totalCogsCents?: number;
  grossProfitCents?: number;
  totalOperatingExpensesCents?: number;
}

export interface QuickbooksIncomeYearAggregate {
  year: number;
  months: number;
  netIncomeCents: number;
  ownerCompCents: number;
  isComplete: boolean;
}

export interface QuickbooksIncomeDerivation {
  annualIncomeCents: number;
  basis: "net_income" | "owner_compensation";
  year: number;
  fromCompleteYear: boolean;
  scale: number;
  annualNetCents: number;
  annualOwnerCompCents: number;
  byYear: QuickbooksIncomeYearAggregate[];
  selectedYearMonths: QuickbooksIncomeMonthlyRow[];
  reason: string;
}

export interface StatementIncomeDerivation {
  verifiedCount: number;
  totalAmountVerifiedCents: number;
  verified: boolean;
  notes: string[];
  estimatedMonthlyIncomeCents?: number;
}

export interface PlaidIncomeDerivation {
  monthlyAmountCents?: number;
  description?: string;
  lookbackDays: number;
}

export interface IncomeExplanation {
  source: "quickbooks" | "plaid" | "statement" | "manual";
  currentAmountCents?: number;
  currentFrequency?: string;
  derivedAnnualCents?: number;
  valueChanged: boolean;
  explanationNote?: string;
  companyName?: string;
  quickbooks?: QuickbooksIncomeDerivation;
  statement?: StatementIncomeDerivation;
  plaid?: PlaidIncomeDerivation;
}

/**
 * Income-classification taxonomy for the Reconcile income view. Orthogonal to
 * the business/personal `category`; `null` = uncategorized. Mirrors
 * BankStatementIncomeCategory on the backend entity.
 */
export type BankStatementIncomeCategory =
  | "revenue"
  | "business_expense"
  | "staff_payroll"
  | "owner_salary"
  | "personal_by_biz"
  | "personal_income"
  | "personal_expense"
  | "internal_transfer";

/**
 * Where a transaction was read from, precisely enough for a reviewer to go and look.
 *
 * `documentId` alone says which of nine PDFs a number came out of, which is not a claim anyone can
 * check. This is the page it was printed on and the rectangle it occupied, so the line can be shown
 * as the bank printed it rather than described.
 *
 * Absent on Plaid lines, on manual entries, and on rows recovered through the flattened-text
 * fallback — that path has no coordinates to offer, and page-level is all it can honestly claim.
 */
export interface StatementLineProvenance {
  /** 0-based, as the analyzer emits. Add one before handing it to a PDF viewer. */
  pageNumber: number;
  /** Normalized to the page, y growing DOWN — the same convention the native renderer crops by. */
  box?: { x: number; y: number; width: number; height: number };
  /** How the page was read, so a reviewer knows how much to trust it without opening it. */
  extraction: "text" | "ocr";
  /** Set where a damaged cell was recognized again close up and the arithmetic accepted the result. */
  repaired?: boolean;
  /** Which generation of the reader produced this, so a re-run can tell stale rows from current ones. */
  readerVersion: string;
}

export interface StatementReviewLine {
  id: string;
  source: "pdf" | "plaid";
  /** Uploaded statement backing a PDF line; absent for Plaid/manual lines. */
  documentId?: string | null;
  /** Page and region this line was read from. See {@link StatementLineProvenance}. */
  provenance?: StatementLineProvenance | null;
  txnDate: string;
  amountCents: number;
  currency: string | null;
  description: string | null;
  category: "business" | "personal" | "unknown" | null;
  categorySource: "rule" | "llm" | "manual" | null;
  categoryConfidence: number | null;
  tags: string[] | null;
  notes: string | null;
  /** Income-view classification (see {@link BankStatementIncomeCategory}). */
  incomeCategory?: BankStatementIncomeCategory | null;
  /** Provenance of `incomeCategory` — 'rule'/'llm' rows are auto-classified and
   * overridable; 'manual' rows are never touched by the auto passes. */
  incomeCategorySource?: "rule" | "llm" | "manual" | null;
  incomeCategoryConfidence?: number | null;
  incomeCategoryReason?: string | null;
  /** Running account balance after this txn, in cents. Null when the parser
   * couldn't extract a balance (or for synthetic split children). */
  runningBalanceCents?: number | null;
  /** Carved-out child of a split. */
  synthetic?: boolean;
  parentId?: string | null;
  /** Per-applicant attribution shares; present on `incomeLines`. */
  attribution?: Array<{ applicantId: string; fraction: number; source?: "manual" | "auto" }> | null;
}

export interface StatementReviewClusterMember {
  id: string;
  status: "active" | "resolved" | "dismissed";
  bankStatementTransaction?: {
    id: string;
    txnDate: string;
    amountCents: number;
    description?: string | null;
    category?: "business" | "personal" | "unknown" | null;
    categoryReason?: string;
    tags?: string[] | null;
    notes?: string | null;
    source: "pdf" | "plaid";
    incomeCategory?: BankStatementIncomeCategory | null;
    synthetic?: boolean;
    parentId?: string | null;
  };
}

/**
 * Recipient-visible shape of a generic application question (a
 * ProposedCommunication with kind="question") as served to brokers/applicants.
 * Internal fields (reasoning, confidence, deduplicationKey) are stripped
 * server-side and never reach these surfaces.
 */
export interface ApplicationQuestion {
  id: string;
  parentId?: string | null;
  batchId?: string | null;
  questionText?: string;
  context?: string | null;
  status?: string;
  recipientRole: "broker" | "applicant" | "other";
  sentAt?: string | Date | null;
  respondedAt?: string | Date | null;
  responseText?: string | null;
  createdAt?: string | Date;
  createdById?: string | null;
}

/**
 * An open bank-statement question (a TransactionClusterQuestion raised against
 * a transaction cluster on the Statement Review tab). Served alongside the
 * generic questions so one request covers everything outstanding on a file.
 * Read-only on the generic-questions surface — these are asked from Statement
 * Review, which promotes a whole cluster batch into one email.
 */
export interface OpenStatementQuestion {
  id: string;
  clusterId: string;
  clusterName: string;
  prompt: string;
  audience: "underwriter" | "broker" | "applicant" | "any";
  status: "pending" | "interpreting";
  /** Set once promoted to an email — i.e. asked and awaiting a reply. */
  proposedCommunicationId: string | null;
  sentAt: string | Date | null;
  /** Transactions in the owning cluster (dismissed members excluded). */
  txnCount: number;
  totalCents: number;
  createdAt: string | Date;
}

export interface ApplicationQuestionsResult {
  /** Employees: full ProposedCommunication entities. Brokers/applicants: {@link ApplicationQuestion}. */
  questions: (entities.ProposedCommunication | ApplicationQuestion)[];
  /** Employee-only: still-open bank-statement questions. */
  statementQuestions?: OpenStatementQuestion[];
}

export interface StatementReviewClusterQuestion {
  id: string;
  prompt: string;
  choices?: Array<{
    label: string;
    effect: {
      category?: "business" | "personal" | "unknown";
      /** Income-taxonomy classification applied when this choice is picked. */
      incomeCategory?: BankStatementIncomeCategory;
      addTags?: string[];
      noteTemplate?: string;
      /** Applicant ids this choice attributes the cluster's txns to (even split). */
      assignApplicantIds?: string[];
    };
  }>;
  allowFreeText: boolean;
  audience: "underwriter" | "broker" | "applicant" | "any";
  status: "pending" | "answered" | "dismissed" | "stale";
  answer?: string | null;
  /** Why the answer was chosen (LLM evidence). Employee view only; null elsewhere. */
  answerReason?: string | null;
  answeredBy?: "underwriter" | "broker" | "applicant" | "llm" | null;
  /** Display name of the answering user. Employee view only; null elsewhere. */
  answeredByName?: string | null;
  answeredAt?: string | null;
  proposedCommunicationId?: string | null;
}

export interface StatementReviewCluster {
  id: string;
  name: string;
  rationale: string | null;
  source: "rule" | "llm" | "manual";
  status: "active" | "resolved" | "dismissed";
  confidence: number | null;
  /**
   * True when the cluster's total is over the auto-answer materiality cap —
   * Stage A deliberately left its question for a human (policy hold, distinct
   * from model confidence).
   */
  heldForReview?: boolean;
  members: StatementReviewClusterMember[];
  questions: StatementReviewClusterQuestion[];
}

export interface StatementReviewSummary {
  /** Opaque optimistic-concurrency token covering the persisted review graph. */
  reviewRevision?: string;
  summary: {
    total: number;
    categorized: number;
    clustered: number;
    pendingQuestions: number;
    /** Money-ribbon aggregates — absolute magnitudes, in cents. Zero for the
     * applicant/broker audience views (they don't render the ribbon). */
    totalCents: number;
    explainedCents: number;
    awaitingCents: number;
    unexplainedCents: number;
    inflowTotalCents: number;
    inflowExplainedCents: number;
    outflowTotalCents: number;
    outflowExplainedCents: number;
  };
  clusters: StatementReviewCluster[];
  unclusteredLines: StatementReviewLine[];
  /** Every operating line (clustered or not) with attribution, for the income
   * view + categorize workbench. Empty on the applicant/broker audience views. */
  incomeLines: StatementReviewLine[];
  /** Persisted per-applicant equity stakes for this application's businesses;
   * applicants without a stake fall back to an even split client-side. */
  applicants: Array<{ id: string; equity: number }>;
  /** In-flight auto-classify job, so a page refresh mid-run resumes the
   * polling/spinner UI. Null when idle (and on the audience views). */
  classifyJob?: { id: string; status: string } | null;
}

export type StatementReviewQuestionInput = {
  prompt: string;
  choices?: StatementReviewClusterQuestion["choices"];
  audience?: "underwriter" | "broker" | "applicant" | "any";
  allowFreeText?: boolean;
};

/** Serializable edit language shared by employee edits and local-agent
 * proposals. Client references use the `draft:` prefix and are mapped to
 * persisted UUIDs by Apply. */
export type StatementReviewOperation =
  | {
      type: "set_income_category";
      lineIds: string[];
      incomeCategory: BankStatementIncomeCategory;
    }
  | { type: "assign_applicants"; lineIds: string[]; applicantIds: string[] }
  | {
      type: "split_out";
      lineId: string;
      clientLineId: string;
      amountCents: number;
      incomeCategory: BankStatementIncomeCategory;
      applicantIds?: string[];
      note?: string;
    }
  | {
      type: "create_cluster";
      clientClusterId: string;
      clientQuestionId?: string;
      transactionIds: string[];
      name: string;
      rationale?: string;
      question?: StatementReviewQuestionInput;
    }
  | { type: "dismiss_cluster"; clusterId: string }
  | {
      type: "answer_question";
      questionId: string;
      choiceLabel?: string;
      freeText?: string;
      incomeCategory?: BankStatementIncomeCategory;
      categoryLabel?: string;
    }
  | {
      type: "split_cluster";
      questionId: string;
      subgroups: Array<{
        clientClusterId: string;
        label: string;
        transactionIds: string[];
        comment?: string;
      }>;
    }
  | { type: "reopen_question"; questionId: string }
  | { type: "edit_question"; questionId: string; prompt: string }
  | {
      type: "fork_question";
      sourceQuestionId: string;
      clientQuestionId: string;
      recipientRole: "broker" | "applicant";
    };

export type StatementReviewAgentStatement = {
  documentId: string;
  accounts?: Array<{ accountNumber: string; name: string }>;
  applicantIds?: string[];
  monthlyDetails?: Array<{ totalAdded: number; totalDeducted: number; month: string }>;
  estimatedMonthlyIncome?: number;
  transactions: Array<{
    clientLineId: string;
    date: string;
    /** Signed dollars, matching the canonical statement parser contract. */
    amount: number;
    description?: string | null;
    counterparty?: string | null;
    runningBalance?: number | null;
    clusterKey?: string | null;
    category?: "business" | "personal" | "unknown";
    incomeCategory?: BankStatementIncomeCategory;
    applicantIds?: string[];
    tags?: string[];
    note?: string;
    /** Page and region this line was read from. See {@link StatementLineProvenance}. */
    provenance?: StatementLineProvenance;
  }>;
};

export type StatementReviewIncomeCalculationDraft = {
  incomeId: string;
  source: "local_agent" | "manual";
  generatedAt?: string;
  selectedType?: string;
  calculations: Array<{
    type: string;
    annualAmountCents: number;
    calculation?: string;
    confidence?: number;
    isRecommended?: boolean;
    sources: Array<{
      kind: "document" | "parsed_bank_statement";
      documentId: string;
      observedAmountCents?: number;
      observedFrequency?: string;
      observedAmountKind?: string;
      observedPeriod?: string;
    }>;
  }>;
};

export interface StatementReviewApplyInput {
  expectedRevision: string;
  origin: "manual" | "local_agent";
  force?: boolean;
  operations: StatementReviewOperation[];
  sendIntents?: Record<string, "applicant" | "broker">;
  /** Document-scoped local ledger replacements, either saved immediately or applied with a proposal. */
  statements?: StatementReviewAgentStatement[];
  incomeCalculations?: StatementReviewIncomeCalculationDraft[];
}

export interface StatementReviewApplyResult {
  applied: true;
  reviewRevision: string;
  idMap: Record<string, string>;
}

export interface InitiateIDVerificationArgs {
  applicantId: string;
}

export interface AnswerIDVerificationArgs {
  applicantId: string;
  answers: types.KiqAnswer[];
  attemptId: string;
}

export interface GroupingLtvOverTimeData {
  administrativeArea: parsers.portfolio.LoanToValueOverTimeData[];
  fsa: parsers.portfolio.LoanToValueOverTimeData[];
  creditScore: parsers.portfolio.LoanToValueOverTimeData[];
  yearCohort: parsers.portfolio.LoanToValueOverTimeData[];
  ungrouped: parsers.portfolio.LoanToValueOverTimeData[];
}

export interface GroupingPropertyValueOverTimeData {
  administrativeArea: parsers.portfolio.PropertyValueOverTimeData[];
  fsa: parsers.portfolio.PropertyValueOverTimeData[];
  creditScore: parsers.portfolio.PropertyValueOverTimeData[];
  yearCohort: parsers.portfolio.PropertyValueOverTimeData[];
  ungrouped: parsers.portfolio.PropertyValueOverTimeData[];
}

export interface PortfolioFilters {
  status?: enums.LoanStatus;
  portfolios?: enums.Portfolio[];
  startDate?: Date;
  endDate?: Date;
  startLoanActivityDate?: Date;
  endLoanActivityDate?: Date;
  country?: enums.Country;
  productType?: enums.ProductType;
  termLength?: number;
  occupancy?: "primary" | "nonprimary";
  administrativeArea?: string[];
}

const constructPortfolioFilter = ({
  status,
  portfolios,
  startDate,
  endDate,
  startLoanActivityDate,
  endLoanActivityDate,
  country,
  productType,
  termLength,
  occupancy,
  administrativeArea,
}: PortfolioFilters) => ({
  portfolios: portfolios?.join(","),
  startDate: startDate?.toISOString(),
  endDate: endDate?.toISOString(),
  startLoanActivityDate: startLoanActivityDate?.toISOString(),
  endLoanActivityDate: endLoanActivityDate?.toISOString(),
  country,
  productType,
  termLength,
  status,
  occupancy,
  administrativeArea: administrativeArea?.join(","),
});

const sortChecklistItems = (
  items: types.ChecklistResult[],
  context: { apiBaseUrl?: string; token?: string }
) => {
  const itemsWithUrls = attachChecklistDocumentUrls(items, context);
  const sorted = _.sortBy(itemsWithUrls, (item) => {
    if (types.isDocumentChecklistItem(item)) {
      return new Date(
        _.maxBy(item.documents, (x) => new Date(x.uploadedOn || new Date("2020-01-01")))?.uploadedOn ||
          new Date("2020-01-01")
      );
    }
    return 0;
  }).reverse();

  // get documents that are not yet uploaded first
  const notUploaded = sorted.filter(
    (doc: types.ChecklistResult) =>
      types.isDocumentChecklistItem(doc) && !types.hasChecklistDocumentUpload(doc)
  );
  const theRest = sorted.filter(
    (doc: types.ChecklistResult) =>
      !types.isDocumentChecklistItem(doc) || types.hasChecklistDocumentUpload(doc)
  );

  return [...notUploaded, ...theRest];
};
